import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  CreditCard,
  MapPin,
  Package,
  Phone,
  RotateCcw,
  Settings2,
  User,
  Wallet,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';

import type {
  SupportTicketCategory,
  SaasPlanCode,
  SaasBillingCycle,
  VendorWarehouseInput,
} from '@terra/api-client';
import {
  Badge,
  type BadgeTone,
  Card,
  CardBody,
  EmptyState,
  PageHeader,
  Spinner,
  Button,
  Input,
} from '@terra/ui';
import { checkGstin, describePayment } from '@terra/utils';

import { ReturnQcPanel } from '../components/ReturnQcPanel';
import { api } from '../lib/api';
import { STOREFRONT_URL } from '../lib/appUrls';
import { useAuthStore } from '../stores/authStore';

import { OrderHero, SectionTitle, statusPillClass } from './OrderDetailParts';
import { CourierCostsCard, FulfilmentPanel } from './orderFulfilment';
import { PICKUP_QUERY_KEY, PickupAddressCard } from './PickupAddressCard';

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

// ══════════════════════════════════════════════════════════════════
// OVERVIEW — extracted to dedicated file for maintainability
// ══════════════════════════════════════════════════════════════════
export { OverviewPage } from './OverviewPage';

// ══════════════════════════════════════════════════════════════════
// PRODUCTS
// ══════════════════════════════════════════════════════════════════

const PRODUCT_STATUS_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  CHANGES_REQUESTED: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  ARCHIVED: 'neutral',
};

/** Whether shoppers can see the product, and if not, why. */
function StorefrontState({ product: p }: { product: any }) {
  if (p.deletedAt) {
    return <Badge tone="danger">Deleted</Badge>;
  }
  if (!p.isActive) {
    return p.hiddenBy === 'ADMIN' ? (
      <span title="Hidden by Terra Shoppy. Raise a support ticket to have it shown again.">
        <Badge tone="danger">Hidden by admin</Badge>
      </span>
    ) : (
      <Badge tone="neutral">Hidden</Badge>
    );
  }
  return p.status === 'APPROVED' ? (
    <Badge tone="success">Live</Badge>
  ) : (
    <span className="text-[11px] font-semibold text-slate-500">After approval</span>
  );
}

export function ProductsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const showingDeleted = statusFilter === 'DELETED';
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['vendor-products', statusFilter, search],
    queryFn: () => {
      const opts: Record<string, unknown> = { pageSize: 50 };
      if (showingDeleted) opts.view = 'deleted';
      else if (statusFilter) opts.status = statusFilter;
      if (search) opts.q = search;
      return api.products.myProducts(opts);
    },
  });

  const refresh = () => {
    setActionError(null);
    void qc.invalidateQueries({ queryKey: ['vendor-products'] });
  };
  const failed = (err: any) =>
    setActionError(
      err?.response?.data?.error?.message ?? err?.message ?? 'That did not work — try again.',
    );

  const submitForApproval = useMutation({
    mutationFn: (id: string) => api.products.submitForApproval(id),
    onSuccess: refresh,
    onError: failed,
  });
  const setVisibility = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.products.vendorSetVisibility(id, isActive),
    onSuccess: refresh,
    onError: failed,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.products.vendorDelete(id),
    onSuccess: refresh,
    onError: failed,
  });
  const restore = useMutation({
    mutationFn: (id: string) => api.products.vendorRestore(id),
    onSuccess: refresh,
    onError: failed,
  });

  const products = data?.data ?? [];

  return (
    <div className="space-y-4">
      <style>{`
        .products-filter-btn {
          font-weight: 700 !important;
          font-size: 0.75rem !important;
          border-radius: 9999px !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .products-filter-btn-active {
          background: linear-gradient(135deg, #a8161b, #850c10) !important;
          border-color: #a8161b !important;
          color: #ffffff !important;
          box-shadow: 0 3px 8px rgba(168, 22, 27, 0.12) !important;
        }
        .products-table-row {
          transition: all 0.2s ease !important;
        }
        .products-table-row:hover {
          background-color: rgba(168, 22, 27, 0.015) !important;
        }
      `}</style>

      <PageHeader
        title="Products"
        description="Manage your product catalog, variants and submissions."
      />

      <div className="mb-2 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1.5">
          {[
            '',
            'DRAFT',
            'PENDING_APPROVAL',
            'APPROVED',
            'REJECTED',
            'CHANGES_REQUESTED',
            'DELETED',
          ].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={statusFilter === s ? 'primary' : 'outline'}
              className={`products-filter-btn ${
                statusFilter === s
                  ? 'products-filter-btn-active border-[#a8161b] bg-[#a8161b] hover:bg-[#850c10]'
                  : 'text-slate-600 hover:border-[#a8161b]/35 hover:text-[#a8161b]'
              }`}
              onClick={() => setStatusFilter(s)}
            >
              {s ? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') : 'All'}
            </Button>
          ))}
        </div>
        <Input
          placeholder="Search products…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-60 focus:border-[#a8161b] focus:ring-red-900/10"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate('/products/bulk')}
          className="ml-auto rounded-lg font-bold"
        >
          Bulk upload
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={() => navigate('/products/new')}
          className="rounded-lg border-[#a8161b] bg-[#a8161b] font-bold text-white shadow-md shadow-red-900/10 hover:border-[#850c10] hover:bg-[#850c10]"
        >
          + Create product
        </Button>
      </div>

      {actionError && (
        <p
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
        >
          {actionError}
        </p>
      )}

      {isLoading ? (
        <div className="grid min-h-[200px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : products.length === 0 && showingDeleted ? (
        <div className="rounded-xl border border-slate-200/80 bg-white p-8 shadow-sm">
          <EmptyState title="No deleted products" description="Products you delete appear here." />
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200/80 bg-white p-8 shadow-sm">
          <EmptyState title="No products" description="Start by creating your first product." />
          <Button
            size="sm"
            variant="primary"
            onClick={() => navigate('/products/new')}
            className="mt-4 border-[#a8161b] bg-[#a8161b] font-bold text-white shadow-md hover:border-[#850c10] hover:bg-[#850c10]"
          >
            + Create your first product
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200/60 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200/60 bg-slate-50/50 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Sku</th>
                <th className="px-4 py-3">Variants</th>
                <th className="px-4 py-3">Price range</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Storefront</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p: any) => {
                const prices = (p.variants ?? []).map((v: any) => v.pricePaise).filter(Boolean);
                const minPrice = prices.length ? Math.min(...prices) : 0;
                const maxPrice = prices.length ? Math.max(...prices) : 0;
                return (
                  <tr
                    key={p.id}
                    className="products-table-row border-b border-slate-100 last:border-0"
                  >
                    <td className="px-4 py-3">
                      <div className="text-xs font-bold text-slate-800 sm:text-sm">{p.title}</div>
                      <div className="mt-0.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                        {p.category?.name ?? ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] font-medium text-slate-500">
                      {p.variants?.[0]?.sku ?? '—'}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-600">
                      {p.variants?.length ?? 0}
                    </td>
                    <td className="px-4 py-3 text-xs font-extrabold text-slate-700">
                      {minPrice === maxPrice
                        ? formatPaise(minPrice)
                        : `${formatPaise(minPrice)} – ${formatPaise(maxPrice)}`}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        tone={PRODUCT_STATUS_TONE[p.status] ?? 'neutral'}
                        className="text-[10px] font-extrabold"
                      >
                        {p.status.charAt(0) + p.status.slice(1).toLowerCase().replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <StorefrontState product={p} />
                    </td>
                    <td className="px-4 py-3">
                      {p.deletedAt ? (
                        p.deletedBy === 'SELLER' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-md py-1 text-xs font-bold"
                            loading={restore.isPending && restore.variables === p.id}
                            onClick={() => restore.mutate(p.id)}
                          >
                            Restore
                          </Button>
                        ) : (
                          <span className="text-[11px] font-semibold text-slate-500">
                            Removed by Terra Shoppy — raise a ticket to restore
                          </span>
                        )
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {(p.status === 'DRAFT' ||
                            p.status === 'CHANGES_REQUESTED' ||
                            p.status === 'REJECTED') && (
                            <Button
                              size="sm"
                              className="rounded-md border-[#a8161b] bg-[#a8161b] py-1 text-xs font-bold text-white hover:border-[#850c10] hover:bg-[#850c10]"
                              onClick={() => submitForApproval.mutate(p.id)}
                              loading={submitForApproval.isPending}
                            >
                              Submit for review
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-md py-1 text-xs font-bold hover:border-[#a8161b]/35 hover:text-[#a8161b]"
                            onClick={() => navigate(`/products/${p.slug}/edit`)}
                          >
                            Edit
                          </Button>
                          {p.isActive ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-md py-1 text-xs font-bold"
                              title="Take it off the storefront without deleting it"
                              loading={
                                setVisibility.isPending && setVisibility.variables?.id === p.id
                              }
                              onClick={() => setVisibility.mutate({ id: p.id, isActive: false })}
                            >
                              Hide
                            </Button>
                          ) : (
                            p.hiddenBy !== 'ADMIN' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-md py-1 text-xs font-bold"
                                loading={
                                  setVisibility.isPending && setVisibility.variables?.id === p.id
                                }
                                onClick={() => setVisibility.mutate({ id: p.id, isActive: true })}
                              >
                                Show
                              </Button>
                            )
                          )}
                          {p.status === 'APPROVED' && p.isActive && (
                            <a
                              href={`${STOREFRONT_URL}/product/${p.slug}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center rounded-md border border-slate-200 px-3 py-1 text-xs font-bold text-slate-700 no-underline hover:border-[#a8161b]/35 hover:text-[#a8161b]"
                            >
                              View
                            </a>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-md border-red-200 py-1 text-xs font-bold text-red-700 hover:bg-red-50"
                            loading={remove.isPending && remove.variables === p.id}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Delete "${p.title}"? It is removed from the storefront and from carts. Past orders are not affected, and you can restore it from the Deleted tab.`,
                                )
                              ) {
                                remove.mutate(p.id);
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// INVENTORY
// ══════════════════════════════════════════════════════════════════

export function InventoryPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'stock' | 'warehouses' | 'history'>('stock');

  const { data: variants, isLoading } = useQuery<any[]>({
    queryKey: ['vendor-inventory'],
    queryFn: () => api.products.getVendorInventory(),
  });

  // Warehouse management state. `editingWarehouseId` null = adding a new one.
  const emptyWarehouseForm = {
    name: '',
    line1: '',
    line2: '',
    landmark: '',
    city: '',
    stateCode: '',
    pincode: '',
    contactName: '',
    contactPhone: '',
    isDefault: false,
  };
  const [showWarehouseForm, setShowWarehouseForm] = useState(false);
  const [editingWarehouseId, setEditingWarehouseId] = useState<string | null>(null);
  const [warehouseForm, setWarehouseForm] = useState(emptyWarehouseForm);
  const closeWarehouseForm = () => {
    setShowWarehouseForm(false);
    setEditingWarehouseId(null);
    setWarehouseForm(emptyWarehouseForm);
    setErrorMsg(null);
  };
  const openEditWarehouse = (wh: any) => {
    setEditingWarehouseId(wh.id);
    setWarehouseForm({
      name: wh.name ?? '',
      line1: wh.line1 ?? '',
      line2: wh.line2 ?? '',
      landmark: wh.landmark ?? '',
      city: wh.city ?? '',
      stateCode: wh.stateCode ?? '',
      pincode: wh.pincode ?? '',
      contactName: wh.contactName ?? '',
      contactPhone: wh.contactPhone ?? '',
      isDefault: Boolean(wh.isDefault),
    });
    setErrorMsg(null);
    setShowWarehouseForm(true);
  };

  const [adjustingVariant, setAdjustingVariant] = useState<any | null>(null);
  const [adjustForm, setAdjustForm] = useState({
    warehouseId: '',
    deltaType: 'add' as 'add' | 'subtract',
    quantity: '0',
    reason: 'RECEIVE',
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Fetch warehouses (authoritative source = vendor warehouse endpoint);
  // we still derive itemCount/totalStock from the inventory snapshot.
  const { data: warehousesData } = useQuery<any[]>({
    queryKey: ['vendor-warehouses'],
    queryFn: async () => {
      const [warehouseList, inventory] = await Promise.all([
        api.vendors.listMyWarehouses(),
        api.products.getVendorInventory(),
      ]);
      const counts = new Map<string, { itemCount: number; totalStock: number }>();
      inventory.forEach((variant: any) => {
        variant.inventoryItems?.forEach((item: any) => {
          if (!item.warehouseId) return;
          const c = counts.get(item.warehouseId) ?? { itemCount: 0, totalStock: 0 };
          c.itemCount += 1;
          c.totalStock += item.onHand;
          counts.set(item.warehouseId, c);
        });
      });
      return warehouseList.map((wh) => ({
        ...wh,
        itemCount: counts.get(wh.id)?.itemCount ?? 0,
        totalStock: counts.get(wh.id)?.totalStock ?? 0,
      }));
    },
  });

  const warehouses = warehousesData ?? [];
  const editingIsDefault = Boolean(
    editingWarehouseId && warehouses.find((w: any) => w.id === editingWarehouseId)?.isDefault,
  );

  const saveWarehouseMutation = useMutation({
    mutationFn: (body: VendorWarehouseInput) =>
      editingWarehouseId
        ? api.vendors.updateWarehouse(editingWarehouseId, body)
        : api.vendors.createWarehouse(body),
    onSuccess: () => {
      const wasEditing = Boolean(editingWarehouseId);
      closeWarehouseForm();
      setSuccessMsg(
        wasEditing ? 'Warehouse updated successfully.' : 'Warehouse created successfully.',
      );
      void qc.invalidateQueries({ queryKey: ['vendor-warehouses'] });
      void qc.invalidateQueries({ queryKey: ['vendor-inventory'] });
      setTimeout(() => setSuccessMsg(null), 3000);
    },
    onError: (err: any) => {
      setErrorMsg(err?.response?.data?.message || err?.message || 'Failed to save warehouse.');
    },
  });

  const deleteWarehouseMutation = useMutation({
    mutationFn: (warehouseId: string) => api.vendors.deleteWarehouse(warehouseId),
    onSuccess: () => {
      setSuccessMsg('Warehouse deleted.');
      setErrorMsg(null);
      void qc.invalidateQueries({ queryKey: ['vendor-warehouses'] });
      void qc.invalidateQueries({ queryKey: ['vendor-inventory'] });
      setTimeout(() => setSuccessMsg(null), 3000);
    },
    onError: (err: any) => {
      setErrorMsg(err?.response?.data?.message || err?.message || 'Failed to delete warehouse.');
    },
  });
  const confirmDeleteWarehouse = (wh: any) => {
    if (wh.isDefault) {
      setErrorMsg('Make another warehouse the default before deleting your pickup warehouse.');
      return;
    }
    if (window.confirm(`Delete warehouse "${wh.name}"? This cannot be undone.`)) {
      deleteWarehouseMutation.mutate(wh.id);
    }
  };

  const adjustStockMutation = useMutation({
    mutationFn: (body: { variantId: string; warehouseId: string; delta: number; reason: string }) =>
      api.products.adjustStock(body),
    onSuccess: () => {
      setAdjustingVariant(null);
      setErrorMsg(null);
      void qc.invalidateQueries({ queryKey: ['vendor-inventory'] });
    },
    onError: (err: any) => {
      setErrorMsg(err?.response?.data?.message || err?.message || 'Failed to adjust stock.');
    },
  });

  const handleAdjustClick = (v: any) => {
    const defaultWarehouseId = v.inventoryItems?.[0]?.warehouseId ?? '';
    setAdjustingVariant(v);
    setAdjustForm({
      warehouseId: defaultWarehouseId,
      deltaType: 'add',
      quantity: '0',
      reason: 'RECEIVE',
    });
    setErrorMsg(null);
  };

  const handleAdjustSubmit = () => {
    const qty = parseInt(adjustForm.quantity, 10);
    if (isNaN(qty) || qty <= 0) {
      setErrorMsg('Quantity must be a positive integer.');
      return;
    }
    const delta = adjustForm.deltaType === 'add' ? qty : -qty;
    if (!adjustingVariant) return;
    adjustStockMutation.mutate({
      variantId: adjustingVariant.id,
      warehouseId: adjustForm.warehouseId,
      delta,
      reason: adjustForm.reason,
    });
  };

  // ── filter state for the stock tab ──
  const [searchQuery, setSearchQuery] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [stockLevelFilter, setStockLevelFilter] = useState<'' | 'low' | 'out' | 'available'>('');

  const all = variants ?? [];
  const list = all.filter((v: any) => {
    // Search: title or SKU substring (case-insensitive)
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const haystack = `${v.product?.title ?? ''} ${v.sku ?? ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    const items = v.inventoryItems ?? [];
    // Warehouse filter: keep variants that have at least one item in the selected warehouse
    if (warehouseFilter && !items.some((i: any) => i.warehouseId === warehouseFilter)) {
      return false;
    }
    // Stock level filter — applied against the warehouse-narrowed item set when set,
    // otherwise across all warehouses.
    const scoped = warehouseFilter
      ? items.filter((i: any) => i.warehouseId === warehouseFilter)
      : items;
    const variantOnHand = scoped.reduce((s: number, i: any) => s + i.onHand, 0);
    if (stockLevelFilter === 'out' && variantOnHand !== 0) return false;
    if (stockLevelFilter === 'low' && (variantOnHand === 0 || variantOnHand > 5)) return false;
    if (stockLevelFilter === 'available' && variantOnHand <= 0) return false;
    return true;
  });

  // Summary Metrics (computed across the unfiltered set so KPIs reflect the
  // vendor's full inventory, not the current filter view).
  const totalSKUs = all.length;
  let totalOnHand = 0;
  let totalReserved = 0;
  let lowStockCount = 0;

  all.forEach((v: any) => {
    const items = v.inventoryItems ?? [];
    let variantOnHand = 0;
    items.forEach((item: any) => {
      totalOnHand += item.onHand;
      totalReserved += item.reserved;
      variantOnHand += item.onHand;
    });
    if (variantOnHand <= 5) lowStockCount++;
  });

  const totalAvailable = totalOnHand - totalReserved;

  /**
   * Download the stock view as it is filtered on screen: one row per SKU per
   * warehouse, narrowed to the selected warehouse when one is chosen.
   */
  const exportStockCsv = (rows: any[], onlyWarehouse: string) => {
    const cell = (v: unknown) => {
      const s = String(v ?? '');
      // Quote everything; neutralise a leading formula character for spreadsheets.
      return `"${(/^[=+\-@]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"`;
    };
    const lines = [['Product', 'SKU', 'Warehouse', 'On hand', 'Reserved', 'Available']];
    for (const v of rows) {
      const items = (v.inventoryItems ?? []).filter(
        (i: any) => !onlyWarehouse || i.warehouseId === onlyWarehouse,
      );
      if (items.length === 0) {
        lines.push([v.product?.title ?? '', v.sku ?? '', '', '0', '0', '0']);
      }
      for (const i of items) {
        lines.push([
          v.product?.title ?? '',
          v.sku ?? '',
          i.warehouse?.name ?? '',
          String(i.onHand),
          String(i.reserved),
          String(i.onHand - i.reserved),
        ]);
      }
    }
    const csv = lines.map((l) => l.map(cell).join(',')).join('\r\n');
    const url = URL.createObjectURL(
      new Blob([String.fromCharCode(0xfeff) + csv], { type: 'text/csv;charset=utf-8' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Shared select styling (matches rest of file)
  const selectCls =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none transition-all focus:border-[#a8161b] focus:ring-2 focus:ring-[#a8161b]/10';

  return (
    <div className="space-y-5">
      <PageHeader
        title="Inventory management"
        description="Real-time warehouse stock tracking. Adjust quantities and monitor low-stock levels."
      />

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'stock', label: 'Stock Levels', icon: '📦' },
          { id: 'warehouses', label: 'Warehouses', icon: '🏭' },
          { id: 'history', label: 'Stock History', icon: '📊' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-bold transition-colors ${
              activeTab === tab.id
                ? 'border-[#a8161b] text-[#a8161b]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {errorMsg && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-xs font-bold text-red-700 shadow-sm">
          <svg
            className="h-4 w-4 shrink-0 text-red-500"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          {errorMsg}
        </div>
      )}

      {successMsg && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-xs font-bold text-emerald-700 shadow-sm">
          <svg
            className="h-4 w-4 shrink-0 text-emerald-500"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {successMsg}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TAB 1: STOCK LEVELS
          ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'stock' && (
        <>
          {/* Action Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by product or SKU..."
                  className="h-9 w-64 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:border-[#a8161b] focus:outline-none focus:ring-1 focus:ring-[#a8161b]"
                />
              </div>
              <select
                value={warehouseFilter}
                onChange={(e) => setWarehouseFilter(e.target.value)}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium focus:border-[#a8161b] focus:outline-none focus:ring-1 focus:ring-[#a8161b]"
              >
                <option value="">All Warehouses</option>
                {warehouses.map((wh: any) => (
                  <option key={wh.id} value={wh.id}>
                    {wh.name}
                  </option>
                ))}
              </select>
              <select
                value={stockLevelFilter}
                onChange={(e) =>
                  setStockLevelFilter(e.target.value as '' | 'low' | 'out' | 'available')
                }
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium focus:border-[#a8161b] focus:outline-none focus:ring-1 focus:ring-[#a8161b]"
              >
                <option value="">All Stock Levels</option>
                <option value="low">Low Stock (≤5)</option>
                <option value="out">Out of Stock</option>
                <option value="available">In Stock</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={list.length === 0}
                onClick={() => exportStockCsv(list, warehouseFilter)}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Export CSV
              </Button>
              <Link
                to="/products/bulk?tab=stock"
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Bulk update stock
              </Link>
            </div>
          </div>

          {/* KPI Summary Cards */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              { label: 'Total SKUs', value: totalSKUs, color: '#a8161b' },
              { label: 'Stock on hand', value: totalOnHand, color: '#16a34a' },
              { label: 'Available stock', value: totalAvailable, color: '#2563eb' },
              { label: 'Low stock items', value: lowStockCount, color: '#dc2626' },
            ].map((kpi) => (
              <div
                key={kpi.label}
                className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                <span className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                  {kpi.label}
                </span>
                <span className="mt-1 block text-2xl font-black" style={{ color: kpi.color }}>
                  {kpi.value}
                </span>
              </div>
            ))}
          </div>

          {isLoading ? (
            <div className="grid min-h-[200px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200/80 bg-white p-8 shadow-sm">
              <EmptyState
                title="No inventory items"
                description="Create products and variants first to populate your inventory."
              />
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200/60 bg-white shadow-sm">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200/60 bg-slate-50/50 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-[#a8161b] focus:ring-[#a8161b]"
                      />
                    </th>
                    <th className="px-4 py-3">Product & options</th>
                    <th className="px-4 py-3">SKU</th>
                    <th className="px-4 py-3">Warehouse</th>
                    <th className="w-24 px-4 py-3 text-center">On hand</th>
                    <th className="w-24 px-4 py-3 text-center">Reserved</th>
                    <th className="w-28 px-4 py-3 text-center">Available</th>
                    <th className="w-24 px-4 py-3 text-center">Reorder</th>
                    <th className="w-32 px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((v: any) => {
                    const items = v.inventoryItems ?? [];
                    const hasInventory = items.length > 0;
                    const totalOnHandForVariant = items.reduce(
                      (sum: number, item: any) => sum + item.onHand,
                      0,
                    );
                    const isLowStock = totalOnHandForVariant > 0 && totalOnHandForVariant <= 5;
                    const isOutOfStock = totalOnHandForVariant === 0;

                    return (
                      <tr
                        key={v.id}
                        className={`border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50/50 ${
                          isOutOfStock ? 'bg-red-50/30' : isLowStock ? 'bg-amber-50/30' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-[#a8161b] focus:ring-[#a8161b]"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <div className="font-semibold text-slate-800">{v.product?.title}</div>
                              <div className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                {Object.entries(v.optionValues ?? {})
                                  .map(([key, val]) => `${key}: ${val}`)
                                  .join(', ') || 'Default variant'}
                              </div>
                            </div>
                            {isOutOfStock && (
                              <Badge tone="danger" className="text-[9px]">
                                Out
                              </Badge>
                            )}
                            {isLowStock && (
                              <Badge tone="warning" className="text-[9px]">
                                Low
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-[11px] font-medium text-slate-500">
                          {v.sku}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-700">
                          {items.map((item: any) => (
                            <div key={item.id}>{item.warehouse?.name || 'Primary Warehouse'}</div>
                          ))}
                          {!hasInventory && (
                            <span className="text-xs italic text-slate-400">No warehouse</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center font-extrabold text-slate-800">
                          {items.map((item: any) => (
                            <div key={item.id}>{item.onHand}</div>
                          ))}
                          {!hasInventory && <span className="text-slate-400">0</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {items.map((item: any) => (
                            <div key={item.id}>
                              {item.reserved > 0 ? (
                                <Badge tone="warning">{item.reserved} held</Badge>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </div>
                          ))}
                          {!hasInventory && <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {items.map((item: any) => {
                            const available = item.onHand - item.reserved;
                            const tone: BadgeTone =
                              available === 0 ? 'danger' : available <= 5 ? 'warning' : 'success';
                            return (
                              <div key={item.id}>
                                <Badge tone={tone}>
                                  {available === 0 ? 'Out of stock' : `${available} units`}
                                </Badge>
                              </div>
                            );
                          })}
                          {!hasInventory && <Badge tone="danger">Out of stock</Badge>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {items.map((item: any) => (
                            <div key={item.id} className="text-xs font-medium text-slate-500">
                              {item.reorderLevel || 5}
                            </div>
                          ))}
                          {!hasInventory && <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-md py-1 text-xs font-bold hover:border-[#a8161b]/35 hover:text-[#a8161b]"
                            onClick={() => handleAdjustClick(v)}
                            disabled={!hasInventory}
                          >
                            Adjust stock
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TAB 2: WAREHOUSES MANAGEMENT
          ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'warehouses' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Your Warehouses</h3>
              <p className="text-xs text-slate-500">
                Manage pickup locations and inventory storage facilities
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                closeWarehouseForm();
                setShowWarehouseForm(true);
              }}
              className="bg-gradient-to-r from-[#a8161b] to-[#850c10]"
            >
              + Add Warehouse
            </Button>
          </div>

          {warehouses.length === 0 ? (
            <Card>
              <CardBody className="p-8 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                  <svg
                    className="h-8 w-8 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h4 className="text-base font-bold text-slate-800">No warehouses yet</h4>
                <p className="mt-1 text-sm text-slate-500">
                  Add your first warehouse to start managing inventory locations
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    closeWarehouseForm();
                    setShowWarehouseForm(true);
                  }}
                  className="mt-4"
                >
                  Create Warehouse
                </Button>
              </CardBody>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {warehouses.map((wh: any) => (
                <Card key={wh.id} className="overflow-hidden">
                  <CardBody className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-slate-800">{wh.name}</h4>
                          {wh.isDefault && (
                            <Badge tone="success" className="text-[9px]">
                              Default
                            </Badge>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {wh.line1}, {wh.city}
                        </p>
                        <p className="text-xs text-slate-500">
                          {wh.stateCode} - {wh.pincode}
                        </p>
                      </div>
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                        <svg
                          className="h-6 w-6 text-slate-600"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
                      <div>
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          SKUs
                        </span>
                        <span className="mt-0.5 block text-lg font-black text-slate-800">
                          {wh.itemCount || 0}
                        </span>
                      </div>
                      <div>
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Total Stock
                        </span>
                        <span className="mt-0.5 block text-lg font-black text-slate-800">
                          {wh.totalStock || 0}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 flex gap-2 border-t border-slate-100 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openEditWarehouse(wh)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1 border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                        disabled={wh.isDefault || deleteWarehouseMutation.isPending}
                        title={
                          wh.isDefault
                            ? 'This is your pickup warehouse. Make another one the default to delete it.'
                            : undefined
                        }
                        onClick={() => confirmDeleteWarehouse(wh)}
                      >
                        {deleteWarehouseMutation.isPending &&
                        deleteWarehouseMutation.variables === wh.id
                          ? 'Deleting…'
                          : 'Delete'}
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TAB 3: STOCK HISTORY
          ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'history' && <StockHistoryPanel />}

      {/* ══════════════════════════════════════════════════════════════════
          WAREHOUSE FORM MODAL
          ══════════════════════════════════════════════════════════════════ */}
      {showWarehouseForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto">
            <CardBody className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-800">
                  {editingWarehouseId ? 'Edit Warehouse' : 'Add New Warehouse'}
                </h3>
                <button
                  type="button"
                  onClick={closeWarehouseForm}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Close"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {errorMsg && (
                <p
                  role="alert"
                  className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {errorMsg}
                </p>
              )}

              {editingIsDefault && (
                <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  This is your pickup warehouse. Changing its address registers a new pickup
                  location with the courier; parcels already booked keep the old one.
                </p>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (
                    !warehouseForm.name.trim() ||
                    !warehouseForm.line1.trim() ||
                    !warehouseForm.city.trim() ||
                    !/^[A-Z]{2}$/.test(warehouseForm.stateCode) ||
                    !/^[0-9]{6}$/.test(warehouseForm.pincode)
                  ) {
                    setErrorMsg(
                      'Please fill all required fields. State code must be 2 letters and pincode 6 digits.',
                    );
                    return;
                  }
                  const phone = warehouseForm.contactPhone.trim();
                  if (phone && !/^(\+91)?[6-9]\d{9}$/.test(phone)) {
                    setErrorMsg('Enter a 10-digit Indian mobile number for the pickup contact.');
                    return;
                  }
                  saveWarehouseMutation.mutate({
                    name: warehouseForm.name.trim(),
                    line1: warehouseForm.line1.trim(),
                    line2: warehouseForm.line2.trim(),
                    landmark: warehouseForm.landmark.trim(),
                    city: warehouseForm.city.trim(),
                    stateCode: warehouseForm.stateCode,
                    pincode: warehouseForm.pincode,
                    contactName: warehouseForm.contactName.trim(),
                    ...(phone ? { contactPhone: phone } : {}),
                    isDefault: warehouseForm.isDefault,
                  });
                }}
                className="space-y-4"
              >
                <Input
                  label="Warehouse Name *"
                  placeholder="e.g. Main Warehouse, Delhi Hub"
                  value={warehouseForm.name}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, name: e.target.value })}
                  required
                />

                <Input
                  label="Address Line 1 *"
                  placeholder="House/shop no., building, street"
                  value={warehouseForm.line1}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, line1: e.target.value })}
                  required
                />

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Address Line 2"
                    placeholder="Area, locality"
                    value={warehouseForm.line2}
                    onChange={(e) => setWarehouseForm({ ...warehouseForm, line2: e.target.value })}
                  />
                  <Input
                    label="Landmark"
                    placeholder="e.g. Near City Mall"
                    value={warehouseForm.landmark}
                    onChange={(e) =>
                      setWarehouseForm({ ...warehouseForm, landmark: e.target.value })
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="City *"
                    placeholder="e.g. Mumbai"
                    value={warehouseForm.city}
                    onChange={(e) => setWarehouseForm({ ...warehouseForm, city: e.target.value })}
                    required
                  />

                  <Input
                    label="State Code *"
                    placeholder="e.g. MH"
                    value={warehouseForm.stateCode}
                    onChange={(e) =>
                      setWarehouseForm({
                        ...warehouseForm,
                        stateCode: e.target.value.toUpperCase(),
                      })
                    }
                    maxLength={2}
                    required
                  />
                </div>

                <Input
                  label="Pincode *"
                  placeholder="e.g. 400001"
                  value={warehouseForm.pincode}
                  onChange={(e) =>
                    setWarehouseForm({
                      ...warehouseForm,
                      pincode: e.target.value.replace(/\D/g, ''),
                    })
                  }
                  maxLength={6}
                  required
                />

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Pickup Contact Name"
                    placeholder="Person the courier asks for"
                    value={warehouseForm.contactName}
                    onChange={(e) =>
                      setWarehouseForm({ ...warehouseForm, contactName: e.target.value })
                    }
                  />
                  <Input
                    label="Pickup Contact Phone"
                    placeholder="10-digit mobile"
                    inputMode="tel"
                    value={warehouseForm.contactPhone}
                    onChange={(e) =>
                      setWarehouseForm({ ...warehouseForm, contactPhone: e.target.value })
                    }
                  />
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={warehouseForm.isDefault}
                    // The default can only move to another warehouse, never be
                    // switched off: the store always needs a pickup address.
                    disabled={editingIsDefault}
                    onChange={(e) =>
                      setWarehouseForm({ ...warehouseForm, isDefault: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-slate-300 text-[#a8161b] focus:ring-[#a8161b]"
                  />
                  <span className="font-medium text-slate-700">
                    Set as default (pickup) warehouse
                  </span>
                </label>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={saveWarehouseMutation.isPending}
                    className="flex-1 bg-gradient-to-r from-[#a8161b] to-[#850c10]"
                  >
                    {saveWarehouseMutation.isPending
                      ? 'Saving…'
                      : editingWarehouseId
                        ? 'Save Changes'
                        : 'Create Warehouse'}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeWarehouseForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </div>
      )}

      {/* ── Adjust-stock slide-over drawer ── */}
      {adjustingVariant && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
          <div
            className="flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white p-6 shadow-2xl"
            style={{ animation: 'slideInRight .2s ease-out' }}
          >
            <style>{`@keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>

            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <h3 className="text-base font-black text-slate-800">Adjust stock level</h3>
                <p className="mt-0.5 text-xs font-semibold text-slate-400">
                  {adjustingVariant.product?.title}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAdjustingVariant(null)}
                className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 space-y-5 overflow-y-auto py-5">
              <div>
                <span className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                  SKU
                </span>
                <span className="font-mono text-sm font-bold text-slate-700">
                  {adjustingVariant.sku}
                </span>
              </div>

              <div>
                <label className="mb-1.5 block text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                  Target warehouse
                </label>
                <select
                  value={adjustForm.warehouseId}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, warehouseId: e.target.value }))}
                  className={selectCls}
                >
                  {adjustingVariant.inventoryItems?.map((item: any) => (
                    <option key={item.id} value={item.warehouseId}>
                      {item.warehouse?.name} (Current: {item.onHand} units)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                  Adjustment action
                </label>
                <div className="grid grid-cols-2 gap-2.5">
                  {(['add', 'subtract'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setAdjustForm((f) => ({ ...f, deltaType: type }))}
                      className={`flex h-11 items-center justify-center gap-1.5 rounded-lg border text-sm font-bold transition-all ${
                        adjustForm.deltaType === type
                          ? type === 'add'
                            ? 'border-green-500 bg-green-50 text-green-700 ring-1 ring-green-300'
                            : 'border-red-500 bg-red-50 text-red-700 ring-1 ring-red-300'
                          : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {type === 'add' ? '+ Add stock' : '− Subtract stock'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                  Quantity
                </label>
                <Input
                  type="number"
                  min="1"
                  value={adjustForm.quantity}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, quantity: e.target.value }))}
                  placeholder="Enter quantity…"
                  className="focus:border-[#a8161b] focus:ring-red-900/10"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                  Reason
                </label>
                <select
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
                  className={selectCls}
                >
                  <option value="RECEIVE">Stock receipt (Inbound Purchase)</option>
                  <option value="ADJUST">Inventory correction / Stock audit</option>
                  <option value="RETURN">Customer return</option>
                  <option value="DAMAGE">Damaged / Expired stock deduction</option>
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 border-t border-slate-100 pt-4">
              <Button
                variant="primary"
                className="flex-1 border-[#a8161b] bg-[#a8161b] font-bold text-white shadow-md hover:border-[#850c10] hover:bg-[#850c10]"
                onClick={handleAdjustSubmit}
                loading={adjustStockMutation.isPending}
              >
                Submit adjustment
              </Button>
              <Button variant="outline" onClick={() => setAdjustingVariant(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stock history panel (StockLedgerEntry, paginated) ──
function StockHistoryPanel() {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['vendor-stock-ledger', page],
    queryFn: () => api.products.getVendorStockLedger({ page, pageSize }),
  });

  if (isLoading) {
    return (
      <Card>
        <CardBody className="p-8 text-center text-sm text-slate-500">Loading history…</CardBody>
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <CardBody className="p-8 text-center text-sm text-rose-600">
          Failed to load stock history.
        </CardBody>
      </Card>
    );
  }
  if (data.data.length === 0) {
    return (
      <Card>
        <CardBody className="p-8 text-center text-sm text-slate-500">
          No stock movements yet. Adjustments and order activity will appear here.
        </CardBody>
      </Card>
    );
  }

  const fmtDate = (iso: string) => new Date(iso).toLocaleString();

  return (
    <Card>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Product / SKU</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3 text-right">Δ Qty</th>
                <th className="px-4 py-3">Ref</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((entry) => (
                <tr key={entry.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-600">{fmtDate(entry.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{entry.variant.product.title}</div>
                    <div className="text-xs text-slate-500">{entry.variant.sku}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{entry.reason}</td>
                  <td
                    className={`px-4 py-3 text-right font-semibold ${
                      entry.delta >= 0 ? 'text-emerald-600' : 'text-rose-600'
                    }`}
                  >
                    {entry.delta >= 0 ? `+${entry.delta}` : entry.delta}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {entry.refType
                      ? `${entry.refType}${entry.refId ? `:${entry.refId.slice(0, 8)}` : ''}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
          <span>
            Page {data.page.page} of {data.page.pageCount} · {data.page.total} entries
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= data.page.pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════
// ORDERS
// ══════════════════════════════════════════════════════════════════

const SUB_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING_PAYMENT: 'warning',
  // Paid, waiting for this vendor to accept — the one state that needs action now.
  PLACED: 'warning',
  CONFIRMED: 'success',
  PACKED: 'success',
  SHIPPED: 'success',
  DELIVERED: 'success',
  CLOSED: 'neutral',
  CANCELLED: 'danger',
  RETURN_REQUESTED: 'warning',
  RETURN_APPROVED: 'warning',
  RETURN_RECEIVED: 'warning',
  REFUNDED: 'neutral',
};

// The only status a seller sets by hand is accepting the order. Packing happens
// by creating the shipment; shipped and delivered come from the courier's scans.
const NEXT_STATUS: Record<string, string> = {
  PLACED: 'CONFIRMED',
};

/** Button copy for advancing a sub-order to `NEXT_STATUS[status]`. */
const NEXT_ACTION_LABEL: Record<string, string> = {
  PLACED: 'Accept order',
};

/** Sub-orders the vendor may still cancel (nothing has been handed to a courier). */
const VENDOR_CANCELLABLE = ['PLACED', 'CONFIRMED', 'PACKED'];

const FILTER_LABELS: Record<string, string> = {
  '': 'All',
  PLACED: 'New · awaiting acceptance',
  CONFIRMED: 'Accepted',
  PACKED: 'Packed',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  RETURN_REQUESTED: 'Return requested',
  RETURN_APPROVED: 'Return approved',
  RETURN_RECEIVED: 'Return received',
  REFUNDED: 'Refunded',
};

const formatStatus = (status: string) => {
  const mapped: Record<string, string> = {
    PENDING_PAYMENT: 'Pending payment',
    PLACED: 'New · accept to start',
    CONFIRMED: 'Accepted',
    PACKED: 'Packed',
    SHIPPED: 'Shipped',
    DELIVERED: 'Delivered',
    CLOSED: 'Closed',
    CANCELLED: 'Cancelled',
    RETURN_REQUESTED: 'Return requested',
    RETURN_APPROVED: 'Return approved',
    RETURN_RECEIVED: 'Return received',
    REFUNDED: 'Refunded',
  };
  return mapped[status] ?? status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ');
};

/**
 * Accessible cancel-with-reason modal. Replaces `window.prompt`, which is
 * blocked in many embedded contexts, fails silently in cross-origin iframes,
 * and is not screen-reader friendly.
 */
function CancelOrderModal({
  open,
  onClose,
  onConfirm,
  isPending,
  title = 'Cancel order',
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isPending?: boolean;
  title?: string;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-order-title"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-2xl">
        <h3 id="cancel-order-title" className="text-base font-black text-slate-800">
          {title}
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          Tell the customer why. They see this reason on their order page and in the cancellation
          notification.
        </p>
        <textarea
          className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-[#a8161b] focus:outline-none focus:ring-1 focus:ring-[#a8161b]"
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Item is out of stock"
        />
        {reason.trim().length > 0 && reason.trim().length < 3 && (
          <p className="mt-1 text-xs font-semibold text-rose-600">Write at least 3 characters.</p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Keep order
          </Button>
          <Button
            variant="primary"
            className="bg-rose-600 hover:bg-rose-700"
            onClick={() => onConfirm(reason.trim())}
            loading={!!isPending}
            disabled={reason.trim().length < 3}
          >
            Confirm cancellation
          </Button>
        </div>
      </div>
    </div>
  );
}

export function OrdersPage() {
  const qc = useQueryClient();
  // A dashboard tile opens this page pre-filtered to what it counted:
  // ?view=<tile name>&status=A,B (order states) or &shipmentStatus=X,Y (parcel
  // states), plus &from=YYYY-MM-DD for the month-to-date tiles.
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardView = searchParams.get('view');
  const shipmentFilter = searchParams.get('shipmentStatus') ?? '';
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState(searchParams.get('from') ?? '');
  const clearDashboardView = () => {
    setSearchParams({});
    setStatusFilter('');
    setFromDate('');
  };
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

  // Reset to page 1 whenever a filter changes so the user doesn't land on an empty page.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, shipmentFilter, searchQuery, fromDate, toDate]);

  const { data, isLoading } = useQuery({
    queryKey: ['vendor-orders', statusFilter, shipmentFilter, searchQuery, fromDate, toDate, page],
    queryFn: () => {
      const opts: Record<string, unknown> = { page, pageSize };
      if (statusFilter) opts.status = statusFilter;
      if (shipmentFilter) opts.shipmentStatus = shipmentFilter;
      if (searchQuery.trim()) opts.q = searchQuery.trim();
      if (fromDate) opts.fromDate = new Date(fromDate).toISOString();
      // Make `toDate` inclusive of the selected day (end-of-day UTC).
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        opts.toDate = end.toISOString();
      }
      return api.orders.vendorOrders(opts);
    },
  });

  const transition = useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: string; note?: string }) =>
      api.orders.transitionSubOrder(id, { status, ...(note ? { note } : {}) }),
    onSuccess: () => {
      setCancelTargetId(null);
      void qc.invalidateQueries({ queryKey: ['vendor-orders'] });
    },
  });

  const subOrders = data?.data ?? [];

  return (
    <div className="space-y-4">
      <style>{`
        .order-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          border: 1px solid rgba(226, 232, 240, 0.8) !important;
        }
        .order-card:hover {
          transform: translateY(-2px) !important;
          border-color: rgba(168, 22, 27, 0.15) !important;
          box-shadow: 0 10px 25px -12px rgba(168, 22, 27, 0.08) !important;
        }
        .filter-pill {
          transition: all 0.2s ease !important;
        }
      `}</style>

      <PageHeader
        title="Orders"
        description="Manage your incoming orders. Pack, ship, and track deliveries."
      />

      {dashboardView && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-indigo-100 bg-indigo-50/70 px-4 py-2.5 text-sm">
          <span className="text-indigo-800">
            Showing <span className="font-bold">{dashboardView}</span> from your dashboard
          </span>
          <button
            type="button"
            onClick={clearDashboardView}
            className="text-xs font-bold text-indigo-700 underline-offset-2 hover:underline"
          >
            Show all orders
          </button>
        </div>
      )}

      <div className="mb-2 flex flex-wrap gap-2">
        {[
          '',
          'PLACED',
          'CONFIRMED',
          'PACKED',
          'SHIPPED',
          'DELIVERED',
          'CANCELLED',
          'RETURN_REQUESTED',
          'RETURN_APPROVED',
          'RETURN_RECEIVED',
          'REFUNDED',
        ].map((s) => {
          const isActive = statusFilter === s;
          return (
            <button
              key={s}
              onClick={() => {
                // Picking a tab leaves the dashboard's view for a plain filter.
                if (dashboardView) setSearchParams({});
                setStatusFilter(s);
              }}
              className={`filter-pill rounded-full border px-4 py-1.5 text-xs font-bold transition-all duration-200 ${
                isActive
                  ? 'border-[#a8161b] bg-gradient-to-r from-[#a8161b] to-[#850c10] text-white shadow-md shadow-red-900/10'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-[#a8161b] hover:bg-[#a8161b]/5 hover:text-[#a8161b]'
              }`}
            >
              {FILTER_LABELS[s] ?? formatStatus(s)}
            </button>
          );
        })}
      </div>

      <form
        className="mb-3 flex flex-wrap items-end gap-2 rounded-xl border border-slate-200/70 bg-white p-3 shadow-sm"
        onSubmit={(e) => {
          e.preventDefault();
          setSearchQuery(searchInput);
        }}
      >
        <div className="flex flex-col">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Search
          </label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Order # or customer"
            className="h-9 w-56 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#a8161b] focus:outline-none focus:ring-1 focus:ring-[#a8161b]"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            From
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#a8161b] focus:outline-none focus:ring-1 focus:ring-[#a8161b]"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            To
          </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#a8161b] focus:outline-none focus:ring-1 focus:ring-[#a8161b]"
          />
        </div>
        <Button type="submit" variant="primary" size="sm">
          Apply
        </Button>
        {(searchQuery || fromDate || toDate) && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setSearchInput('');
              setSearchQuery('');
              setFromDate('');
              setToDate('');
            }}
          >
            Clear
          </Button>
        )}
      </form>

      {isLoading ? (
        <div className="grid min-h-[200px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : subOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200/80 bg-white p-8 shadow-sm">
          <EmptyState
            title="No orders"
            description="Orders will appear here once customers place them."
          />
        </div>
      ) : (
        <div className="space-y-3">
          {subOrders.map((sub: any) => (
            <Card key={sub.id} className="order-card overflow-hidden rounded-xl bg-white shadow-sm">
              <CardBody className="p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-black text-[#a8161b]">
                      #{sub.orderGroup?.orderNumber ?? '—'}
                    </span>
                    <span className="inline-block rounded-md border border-slate-100 bg-slate-50 px-2.5 py-0.5 text-xs font-bold text-slate-400">
                      {sub.orderGroup?.customer?.name ??
                        sub.orderGroup?.customer?.email ??
                        'Guest Customer'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-extrabold text-slate-800">
                      {formatPaise(sub.totalPaise ?? 0)}
                    </span>
                    <Badge
                      tone={SUB_STATUS_TONE[sub.status] ?? 'neutral'}
                      className="text-[10px] font-extrabold"
                    >
                      {formatStatus(sub.status)}
                    </Badge>
                    {NEXT_STATUS[sub.status] && (
                      <Button
                        size="sm"
                        className="rounded-lg border-[#a8161b] bg-[#a8161b] px-3.5 py-1.5 text-xs font-extrabold text-white shadow-sm shadow-red-900/5 hover:border-[#850c10] hover:bg-[#850c10]"
                        onClick={() =>
                          transition.mutate({ id: sub.id, status: NEXT_STATUS[sub.status]! })
                        }
                        loading={transition.isPending}
                      >
                        {NEXT_ACTION_LABEL[sub.status]}
                      </Button>
                    )}
                    {(sub.status === 'CONFIRMED' || (sub.status === 'PACKED' && !sub.shipment)) && (
                      <Link to={`/orders/${sub.id}`}>
                        <Button
                          size="sm"
                          className="rounded-lg border-[#a8161b] bg-[#a8161b] px-3.5 py-1.5 text-xs font-extrabold text-white shadow-sm hover:bg-[#850c10]"
                        >
                          Ready to ship →
                        </Button>
                      </Link>
                    )}
                    {VENDOR_CANCELLABLE.includes(sub.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg border-red-200 px-3.5 py-1.5 text-xs font-extrabold text-red-600 hover:border-red-300 hover:bg-red-50"
                        onClick={() => setCancelTargetId(sub.id)}
                        disabled={transition.isPending}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>

                {sub.lineItems && sub.lineItems.length > 0 && (
                  <div className="mt-3.5 space-y-2 border-t border-dashed border-slate-100 pt-3">
                    <span className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                      Items ordered:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {sub.lineItems.map((li: any) => (
                        <span
                          key={li.id}
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-200/50 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-sm"
                        >
                          {li.imageUrl && (
                            <img
                              src={li.imageUrl}
                              alt=""
                              className="h-6 w-6 rounded object-cover"
                              loading="lazy"
                            />
                          )}
                          <span className="text-slate-800">{li.productTitle}</span>
                          <span className="py-0.2 rounded bg-[#a8161b]/10 px-1.5 text-[10px] font-extrabold text-[#a8161b]">
                            × {li.qty}
                          </span>
                          <span className="text-slate-400">
                            ({formatPaise(li.lineSubtotalPaise)})
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-3 flex justify-end border-t border-slate-100 pt-3">
                  <Link to={`/orders/${sub.id}`}>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg border-slate-200 px-3.5 py-1.5 text-xs font-extrabold text-slate-600 hover:border-[#a8161b] hover:bg-[#a8161b]/5 hover:text-[#a8161b]"
                    >
                      View Details →
                    </Button>
                  </Link>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {data?.page && data.page.total > pageSize && (
        <div className="mt-4 flex items-center justify-between rounded-xl border border-slate-200/70 bg-white px-4 py-3 text-sm text-slate-500 shadow-sm">
          <span>
            Page {data.page.page} of {data.page.pageCount} · {data.page.total} orders
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= data.page.pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <CancelOrderModal
        open={cancelTargetId !== null}
        onClose={() => setCancelTargetId(null)}
        isPending={transition.isPending}
        onConfirm={(reason) => {
          if (!cancelTargetId) return;
          transition.mutate({
            id: cancelTargetId,
            status: 'CANCELLED',
            ...(reason ? { note: reason } : {}),
          });
        }}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ORDER DETAIL
// ══════════════════════════════════════════════════════════════════

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const { data: subOrder, isLoading } = useQuery({
    queryKey: ['vendor-order-detail', orderId],
    queryFn: () => api.orders.getVendorSubOrder(orderId!),
    enabled: !!orderId,
  });

  const transition = useMutation({
    mutationFn: ({ status, note }: { status: string; note?: string }) =>
      api.orders.transitionSubOrder(orderId!, { status, ...(note ? { note } : {}) }),
    onSuccess: () => {
      setShowCancelModal(false);
      void qc.invalidateQueries({ queryKey: ['vendor-order-detail', orderId] });
      void qc.invalidateQueries({ queryKey: ['vendor-orders'] });
    },
  });

  if (isLoading) {
    return (
      <div className="grid min-h-[400px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!subOrder) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Order Not Found"
          description="The order you're looking for doesn't exist."
        />
        <Button onClick={() => navigate('/orders')}>← Back to Orders</Button>
      </div>
    );
  }

  const orderGroup = subOrder.orderGroup || {};
  const customer = orderGroup.customer || {};
  const shippingAddress = orderGroup.shippingAddress || {};
  const billingAddress = orderGroup.billingAddress || {};
  const lineItems = subOrder.lineItems || [];
  const statusEvents = subOrder.statusEvents || [];
  const shipment = subOrder.shipment;
  const payments = orderGroup.payments || [];
  const returnRequests = subOrder.returnRequests || [];
  const codRemittance = subOrder.codRemittance as
    | {
        status: 'PENDING' | 'COLLECTED' | 'REMITTED' | 'RECONCILED';
        amountPaise: number;
        collectedAt: string | null;
        remittedAt: string | null;
      }
    | null
    | undefined;
  const orderPayments = orderGroup.payments || [];
  const isCodOrder = orderPayments.some((p: any) => p.method === 'COD' || p.provider === 'COD');
  // COD is unpaid until the courier collects it; never show it as settled.
  const paymentView = describePayment(orderPayments, 'seller', { orderStatus: subOrder.status });

  // Calculate totals
  const itemsSubtotal = lineItems.reduce(
    (sum: number, li: any) => sum + (li.lineSubtotalPaise || 0),
    0,
  );
  const taxTotal = lineItems.reduce((sum: number, li: any) => sum + (li.taxPaise || 0), 0);
  const shippingFee = subOrder.shippingFeePaise || 0;
  const grandTotal = subOrder.totalPaise || 0;

  return (
    <div className="space-y-6">
      <style>{`
        .detail-card {
          transition: all 0.2s ease !important;
          border: 1px solid rgba(226, 232, 240, 0.8) !important;
        }
        .detail-card:hover {
          border-color: rgba(168, 22, 27, 0.1) !important;
          box-shadow: 0 4px 12px -4px rgba(168, 22, 27, 0.05) !important;
        }
        .timeline-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 3px solid #a8161b;
          background: white;
        }
        .timeline-line {
          width: 2px;
          background: linear-gradient(to bottom, #a8161b 0%, rgba(168, 22, 27, 0.2) 100%);
        }
      `}</style>

      <OrderHero
        orderNumber={orderGroup.orderNumber || '—'}
        placedAt={new Date(subOrder.createdAt || Date.now()).toLocaleString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
        status={subOrder.status}
        statusLabel={formatStatus(subOrder.status)}
        itemCount={lineItems.reduce((n: number, li: any) => n + (li.qty || 0), 0)}
        totalLabel={formatPaise(grandTotal)}
        paymentLabel={paymentView.label}
        onBack={() => navigate('/orders')}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Line Items */}
          <Card className="detail-card overflow-hidden rounded-xl bg-white shadow-sm">
            <CardBody className="p-6">
              <SectionTitle icon={Package}>Order Items</SectionTitle>
              <div className="space-y-3">
                {lineItems.map((li: any) => (
                  <div
                    key={li.id}
                    className="flex items-start gap-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4 transition-colors hover:border-[#a8161b]/15 hover:bg-white"
                  >
                    <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-white text-[#a8161b] ring-1 ring-slate-200">
                      {li.imageUrl ? (
                        <img
                          src={li.imageUrl}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <Package className="h-5 w-5" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold leading-snug text-slate-900">{li.productTitle}</h3>
                      {li.variantTitle && (
                        <p className="text-xs text-slate-500">Variant: {li.variantTitle}</p>
                      )}
                      <p className="mt-1.5 inline-block rounded-md bg-white px-2 py-0.5 font-mono text-[11px] text-slate-500 ring-1 ring-slate-200">
                        SKU {li.sku || '—'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-600">
                        {formatPaise(li.unitPricePaise)} × {li.qty}
                      </p>
                      <p className="mt-1 text-base font-black text-[#a8161b]">
                        {formatPaise(li.lineSubtotalPaise)}
                      </p>
                      {li.taxPaise > 0 && (
                        <p className="text-xs text-slate-400">+ {formatPaise(li.taxPaise)} tax</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="mt-6 space-y-2 border-t border-slate-200 pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Items Subtotal</span>
                  <span className="font-bold text-slate-900">{formatPaise(itemsSubtotal)}</span>
                </div>
                {taxTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Tax</span>
                    <span className="font-bold text-slate-900">{formatPaise(taxTotal)}</span>
                  </div>
                )}
                {shippingFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Shipping Fee</span>
                    <span className="font-bold text-slate-900">{formatPaise(shippingFee)}</span>
                  </div>
                )}
                <div className="-mx-2 mt-2 flex items-center justify-between rounded-xl bg-[#a8161b]/5 px-3 py-2.5 text-base">
                  <span className="font-black text-slate-900">Grand Total</span>
                  <span className="text-lg font-black text-[#a8161b]">
                    {formatPaise(grandTotal)}
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Shipping Information */}
          <Card className="detail-card overflow-hidden rounded-xl bg-white shadow-sm">
            <CardBody className="p-6">
              <SectionTitle icon={MapPin}>Shipping Information</SectionTitle>
              <div className="space-y-4">
                <div>
                  <h3 className="mb-2 text-xs font-extrabold uppercase tracking-wider text-slate-400">
                    Delivery Address
                  </h3>
                  <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-4">
                    <p className="font-bold text-slate-900">
                      {shippingAddress.contactName || customer.name || 'Customer'}
                    </p>
                    {shippingAddress.line1 && (
                      <>
                        <p className="mt-1 text-sm text-slate-600">{shippingAddress.line1}</p>
                        {shippingAddress.line2 && (
                          <p className="text-sm text-slate-600">{shippingAddress.line2}</p>
                        )}
                        <p className="text-sm text-slate-600">
                          {shippingAddress.city}, {shippingAddress.state}{' '}
                          {shippingAddress.pincode ?? shippingAddress.postalCode}
                        </p>
                        {shippingAddress.country && (
                          <p className="text-sm text-slate-600">{shippingAddress.country}</p>
                        )}
                      </>
                    )}
                    {(shippingAddress.contactPhone ?? shippingAddress.phone) && (
                      <a
                        href={`tel:${shippingAddress.contactPhone ?? shippingAddress.phone}`}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 font-mono text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:text-[#a8161b]"
                      >
                        <Phone className="h-3.5 w-3.5" />
                        {shippingAddress.contactPhone ?? shippingAddress.phone}
                      </a>
                    )}
                  </div>
                </div>

                {shipment && (
                  <div>
                    <h3 className="mb-2 text-xs font-extrabold uppercase tracking-wider text-slate-400">
                      Shipment Tracking
                    </h3>
                    <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-600">
                            Courier:{' '}
                            <span className="font-bold">{shipment.courierName || '—'}</span>
                          </p>
                          <p className="mt-1 text-sm text-slate-600">
                            AWB:{' '}
                            <span className="font-mono font-bold">{shipment.awbNumber || '—'}</span>
                          </p>
                        </div>
                        <Badge tone="info" className="text-xs font-bold">
                          {shipment.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Payment Information */}
          {payments.length > 0 && (
            <Card className="detail-card overflow-hidden rounded-xl bg-white shadow-sm">
              <CardBody className="p-6">
                <SectionTitle icon={CreditCard}>Payment Information</SectionTitle>
                <div className="space-y-3">
                  {payments.map((payment: any) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/50 p-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-slate-600 ring-1 ring-slate-200">
                          {payment.method === 'COD' ? (
                            <Wallet className="h-4 w-4" />
                          ) : (
                            <CreditCard className="h-4 w-4" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-slate-900">
                            {payment.method === 'COD'
                              ? 'Cash on delivery'
                              : `${payment.method ?? 'Online'} · ${
                                  payment.provider === 'RAZORPAY'
                                    ? 'Razorpay'
                                    : (payment.provider ?? '').replace(/^\w/, (c: string) =>
                                        c.toUpperCase(),
                                      )
                                }`}
                          </p>
                          <p className="truncate font-mono text-[11px] text-slate-400">
                            Ref {payment.id}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-black text-slate-900">{formatPaise(grandTotal)}</p>
                        <p className="text-[10px] text-slate-400">this package</p>
                        <span
                          className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-extrabold ring-1 ring-inset ${
                            payment.status === 'CAPTURED'
                              ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                              : payment.status === 'FAILED'
                                ? 'bg-rose-50 text-rose-700 ring-rose-200'
                                : 'bg-amber-50 text-amber-800 ring-amber-200'
                          }`}
                        >
                          {
                            describePayment([payment], 'seller', { orderStatus: subOrder.status })
                              .label
                          }
                        </span>
                        {(() => {
                          const d = describePayment([payment], 'seller', {
                            orderStatus: subOrder.status,
                          }).detail;
                          return d ? (
                            <p className="mt-1 max-w-[14rem] text-[10px] text-slate-500">{d}</p>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Right Column - Actions & Timeline */}
        <div className="space-y-6">
          {/* Action Buttons */}
          <Card className="detail-card overflow-hidden rounded-xl bg-white shadow-sm">
            <CardBody className="p-6">
              <SectionTitle icon={Settings2}>Actions</SectionTitle>
              <div className="space-y-3">
                {NEXT_STATUS[subOrder.status] && (
                  <Button
                    className="w-full rounded-lg border-[#a8161b] bg-[#a8161b] py-2.5 text-sm font-extrabold text-white shadow-sm hover:border-[#850c10] hover:bg-[#850c10]"
                    onClick={() => transition.mutate({ status: NEXT_STATUS[subOrder.status]! })}
                    loading={transition.isPending}
                  >
                    {NEXT_ACTION_LABEL[subOrder.status]}
                  </Button>
                )}
                {VENDOR_CANCELLABLE.includes(subOrder.status) && (
                  <Button
                    variant="outline"
                    className="w-full rounded-lg border-red-200 py-2.5 text-sm font-extrabold text-red-600 hover:border-red-300 hover:bg-red-50"
                    onClick={() => setShowCancelModal(true)}
                    disabled={transition.isPending}
                  >
                    Cancel Order
                  </Button>
                )}
                {transition.error && (
                  <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                    {transition.error.message}
                  </p>
                )}
              </div>
            </CardBody>
          </Card>

          <FulfilmentPanel subOrder={subOrder} />
          <CourierCostsCard subOrderId={subOrder.id} />

          {/* COD Remittance (only for COD orders) */}
          {isCodOrder && (
            <Card className="detail-card overflow-hidden rounded-xl bg-white shadow-sm">
              <CardBody className="p-6">
                <h2 className="mb-3 flex items-center gap-2 text-lg font-black text-slate-900">
                  COD Remittance
                  <Badge
                    tone={
                      codRemittance?.status === 'REMITTED' || codRemittance?.status === 'RECONCILED'
                        ? 'success'
                        : codRemittance?.status === 'COLLECTED'
                          ? 'warning'
                          : 'neutral'
                    }
                    className="text-[10px] font-extrabold"
                  >
                    {codRemittance ? formatStatus(codRemittance.status) : 'Awaiting delivery'}
                  </Badge>
                </h2>
                {!codRemittance ? (
                  <p className="text-sm text-slate-500">
                    No remittance record yet. This will appear once the delivery agent collects cash
                    on delivery.
                  </p>
                ) : (
                  <div className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Amount
                      </p>
                      <p className="font-semibold text-slate-800">
                        {formatPaise(codRemittance.amountPaise)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Collected
                      </p>
                      <p className="text-slate-700">
                        {codRemittance.collectedAt
                          ? new Date(codRemittance.collectedAt).toLocaleString()
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Remitted to platform
                      </p>
                      <p className="text-slate-700">
                        {codRemittance.remittedAt
                          ? new Date(codRemittance.remittedAt).toLocaleString()
                          : '—'}
                      </p>
                    </div>
                  </div>
                )}
                <p className="mt-3 text-xs text-slate-400">
                  COD amounts are remitted to your wallet only after the platform reconciles
                  collection with the delivery agent.
                </p>
              </CardBody>
            </Card>
          )}

          {/* Return Requests */}
          {returnRequests.length > 0 && (
            <Card className="detail-card overflow-hidden rounded-xl bg-white shadow-sm">
              <CardBody className="p-6">
                <SectionTitle
                  icon={RotateCcw}
                  right={
                    <Badge tone="warning" className="text-[10px] font-extrabold">
                      {returnRequests.length}
                    </Badge>
                  }
                >
                  Return Requests
                </SectionTitle>
                <div className="space-y-3">
                  {returnRequests.map((rr: any) => (
                    <div
                      key={rr.id}
                      className="rounded-lg border border-amber-200 bg-amber-50/40 p-4"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-amber-700">
                            #{rr.id.slice(0, 8)}
                          </span>
                          <Badge tone="warning" className="text-[10px] font-extrabold">
                            {formatStatus(rr.status)}
                          </Badge>
                        </div>
                        <span className="text-xs text-slate-500">
                          {new Date(rr.createdAt).toLocaleString()}
                        </span>
                      </div>
                      {rr.reason && (
                        <p className="mt-2 text-sm text-slate-700">
                          <span className="font-semibold text-slate-500">Reason:</span> {rr.reason}
                        </p>
                      )}
                      {rr.fault && (
                        <p className="mt-1 text-xs text-slate-600">
                          <span className="font-semibold text-slate-500">Return pickup:</span>{' '}
                          {rr.fault === 'SELLER'
                            ? 'charged to you — the item was reported damaged, wrong or incomplete'
                            : 'paid by the customer from their refund'}
                        </p>
                      )}
                      {Array.isArray(rr.items) && rr.items.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            Items requested
                          </p>
                          {rr.items.map((it: any) => {
                            const li = lineItems.find((l: any) => l.id === it.lineItemId);
                            return (
                              <div
                                key={it.id}
                                className="flex items-center justify-between text-sm text-slate-700"
                              >
                                <span>{li?.productTitle ?? it.lineItemId}</span>
                                <span className="font-bold">× {it.qty}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {rr.returnShipment && (
                        <p className="mt-2 text-xs text-slate-600">
                          <span className="font-semibold text-slate-500">Return pickup:</span>{' '}
                          {formatStatus(rr.returnShipment.status)}
                          {rr.returnShipment.awbNumber
                            ? ` · ${rr.returnShipment.courierName ?? 'Courier'} ${rr.returnShipment.awbNumber}`
                            : ''}
                        </p>
                      )}
                      {rr.resolution && (
                        <p className="mt-2 text-xs text-slate-500">
                          <span className="font-semibold">Resolution:</span> {rr.resolution}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-slate-400">
                  Return approvals and refunds are processed by the admin team.
                </p>
              </CardBody>
            </Card>
          )}

          {/* Order Timeline */}
          <Card className="detail-card overflow-hidden rounded-xl bg-white shadow-sm">
            <CardBody className="p-6">
              <SectionTitle icon={Clock}>Order Timeline</SectionTitle>
              <div className="space-y-4">
                {statusEvents.length === 0 ? (
                  <p className="text-sm text-slate-500">No status updates yet.</p>
                ) : (
                  <ol className="relative">
                    {[...statusEvents]
                      // Newest first, whatever order the API sent them in.
                      .sort(
                        (a: any, b: any) =>
                          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                      )
                      .map((event: any, idx: number, all: any[]) => {
                        const latest = idx === 0;
                        const at = new Date(event.createdAt);
                        return (
                          <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
                            {idx < all.length - 1 && (
                              <span
                                className="absolute left-[11px] top-7 h-[calc(100%-1rem)] w-0.5 bg-gradient-to-b from-slate-300 to-slate-200"
                                aria-hidden
                              />
                            )}
                            <span
                              className={`relative z-10 mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full ring-4 ${
                                latest
                                  ? 'bg-[#a8161b] text-white ring-[#a8161b]/15'
                                  : 'bg-white text-slate-500 ring-slate-100'
                              }`}
                            >
                              <span
                                className={`h-2 w-2 rounded-full ${latest ? 'bg-white' : 'bg-slate-400'}`}
                              />
                            </span>
                            <div
                              className={`min-w-0 flex-1 rounded-xl border p-3 ${
                                latest
                                  ? 'border-[#a8161b]/20 bg-[#a8161b]/[0.04] shadow-sm'
                                  : 'border-slate-200 bg-white'
                              }`}
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span
                                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-extrabold ring-1 ring-inset ${statusPillClass(event.toStatus)}`}
                                >
                                  {formatStatus(event.toStatus)}
                                </span>
                                {latest && (
                                  <span className="rounded-full bg-[#a8161b] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white">
                                    Latest
                                  </span>
                                )}
                              </div>
                              {event.note && (
                                <p className="mt-1.5 text-[13px] font-medium text-slate-800">
                                  {event.note}
                                </p>
                              )}
                              <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px]">
                                <span className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2 py-0.5 font-bold text-white">
                                  <Clock className="h-3 w-3" />
                                  {at.toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })}
                                  <span className="text-white/60">·</span>
                                  {at.toLocaleTimeString('en-IN', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                                {event.fromStatus && (
                                  <span className="text-[11.5px] font-semibold text-slate-500">
                                    after {formatStatus(event.fromStatus)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                  </ol>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Customer Information */}
          <Card className="detail-card overflow-hidden rounded-xl bg-white shadow-sm">
            <CardBody className="p-6">
              <SectionTitle icon={User}>Customer</SectionTitle>
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#a8161b] to-[#c2410c] text-base font-black text-white">
                  {(customer.name || shippingAddress.contactName || 'C')
                    .trim()
                    .charAt(0)
                    .toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="truncate font-bold text-slate-900">
                    {customer.name || shippingAddress.contactName || 'Customer'}
                  </p>
                  <p className="text-[12px] text-slate-500">
                    Contact details are on the delivery address.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <CancelOrderModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        isPending={transition.isPending}
        onConfirm={(reason) =>
          transition.mutate({ status: 'CANCELLED', ...(reason ? { note: reason } : {}) })
        }
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// RETURNS
// ══════════════════════════════════════════════════════════════════

const RETURN_FILTER_LABELS: Record<string, string> = {
  RETURN_REQUESTED: 'Return requested',
  RETURN_APPROVED: 'Return approved',
  RETURN_RECEIVED: 'Return received',
  REFUNDED: 'Refunded',
  CLOSED: 'Closed',
};

/** What the seller should expect at each return step. The platform makes the calls. */
const RETURN_NEXT_STEP: Record<string, string> = {
  RETURN_REQUESTED:
    'Terra Shoppy is reviewing this request. If it is approved, a pickup is booked and the parcel comes back to you.',
  RETURN_APPROVED:
    'Approved — the courier will collect the parcel from the customer and bring it back to your pickup address. When it reaches you, inspect it in "Inspect returns" above.',
  RETURN_RECEIVED:
    'The parcel is back with you. The customer is refunded and the amount is adjusted in your settlement.',
  REFUNDED: 'The customer has been refunded. The amount is reversed in your settlement.',
  CLOSED: 'This return is closed.',
};

export function ReturnsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('RETURN_REQUESTED');

  const { data, isLoading } = useQuery({
    queryKey: ['vendor-orders-returns', statusFilter],
    queryFn: () => {
      const opts: Record<string, unknown> = { pageSize: 50 };
      if (statusFilter) opts.status = statusFilter;
      return api.orders.vendorOrders(opts);
    },
  });

  const returnOrders = data?.data ?? [];

  return (
    <div className="space-y-4">
      <style>{`
        .order-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          border: 1px solid rgba(226, 232, 240, 0.8) !important;
        }
        .order-card:hover {
          transform: translateY(-2px) !important;
          border-color: rgba(168, 22, 27, 0.15) !important;
          box-shadow: 0 10px 25px -12px rgba(168, 22, 27, 0.08) !important;
        }
        .filter-pill {
          transition: all 0.2s ease !important;
        }
      `}</style>

      <PageHeader
        title="Returns"
        description="Return requests from customers. Review and process RMAs."
      />

      <ReturnQcPanel />

      <div className="mb-2 flex flex-wrap gap-2">
        {['RETURN_REQUESTED', 'RETURN_APPROVED', 'RETURN_RECEIVED', 'REFUNDED', 'CLOSED'].map(
          (s) => {
            const isActive = statusFilter === s;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`filter-pill rounded-full border px-4 py-1.5 text-xs font-bold transition-all duration-200 ${
                  isActive
                    ? 'border-[#a8161b] bg-gradient-to-r from-[#a8161b] to-[#850c10] text-white shadow-md shadow-red-900/10'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-[#a8161b] hover:bg-[#a8161b]/5 hover:text-[#a8161b]'
                }`}
              >
                {RETURN_FILTER_LABELS[s] ?? s}
              </button>
            );
          },
        )}
      </div>

      {isLoading ? (
        <div className="grid min-h-[200px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : returnOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200/80 bg-white p-8 shadow-sm">
          <EmptyState
            title="No returns found"
            description="Return requests will appear here when they match this status."
          />
        </div>
      ) : (
        <div className="space-y-3">
          {returnOrders.map((sub: any) => (
            <Card key={sub.id} className="order-card overflow-hidden rounded-xl bg-white shadow-sm">
              <CardBody className="p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-black text-[#a8161b]">
                      #{sub.orderGroup?.orderNumber ?? '—'}
                    </span>
                    <span className="inline-block rounded-md border border-slate-100 bg-slate-50 px-2.5 py-0.5 text-xs font-bold text-slate-400">
                      {sub.orderGroup?.customer?.name ??
                        sub.orderGroup?.customer?.email ??
                        'Guest Customer'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-extrabold text-slate-800">
                      {formatPaise(sub.totalPaise ?? 0)}
                    </span>
                    <Badge
                      tone={SUB_STATUS_TONE[sub.status] ?? 'neutral'}
                      className="text-[10px] font-extrabold"
                    >
                      {formatStatus(sub.status)}
                    </Badge>
                  </div>
                </div>

                {sub.lineItems && sub.lineItems.length > 0 && (
                  <div className="mt-3.5 space-y-2 border-t border-dashed border-slate-100 pt-3">
                    <span className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                      Items returned:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {sub.lineItems.map((li: any) => (
                        <span
                          key={li.id}
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-200/50 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-sm"
                        >
                          {li.imageUrl && (
                            <img
                              src={li.imageUrl}
                              alt=""
                              className="h-6 w-6 rounded object-cover"
                              loading="lazy"
                            />
                          )}
                          <span className="text-slate-800">{li.productTitle}</span>
                          <span className="py-0.2 rounded bg-[#a8161b]/10 px-1.5 text-[10px] font-extrabold text-[#a8161b]">
                            × {li.qty}
                          </span>
                          <span className="text-slate-400">
                            ({formatPaise(li.lineSubtotalPaise)})
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* What happens next. Returns are decided by Terra Shoppy (the
                    /admin/returns endpoints); the seller's own status endpoint
                    refuses every RETURN_* change, so the approve / receive /
                    refund buttons that were here could only ever fail. */}
                {RETURN_NEXT_STEP[sub.status] && (
                  <p className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-900">
                    {RETURN_NEXT_STEP[sub.status]}
                  </p>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// WALLET
// ══════════════════════════════════════════════════════════════════

const SETTLEMENT_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING_COD_REMITTANCE: 'warning',
  PENDING_REFUND_WINDOW: 'warning',
  ELIGIBLE: 'success',
  SETTLED: 'success',
  HELD: 'warning',
  REVERSED: 'danger',
};

/**
 * Plain-language reason a settlement is not money yet.
 *
 * The raw enum tells a seller nothing about who they are waiting on, and "why has this not
 * paid out" is the question this screen exists to answer.
 */
const SETTLEMENT_STATUS_LABEL: Record<string, string> = {
  PENDING_COD_REMITTANCE: 'Awaiting cash from courier',
  PENDING_REFUND_WINDOW: 'In return window',
  ELIGIBLE: 'Ready to settle',
  SETTLED: 'Settled',
  HELD: 'On hold',
  REVERSED: 'Reversed (refunded)',
};
const PAYOUT_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  RELEASED: 'success',
  REJECTED: 'danger',
  FAILED: 'danger',
};
const BANK_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING: 'warning',
  VERIFIED: 'success',
  REJECTED: 'danger',
  DISABLED: 'neutral',
};

export function WalletPage() {
  const qc = useQueryClient();
  // Deep-link support: `/wallet?tab=banks` (or transactions/payouts) opens
  // directly on that tab so quick actions elsewhere in the app can point
  // straight at the relevant section.
  const [searchParams] = useSearchParams();
  const initialTab = (() => {
    const requested = searchParams.get('tab');
    return requested === 'banks' || requested === 'transactions' || requested === 'payouts'
      ? requested
      : 'overview';
  })();
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'payouts' | 'banks'>(
    initialTab,
  );

  const wallet = useQuery({
    queryKey: ['vendor-wallet'],
    queryFn: () => api.wallet.vendorMySummary(),
  });
  const eligibility = useQuery({
    queryKey: ['vendor-eligibility'],
    queryFn: () => api.settlements.myEligibility(),
  });
  const settlements = useQuery({
    queryKey: ['vendor-settlements'],
    queryFn: () => api.settlements.mySettlements(),
  });
  const payouts = useQuery({
    queryKey: ['vendor-payouts'],
    queryFn: () => api.settlements.myPayouts(),
  });
  const bankAccounts = useQuery({
    queryKey: ['vendor-bank-accounts'],
    queryFn: () => api.settlements.myBankAccounts(),
  });
  // Only fetch the ledger when the Transactions tab is open — it can be long.
  const ledger = useQuery({
    queryKey: ['vendor-wallet-ledger'],
    queryFn: () => api.wallet.vendorMyLedger(),
    enabled: activeTab === 'transactions',
  });

  const [bankForm, setBankForm] = useState({
    accountHolderName: '',
    accountNumber: '',
    ifsc: '',
    isDefault: true,
  });
  const [payoutForm, setPayoutForm] = useState({ amountRupees: '', bankAccountId: '', note: '' });

  const addBank = useMutation({
    mutationFn: () =>
      api.settlements.addMyBankAccount({
        accountHolderName: bankForm.accountHolderName.trim(),
        accountNumber: bankForm.accountNumber.trim(),
        ifsc: bankForm.ifsc.toUpperCase().trim(),
        isDefault: bankForm.isDefault,
      }),
    onSuccess: () => {
      setBankForm({ accountHolderName: '', accountNumber: '', ifsc: '', isDefault: true });
      void qc.invalidateQueries({ queryKey: ['vendor-bank-accounts'] });
    },
  });

  const requestPayout = useMutation({
    mutationFn: () => {
      const amountPaise = Math.round(Number(payoutForm.amountRupees) * 100);
      const body: { amountPaise: number; bankAccountId?: string; note?: string } = { amountPaise };
      if (payoutForm.bankAccountId) body.bankAccountId = payoutForm.bankAccountId;
      if (payoutForm.note) body.note = payoutForm.note;
      return api.settlements.requestPayout(body);
    },
    onSuccess: () => {
      setPayoutForm({ amountRupees: '', bankAccountId: '', note: '' });
      void qc.invalidateQueries({ queryKey: ['vendor-payouts'] });
      void qc.invalidateQueries({ queryKey: ['vendor-eligibility'] });
    },
  });

  // The client already unwraps the API's { data } envelope; reading .data off
  // the array again left all three lists permanently empty.
  const bankAccountsList: any[] = bankAccounts.data ?? [];
  const settlementsList: any[] = settlements.data ?? [];
  const payoutsList: any[] = payouts.data ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <style>{`
        .wallet-input {
          border-radius: 0.5rem !important;
          border-color: #e2e8f0 !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          font-size: 0.8125rem !important;
          background-color: #ffffff !important;
        }
        .wallet-input:focus {
          border-color: #a8161b !important;
          box-shadow: 0 0 0 3px rgba(168, 22, 27, 0.08) !important;
          outline: none !important;
        }
        .wallet-table-row {
          transition: all 0.2s ease !important;
        }
        .wallet-table-row:hover {
          background-color: rgba(168, 22, 27, 0.015) !important;
        }
      `}</style>

      <PageHeader
        title="Wallet & payouts"
        description="Your settlement balance, payout requests and bank accounts."
      />

      {/* ── TAB NAVIGATION ── */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { key: 'overview', label: 'Overview', icon: '💰' },
          { key: 'transactions', label: 'Transactions', icon: '📊' },
          { key: 'payouts', label: 'Payouts', icon: '💸' },
          { key: 'banks', label: 'Bank Accounts', icon: '🏦' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-bold transition-all ${
              activeTab === tab.key
                ? 'border-[#a8161b] text-[#a8161b]'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'overview' && (
        <>
          {/* ── TOP THREE METRIC CARDS (With hyper-realistic 3D skeuomorphic SVGs) ── */}
          <section>
            <div className="grid gap-4 sm:grid-cols-3">
              {/* Cash Balance Card */}
              <Card className="transform overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
                <CardBody className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                      Cash balance
                    </p>
                    <p className="mt-2 text-2xl font-black text-slate-800 sm:text-3xl">
                      {wallet.isLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        formatPaise(wallet.data?.cashPaise ?? 0)
                      )}
                    </p>
                  </div>
                  <div className="flex-shrink-0 cursor-pointer transition-all duration-300 hover:rotate-3 hover:scale-110">
                    <svg className="h-11 w-11" viewBox="0 0 48 48" fill="none">
                      <defs>
                        <linearGradient
                          id="goldCoinTop"
                          x1="24"
                          y1="4"
                          x2="24"
                          y2="16"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor="#fffbeb" />
                          <stop offset="100%" stopColor="#fbbf24" />
                        </linearGradient>
                        <linearGradient
                          id="goldCoinSide"
                          x1="12"
                          y1="16"
                          x2="36"
                          y2="44"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor="#d97706" />
                          <stop offset="100%" stopColor="#78350f" />
                        </linearGradient>
                        <filter id="goldCoinShadow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow
                            dx="0"
                            dy="4"
                            stdDeviation="3"
                            floodColor="#d97706"
                            floodOpacity="0.4"
                          />
                        </filter>
                      </defs>
                      <g filter="url(#goldCoinShadow)">
                        <ellipse cx="24" cy="36" rx="16" ry="6" fill="url(#goldCoinSide)" />
                        <ellipse cx="24" cy="34" rx="16" ry="6" fill="url(#goldCoinTop)" />
                        <ellipse cx="24" cy="28" rx="16" ry="6" fill="url(#goldCoinSide)" />
                        <ellipse cx="24" cy="26" rx="16" ry="6" fill="url(#goldCoinTop)" />
                        <ellipse cx="24" cy="20" rx="16" ry="6" fill="url(#goldCoinSide)" />
                        <ellipse cx="24" cy="18" rx="16" ry="6" fill="url(#goldCoinTop)" />
                        <text
                          x="24"
                          y="21"
                          fontFamily="sans-serif"
                          fontSize="11"
                          fontWeight="900"
                          fill="#b45309"
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          ₹
                        </text>
                      </g>
                    </svg>
                  </div>
                </CardBody>
              </Card>

              {/* Held in Reserve Card */}
              <Card className="transform overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
                <CardBody className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                      Held in reserve
                    </p>
                    <p className="mt-2 text-2xl font-black text-slate-500 sm:text-3xl">
                      {wallet.isLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        formatPaise(wallet.data?.heldPaise ?? 0)
                      )}
                    </p>
                  </div>
                  <div className="flex-shrink-0 cursor-pointer transition-all duration-300 hover:rotate-3 hover:scale-110">
                    <svg className="h-11 w-11" viewBox="0 0 48 48" fill="none">
                      <defs>
                        <linearGradient
                          id="lockBody"
                          x1="24"
                          y1="20"
                          x2="24"
                          y2="44"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor="#94a3b8" />
                          <stop offset="100%" stopColor="#334155" />
                        </linearGradient>
                        <linearGradient
                          id="lockShackle"
                          x1="24"
                          y1="4"
                          x2="24"
                          y2="24"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor="#cbd5e1" />
                          <stop offset="100%" stopColor="#64748b" />
                        </linearGradient>
                        <filter id="lockShadow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow
                            dx="0"
                            dy="4"
                            stdDeviation="3"
                            floodColor="#334155"
                            floodOpacity="0.3"
                          />
                        </filter>
                      </defs>
                      <g filter="url(#lockShadow)">
                        <path
                          d="M14,22 V14 A10,10 0 0,1 34,14 V22"
                          stroke="url(#lockShackle)"
                          strokeWidth="5.5"
                          strokeLinecap="round"
                          fill="none"
                        />
                        <rect x="10" y="20" width="28" height="22" rx="6" fill="url(#lockBody)" />
                        <circle cx="24" cy="29" r="2.5" fill="#1e293b" />
                        <polygon points="22.5,29 25.5,29 26,36 22,36" fill="#1e293b" />
                      </g>
                    </svg>
                  </div>
                </CardBody>
              </Card>

              {/* Withdrawable Now Card */}
              <Card className="transform overflow-hidden rounded-xl border border-l-4 border-slate-100 border-l-emerald-500 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
                <CardBody className="flex items-center justify-between p-4">
                  <div className="flex-1">
                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                      Withdrawable now
                    </p>
                    <p className="mt-2 text-2xl font-black text-emerald-600 sm:text-3xl">
                      {wallet.isLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        formatPaise(wallet.data?.withdrawablePaise ?? 0)
                      )}
                    </p>
                    {eligibility.data && (
                      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                          Threshold:{' '}
                          <span className="font-extrabold text-slate-600">
                            {formatPaise(eligibility.data.payoutThresholdPaise)}
                          </span>
                        </span>
                        {eligibility.data.eligibleForAutoPayout && (
                          <Badge
                            tone="success"
                            className="text-[8px] font-extrabold uppercase tracking-wider"
                          >
                            Auto-payout
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 cursor-pointer transition-all duration-300 hover:rotate-3 hover:scale-110">
                    <svg className="h-11 w-11" viewBox="0 0 48 48" fill="none">
                      <defs>
                        <linearGradient
                          id="greenCashTop"
                          x1="24"
                          y1="6"
                          x2="24"
                          y2="22"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor="#d1fae5" />
                          <stop offset="100%" stopColor="#10b981" />
                        </linearGradient>
                        <linearGradient
                          id="greenCashSide"
                          x1="10"
                          y1="18"
                          x2="24"
                          y2="44"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor="#059669" />
                          <stop offset="100%" stopColor="#064e3b" />
                        </linearGradient>
                        <filter id="greenCashShadow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow
                            dx="0"
                            dy="4"
                            stdDeviation="3.5"
                            floodColor="#10b981"
                            floodOpacity="0.35"
                          />
                        </filter>
                      </defs>
                      <g filter="url(#greenCashShadow)">
                        <polygon points="24,6 40,15 24,24 8,15" fill="url(#greenCashTop)" />
                        <polygon points="8,15 24,24 24,40 8,31" fill="url(#greenCashSide)" />
                        <polygon points="24,24 40,15 40,31 24,40" fill="url(#greenCashSide)" />
                        <path
                          d="M19,21 l3,3 l7,-7"
                          stroke="#ffffff"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                          opacity="0.95"
                        />
                      </g>
                    </svg>
                  </div>
                </CardBody>
              </Card>
            </div>
          </section>

          {/* ── ALIGNED DUAL FORMS SECTION ── */}
          <section className="grid gap-6 md:grid-cols-2">
            {/* Request Payout Form Card */}
            <Card className="flex h-full transform flex-col overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <CardBody className="flex h-full flex-1 flex-col p-4">
                <div className="mb-4 flex items-center gap-2 border-b border-slate-100 pb-3">
                  <svg
                    className="h-5 w-5 flex-shrink-0 cursor-pointer transition-transform duration-300 hover:rotate-6 hover:scale-110"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <defs>
                      <linearGradient
                        id="payoutArrow"
                        x1="12"
                        y1="2"
                        x2="12"
                        y2="22"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0%" stopColor="#fca5a5" />
                        <stop offset="100%" stopColor="#b91c1c" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M12,2 L19,9 L14,9 L14,20 L10,20 L10,9 L5,9 Z"
                      fill="url(#payoutArrow)"
                    />
                  </svg>
                  <h3 className="text-slate-850 text-xs font-extrabold uppercase tracking-wider">
                    Request payout
                  </h3>
                </div>

                {(() => {
                  const amountPaise = Math.round(Number(payoutForm.amountRupees || 0) * 100);
                  const withdrawable = eligibility.data?.withdrawablePaise ?? 0;
                  const threshold = eligibility.data?.payoutThresholdPaise ?? 0;
                  const belowThreshold = withdrawable < threshold;
                  const exceedsBalance = amountPaise > 0 && amountPaise > withdrawable;
                  const validAmount = amountPaise > 0 && !exceedsBalance && !belowThreshold;
                  return (
                    <form
                      className="flex flex-1 flex-col justify-between"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (validAmount) requestPayout.mutate();
                      }}
                    >
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <label className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                            Amount (₹)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={payoutForm.amountRupees}
                            onChange={(e) =>
                              setPayoutForm((f) => ({ ...f, amountRupees: e.target.value }))
                            }
                            className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition-all"
                            required
                          />
                          {belowThreshold && (
                            <p className="mt-1 text-xs font-bold text-amber-600">
                              Withdrawable balance ({formatPaise(withdrawable)}) is below the
                              minimum payout threshold ({formatPaise(threshold)}). Earn more before
                              requesting.
                            </p>
                          )}
                          {!belowThreshold && exceedsBalance && (
                            <p className="mt-1 text-xs font-bold text-rose-600">
                              Amount exceeds withdrawable balance ({formatPaise(withdrawable)}).
                            </p>
                          )}
                        </div>

                        <div className="space-y-1">
                          <label className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                            Bank account
                          </label>
                          <select
                            className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition-all"
                            value={payoutForm.bankAccountId}
                            onChange={(e) =>
                              setPayoutForm((f) => ({ ...f, bankAccountId: e.target.value }))
                            }
                          >
                            <option value="">Default verified account</option>
                            {bankAccountsList
                              .filter((b: any) => b.status === 'VERIFIED')
                              .map((b: any) => (
                                <option key={b.id} value={b.id}>
                                  {b.accountHolderName} · {b.accountNumberMasked} · {b.ifsc}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div className="space-y-1">
                          <label className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                            Note
                          </label>
                          <input
                            type="text"
                            value={payoutForm.note}
                            onChange={(e) => setPayoutForm((f) => ({ ...f, note: e.target.value }))}
                            className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition-all"
                          />
                        </div>
                      </div>

                      <div className="mt-auto pt-4">
                        <Button
                          type="submit"
                          disabled={!validAmount || requestPayout.isPending}
                          className="w-full rounded-lg border-[#a8161b] bg-[#a8161b] py-2 text-xs font-extrabold text-white shadow-sm hover:border-[#850c10] hover:bg-[#850c10] disabled:opacity-60"
                          loading={requestPayout.isPending}
                        >
                          Request payout
                        </Button>
                        {requestPayout.isSuccess && (
                          <p className="mt-2 text-xs font-bold text-emerald-600">
                            Payout requested.
                          </p>
                        )}
                        {requestPayout.isError && (
                          <p className="mt-2 text-xs font-bold text-red-600">
                            Request failed — check threshold/withdrawable balance.
                          </p>
                        )}
                      </div>
                    </form>
                  );
                })()}
              </CardBody>
            </Card>

            {/* Add Bank Account Form Card */}
            <Card className="flex h-full transform flex-col overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <CardBody className="flex h-full flex-1 flex-col p-4">
                <div className="mb-4 flex items-center gap-2 border-b border-slate-100 pb-3">
                  <svg
                    className="h-5 w-5 flex-shrink-0 cursor-pointer transition-transform duration-300 hover:rotate-6 hover:scale-110"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <defs>
                      <linearGradient
                        id="bank3D"
                        x1="12"
                        y1="2"
                        x2="12"
                        y2="22"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0%" stopColor="#fca5a5" />
                        <stop offset="100%" stopColor="#b91c1c" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M2,20 L22,20 L22,22 L2,22 Z M12,2 L2,7 L2,9 L22,9 L22,7 Z M4,11 L7,11 L7,18 L4,18 Z M10,11 L13,11 L13,18 L10,18 Z M16,11 L19,11 L19,18 L16,18 Z"
                      fill="url(#bank3D)"
                    />
                  </svg>
                  <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">
                    Add bank account
                  </h3>
                </div>

                <form
                  className="flex flex-1 flex-col justify-between"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (bankForm.accountHolderName && bankForm.accountNumber && bankForm.ifsc)
                      addBank.mutate();
                  }}
                >
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <label className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                        Account holder name
                      </label>
                      <input
                        type="text"
                        value={bankForm.accountHolderName}
                        onChange={(e) =>
                          setBankForm((f) => ({ ...f, accountHolderName: e.target.value }))
                        }
                        className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition-all"
                        required
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                        Account number
                      </label>
                      <input
                        type="text"
                        value={bankForm.accountNumber}
                        onChange={(e) =>
                          setBankForm((f) => ({ ...f, accountNumber: e.target.value }))
                        }
                        className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition-all"
                        required
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                        Ifsc
                      </label>
                      <input
                        type="text"
                        value={bankForm.ifsc}
                        onChange={(e) =>
                          setBankForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))
                        }
                        className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase text-slate-800 outline-none transition-all"
                        placeholder="HDFC0001234"
                        required
                      />
                    </div>

                    <div className="pt-1">
                      <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs font-bold text-slate-500">
                        <input
                          type="checkbox"
                          checked={bankForm.isDefault}
                          onChange={(e) =>
                            setBankForm((f) => ({ ...f, isDefault: e.target.checked }))
                          }
                          className="h-4 w-4 rounded border-slate-200 text-[#a8161b] accent-[#a8161b] focus:ring-[#a8161b]"
                        />
                        Set as default
                      </label>
                    </div>
                  </div>

                  <div className="mt-auto pt-4">
                    <Button
                      type="submit"
                      className="w-full rounded-lg border-[#a8161b] bg-[#a8161b] py-2 text-xs font-extrabold text-white shadow-sm hover:border-[#850c10] hover:bg-[#850c10]"
                      loading={addBank.isPending}
                    >
                      Add account
                    </Button>
                    {addBank.isSuccess && (
                      <p className="mt-2 text-xs font-bold text-emerald-600">
                        Bank account submitted for verification.
                      </p>
                    )}
                  </div>
                </form>
              </CardBody>
            </Card>
          </section>

          <section className="space-y-2.5">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">
              Bank accounts
            </h3>
            {bankAccounts.isLoading ? (
              <Spinner size="sm" />
            ) : bankAccountsList.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200/80 bg-white p-8 shadow-sm">
                <EmptyState
                  title="No bank accounts"
                  description="Add a bank account to receive payouts."
                />
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200/60 bg-white shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200/60 bg-slate-50/50 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3">Holder</th>
                      <th className="px-4 py-3">Account</th>
                      <th className="px-4 py-3">Ifsc</th>
                      <th className="px-4 py-3">Default</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankAccountsList.map((b: any) => (
                      <tr
                        key={b.id}
                        className="wallet-table-row border-b border-slate-100 last:border-0"
                      >
                        <td className="px-4 py-3 text-xs font-bold text-slate-800 sm:text-sm">
                          {b.accountHolderName}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">
                          {b.accountNumberMasked}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{b.ifsc}</td>
                        <td className="px-4 py-3">
                          {b.isDefault ? (
                            <Badge
                              tone="success"
                              className="text-[9px] font-extrabold uppercase tracking-wider"
                            >
                              Default
                            </Badge>
                          ) : (
                            <span className="text-xs font-bold text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            tone={BANK_STATUS_TONE[b.status] ?? 'neutral'}
                            className="text-[10px] font-extrabold"
                          >
                            {b.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="space-y-2.5">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">
              Settlements
            </h3>
            {settlements.isLoading ? (
              <Spinner size="sm" />
            ) : settlementsList.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200/80 bg-white p-8 shadow-sm">
                <p className="text-xs font-bold text-slate-400">
                  No settlements yet. Fulfill orders to earn settlements.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200/60 bg-white shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200/60 bg-slate-50/50 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3">Sub-order</th>
                      <th className="px-4 py-3">Gross</th>
                      <th className="px-4 py-3">Commission</th>
                      <th className="px-4 py-3">Tcs</th>
                      <th className="px-4 py-3">Tds</th>
                      <th className="px-4 py-3">Shipping</th>
                      <th className="px-4 py-3">Reserve</th>
                      <th className="px-4 py-3">Net</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settlementsList.map((s: any) => (
                      <tr
                        key={s.id}
                        className="wallet-table-row border-b border-slate-100 last:border-0"
                      >
                        <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-600">
                          {s.subOrderId.slice(0, 10)}…
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-slate-700">
                          {formatPaise(s.grossPaise)}
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-red-600">
                          −{formatPaise(s.commissionPaise)}
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-red-500">
                          −{formatPaise(s.tcsPaise)}
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-red-500">
                          −{formatPaise(s.tdsPaise)}
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-red-500">
                          −{formatPaise(s.shippingFeePaise ?? 0)}
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-amber-600">
                          −{formatPaise(s.reserveHeldPaise)}
                        </td>
                        <td className="px-4 py-3 text-xs font-extrabold text-slate-800">
                          {formatPaise(s.netPayablePaise)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            tone={SETTLEMENT_STATUS_TONE[s.status] ?? 'neutral'}
                            className="text-[10px] font-extrabold"
                          >
                            {SETTLEMENT_STATUS_LABEL[s.status] ?? s.status.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="space-y-2.5">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">
              Payout requests
            </h3>
            {payouts.isLoading ? (
              <Spinner size="sm" />
            ) : payoutsList.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200/80 bg-white p-8 shadow-sm">
                <p className="text-xs font-bold text-slate-400">No payout requests yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200/60 bg-white shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200/60 bg-slate-50/50 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3">Requested</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Bank ref</th>
                      <th className="px-4 py-3">Released</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutsList.map((p: any) => (
                      <tr
                        key={p.id}
                        className="wallet-table-row border-b border-slate-100 last:border-0"
                      >
                        <td className="px-4 py-3 text-xs font-medium text-slate-600">
                          {new Date(p.requestedAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm font-extrabold text-slate-800">
                          {formatPaise(p.amountPaise)}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-500">
                          {p.bankRef ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-slate-400">
                          {p.releasedAt ? new Date(p.releasedAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            tone={PAYOUT_STATUS_TONE[p.status] ?? 'neutral'}
                            className="text-[10px] font-extrabold"
                          >
                            {p.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {/* ── TRANSACTIONS TAB ── */}
      {activeTab === 'transactions' && (
        <div className="space-y-6">
          {/* Transaction History Header */}
          <Card className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
            <CardBody className="p-6">
              <h2 className="mb-4 text-lg font-black text-slate-900">Transaction History</h2>
              <p className="text-sm text-slate-600">
                Complete history of all settlements, payouts, and wallet transactions.
              </p>
            </CardBody>
          </Card>

          {/* Ledger Entries */}
          <Card className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
            <CardBody className="p-0">
              {ledger.isLoading ? (
                <div className="grid place-items-center p-8">
                  <Spinner size="md" />
                </div>
              ) : ledger.isError ? (
                <p className="p-6 text-sm text-rose-600">Failed to load ledger entries.</p>
              ) : (ledger.data ?? []).length === 0 ? (
                <p className="p-6 text-sm text-slate-500">
                  No ledger entries yet. Settlements, holds, and payouts will appear here.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-4 py-3">When</th>
                        <th className="px-4 py-3">Account</th>
                        <th className="px-4 py-3">Kind</th>
                        <th className="px-4 py-3 text-right">Debit</th>
                        <th className="px-4 py-3 text-right">Credit</th>
                        <th className="px-4 py-3">Memo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ledger.data ?? []).map((row) => (
                        <tr key={row.id} className="border-t border-slate-100">
                          <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                            {new Date(row.postedAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs font-semibold text-slate-800">
                              {row.accountCode}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">{row.kind}</td>
                          <td className="px-4 py-3 text-right font-semibold text-rose-600">
                            {row.debitPaise > 0 ? formatPaise(row.debitPaise) : '—'}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-emerald-600">
                            {row.creditPaise > 0 ? formatPaise(row.creditPaise) : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">{row.memo ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Holds/Reserves Breakdown */}
          {wallet.data && wallet.data.heldPaise > 0 && (
            <Card className="overflow-hidden rounded-xl border border-amber-100 bg-amber-50/30 shadow-sm">
              <CardBody className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-sm font-black text-amber-900">Funds on Hold</h3>
                    <p className="mt-1 text-2xl font-black text-amber-700">
                      {formatPaise(wallet.data.heldPaise)}
                    </p>
                  </div>
                  <Badge tone="warning" className="text-xs font-bold">
                    Reserved
                  </Badge>
                </div>

                {wallet.data.holdsByType && wallet.data.holdsByType.length > 0 && (
                  <div className="mt-4 space-y-2 border-t border-amber-200 pt-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-700">
                      Breakdown by type:
                    </p>
                    {wallet.data.holdsByType.map((hold: any, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded-lg border border-amber-200 bg-white px-3 py-2"
                      >
                        <span className="text-xs font-bold text-slate-700">
                          {hold.type.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm font-black text-amber-700">
                          {formatPaise(hold.paise)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Settlements as Transactions */}
          <section className="space-y-3">
            <h3 className="text-sm font-black text-slate-900">Settlement Transactions</h3>
            {settlements.isLoading ? (
              <div className="grid min-h-[200px] place-items-center">
                <Spinner size="lg" />
              </div>
            ) : settlementsList.length === 0 ? (
              <Card className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                <CardBody className="p-8">
                  <EmptyState
                    title="No transactions yet"
                    description="Settlements will appear here once orders are delivered."
                  />
                </CardBody>
              </Card>
            ) : (
              <div className="space-y-3">
                {settlementsList.map((s: any) => (
                  <Card
                    key={s.id}
                    className="transform overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <CardBody className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge
                              tone={SETTLEMENT_STATUS_TONE[s.status] ?? 'neutral'}
                              className="text-xs font-bold"
                            >
                              {SETTLEMENT_STATUS_LABEL[s.status] ?? s.status.replace(/_/g, ' ')}
                            </Badge>
                            <span className="font-mono text-xs text-slate-400">
                              Order: {s.subOrderId.slice(0, 12)}...
                            </span>
                          </div>

                          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Gross
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-700">
                                {formatPaise(s.grossPaise)}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Commission
                              </p>
                              <p className="mt-1 text-sm font-bold text-red-600">
                                −{formatPaise(s.commissionPaise)}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Tax (TCS+TDS)
                              </p>
                              <p className="mt-1 text-sm font-bold text-red-500">
                                −{formatPaise(s.tcsPaise + s.tdsPaise)}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Reserve
                              </p>
                              <p className="mt-1 text-sm font-bold text-amber-600">
                                −{formatPaise(s.reserveHeldPaise)}
                              </p>
                            </div>
                          </div>

                          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                            <span className="text-xs font-bold text-slate-500">Net Payable</span>
                            <span className="text-lg font-black text-emerald-600">
                              {formatPaise(s.netPayablePaise)}
                            </span>
                          </div>

                          {s.eligibleAt && (
                            <p className="mt-2 text-xs text-slate-400">
                              Eligible: {new Date(s.eligibleAt).toLocaleDateString('en-IN')}
                            </p>
                          )}
                          {s.settledAt && (
                            <p className="mt-1 text-xs font-bold text-emerald-600">
                              Settled: {new Date(s.settledAt).toLocaleDateString('en-IN')}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ── PAYOUTS TAB ── */}
      {activeTab === 'payouts' && (
        <div className="space-y-6">
          {/* Request Payout Form */}
          <Card className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
            <CardBody className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                  <defs>
                    <linearGradient id="payoutIcon" x1="12" y1="2" x2="12" y2="22">
                      <stop offset="0%" stopColor="#fca5a5" />
                      <stop offset="100%" stopColor="#b91c1c" />
                    </linearGradient>
                  </defs>
                  <path d="M12,2 L19,9 L14,9 L14,20 L10,20 L10,9 L5,9 Z" fill="url(#payoutIcon)" />
                </svg>
                <h2 className="text-lg font-black text-slate-900">Request Payout</h2>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (Number(payoutForm.amountRupees) > 0) requestPayout.mutate();
                }}
                className="space-y-4"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={payoutForm.amountRupees}
                      onChange={(e) =>
                        setPayoutForm((f) => ({ ...f, amountRupees: e.target.value }))
                      }
                      className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      required
                    />
                    {eligibility.data && (
                      <p className="text-xs text-slate-500">
                        Withdrawable: {formatPaise(eligibility.data.withdrawablePaise)}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Bank Account
                    </label>
                    <select
                      className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      value={payoutForm.bankAccountId}
                      onChange={(e) =>
                        setPayoutForm((f) => ({ ...f, bankAccountId: e.target.value }))
                      }
                    >
                      <option value="">Default verified account</option>
                      {bankAccountsList
                        .filter((b: any) => b.status === 'VERIFIED')
                        .map((b: any) => (
                          <option key={b.id} value={b.id}>
                            {b.accountHolderName} · {b.accountNumberMasked}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Note (Optional)
                  </label>
                  <input
                    type="text"
                    value={payoutForm.note}
                    onChange={(e) => setPayoutForm((f) => ({ ...f, note: e.target.value }))}
                    className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    placeholder="e.g., Monthly payout"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-lg bg-[#a8161b] py-2.5 text-sm font-bold text-white hover:bg-[#850c10]"
                  loading={requestPayout.isPending}
                >
                  Request Payout
                </Button>

                {requestPayout.isSuccess && (
                  <p className="text-sm font-bold text-emerald-600">
                    ✓ Payout requested successfully!
                  </p>
                )}
                {requestPayout.isError && (
                  <p className="text-sm font-bold text-red-600">
                    ✗ Request failed. Check your withdrawable balance.
                  </p>
                )}
              </form>
            </CardBody>
          </Card>

          {/* Payout History */}
          <section className="space-y-3">
            <h3 className="text-sm font-black text-slate-900">Payout History</h3>
            {payouts.isLoading ? (
              <div className="grid min-h-[200px] place-items-center">
                <Spinner size="lg" />
              </div>
            ) : payoutsList.length === 0 ? (
              <Card className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                <CardBody className="p-8">
                  <EmptyState
                    title="No payouts yet"
                    description="Your payout requests will appear here."
                  />
                </CardBody>
              </Card>
            ) : (
              <div className="space-y-3">
                {payoutsList.map((p: any) => (
                  <Card
                    key={p.id}
                    className="transform overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <CardBody className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge
                              tone={PAYOUT_STATUS_TONE[p.status] ?? 'neutral'}
                              className="text-xs font-bold"
                            >
                              {p.status}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {new Date(p.requestedAt).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </span>
                          </div>

                          <div className="mt-3 flex items-baseline gap-2">
                            <span className="text-2xl font-black text-slate-900">
                              {formatPaise(p.amountPaise)}
                            </span>
                            {p.note && <span className="text-xs text-slate-500">· {p.note}</span>}
                          </div>

                          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
                            {p.bankRef && (
                              <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                  Bank Reference
                                </p>
                                <p className="mt-1 font-mono text-xs font-bold text-slate-600">
                                  {p.bankRef}
                                </p>
                              </div>
                            )}
                            {p.releasedAt && (
                              <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                  Released On
                                </p>
                                <p className="mt-1 text-xs font-bold text-emerald-600">
                                  {new Date(p.releasedAt).toLocaleDateString('en-IN')}
                                </p>
                              </div>
                            )}
                            {p.failureReason && (
                              <div className="col-span-2">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-red-400">
                                  Failure Reason
                                </p>
                                <p className="mt-1 text-xs font-bold text-red-600">
                                  {p.failureReason}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ── BANK ACCOUNTS TAB ── */}
      {activeTab === 'banks' && (
        <div className="space-y-6">
          {/* Add Bank Account Form */}
          <Card className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
            <CardBody className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                  <defs>
                    <linearGradient id="bankIcon" x1="12" y1="2" x2="12" y2="22">
                      <stop offset="0%" stopColor="#fca5a5" />
                      <stop offset="100%" stopColor="#b91c1c" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M2,20 L22,20 L22,22 L2,22 Z M12,2 L2,7 L2,9 L22,9 L22,7 Z M4,11 L7,11 L7,18 L4,18 Z M10,11 L13,11 L13,18 L10,18 Z M16,11 L19,11 L19,18 L16,18 Z"
                    fill="url(#bankIcon)"
                  />
                </svg>
                <h2 className="text-lg font-black text-slate-900">Add Bank Account</h2>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (bankForm.accountHolderName && bankForm.accountNumber && bankForm.ifsc)
                    addBank.mutate();
                }}
                className="space-y-4"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Account Holder Name
                    </label>
                    <input
                      type="text"
                      value={bankForm.accountHolderName}
                      onChange={(e) =>
                        setBankForm((f) => ({ ...f, accountHolderName: e.target.value }))
                      }
                      className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={bankForm.accountNumber}
                      onChange={(e) =>
                        setBankForm((f) => ({ ...f, accountNumber: e.target.value }))
                      }
                      className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    value={bankForm.ifsc}
                    onChange={(e) =>
                      setBankForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))
                    }
                    className="wallet-input w-full rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase"
                    placeholder="HDFC0001234"
                    required
                  />
                </div>

                <div>
                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-bold text-slate-600">
                    <input
                      type="checkbox"
                      checked={bankForm.isDefault}
                      onChange={(e) => setBankForm((f) => ({ ...f, isDefault: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300 text-[#a8161b] focus:ring-[#a8161b]"
                    />
                    Set as default account
                  </label>
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-lg bg-[#a8161b] py-2.5 text-sm font-bold text-white hover:bg-[#850c10]"
                  loading={addBank.isPending}
                >
                  Add Bank Account
                </Button>

                {addBank.isSuccess && (
                  <p className="text-sm font-bold text-emerald-600">
                    ✓ Bank account submitted for verification!
                  </p>
                )}
              </form>
            </CardBody>
          </Card>

          {/* Bank Accounts List */}
          <section className="space-y-3">
            <h3 className="text-sm font-black text-slate-900">Your Bank Accounts</h3>
            {bankAccounts.isLoading ? (
              <div className="grid min-h-[200px] place-items-center">
                <Spinner size="lg" />
              </div>
            ) : bankAccountsList.length === 0 ? (
              <Card className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                <CardBody className="p-8">
                  <EmptyState
                    title="No bank accounts"
                    description="Add a bank account to receive payouts."
                  />
                </CardBody>
              </Card>
            ) : (
              <div className="space-y-3">
                {bankAccountsList.map((b: any) => (
                  <Card
                    key={b.id}
                    className="transform overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <CardBody className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge
                              tone={BANK_STATUS_TONE[b.status] ?? 'neutral'}
                              className="text-xs font-bold"
                            >
                              {b.status}
                            </Badge>
                            {b.isDefault && (
                              <Badge tone="success" className="text-xs font-bold">
                                DEFAULT
                              </Badge>
                            )}
                          </div>

                          <h3 className="mt-2 text-lg font-black text-slate-900">
                            {b.accountHolderName}
                          </h3>

                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Account Number
                              </p>
                              <p className="mt-1 font-mono text-sm font-bold text-slate-700">
                                {b.accountNumberMasked}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                IFSC Code
                              </p>
                              <p className="mt-1 font-mono text-sm font-bold text-slate-700">
                                {b.ifsc}
                              </p>
                            </div>
                          </div>

                          {b.verifiedAt && (
                            <p className="mt-3 text-xs text-emerald-600">
                              ✓ Verified on {new Date(b.verifiedAt).toLocaleDateString('en-IN')}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ONBOARDING
// ══════════════════════════════════════════════════════════════════

export function OnboardingPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user) as any;
  const navigate = useNavigate();

  // ── Multi-step wizard state ──
  const [currentStep, setCurrentStep] = useState(1);

  // Real-time vendor store query
  const { data: vendorStore, isLoading: isLoadingVendor } = useQuery({
    queryKey: ['vendor-store'],
    queryFn: () => api.vendors.getMyStore().catch(() => null),
  });

  // Real-time KYC query
  const { data: kycData, isLoading: isLoadingKyc } = useQuery({
    queryKey: ['kyc-status'],
    queryFn: () => api.users.getKycStatus(),
  });

  const pickupQuery = useQuery({
    queryKey: PICKUP_QUERY_KEY,
    queryFn: () => api.vendors.getMyPickupAddress(),
    enabled: !!vendorStore,
  });

  // ── Step 1: Store Profile ──
  const [storeName, setStoreName] = useState('');
  const [storeSlug, setStoreSlug] = useState('');
  const [storeDescription, setStoreDescription] = useState('');
  const [storeGstin, setStoreGstin] = useState('');
  // The pickup state (for the GSTIN check); the address itself is edited in
  // the pickup address card, which also sets it on the store.
  const [pickupStateCode, setPickupStateCode] = useState('');

  // ── Step 2: KYC Documents ──
  const [legalName, setLegalName] = useState('');
  const [pan, setPan] = useState('');
  const [gstin, setGstin] = useState('');
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');

  // Uploaded documents secure URLs/File IDs
  const [panDocFileId, setPanDocFileId] = useState('');
  const [addressProofFileId, setAddressProofFileId] = useState('');
  const [gstDocFileId, setGstDocFileId] = useState('');

  // Uploading and form submit states
  const [uploadProgress, setUploadProgress] = useState<Record<string, boolean>>({
    pan: false,
    address: false,
    gst: false,
  });
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // ── Auto-slugify store name ──
  useEffect(() => {
    if (storeName && !vendorStore) {
      const generatedSlug = storeName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');
      setStoreSlug(generatedSlug);
    }
  }, [storeName, vendorStore]);

  // ── Populate existing vendor store data ──
  useEffect(() => {
    if (vendorStore) {
      setStoreName(vendorStore.storeName || '');
      setStoreSlug(vendorStore.slug || '');
      setStoreDescription(vendorStore.description || '');
      setStoreGstin(vendorStore.gstin || '');
      setPickupStateCode(vendorStore.pickupStateCode || '');
    }
  }, [vendorStore]);

  // ── Store Registration Mutation ──
  const registerStoreMutation = useMutation({
    mutationFn: (body: any) => {
      if (vendorStore) {
        return api.vendors.updateMyStore(body);
      }
      return api.vendors.register(body);
    },
    onSuccess: () => {
      setSuccessMsg('Store profile saved successfully!');
      void queryClient.invalidateQueries({ queryKey: ['vendor-store'] });
      setTimeout(() => {
        setCurrentStep(2);
        setSuccessMsg('');
      }, 1000);
    },
    onError: (err: any) => {
      setErrorMsg(
        err.response?.data?.message ||
          err.message ||
          'Failed to save store profile. Please check your inputs.',
      );
    },
  });

  const submitKycMutation = useMutation({
    mutationFn: (body: any) => api.users.submitKyc(body),
    onSuccess: () => {
      setSuccessMsg(
        'KYC documents submitted successfully! Our compliance team is reviewing your profile.',
      );
      void queryClient.invalidateQueries({ queryKey: ['kyc-status'] });
      void queryClient.invalidateQueries({ queryKey: ['vendor-store'] });
      // Invalidate bootstrap/user to update in-memory roles/status if any
      const bootstrap = useAuthStore.getState().bootstrap;
      void bootstrap();
      // Do NOT auto-advance to step 3 here — the effect above gates step 3
      // on vendor.status === 'APPROVED' && kycStatus === 'APPROVED'. Submission
      // alone is not approval.
      setTimeout(() => setSuccessMsg(''), 1500);
    },
    onError: (err: any) => {
      setErrorMsg(
        err.response?.data?.message ||
          err.message ||
          'Failed to submit KYC. Please check your inputs.',
      );
    },
  });

  // ── Submit Store Profile (Step 1) ──
  const handleStoreSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    // Validation
    if (!storeName.trim()) return setErrorMsg('Store name is required');
    if (!storeSlug.trim() || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(storeSlug)) {
      return setErrorMsg(
        'Store slug must be alphanumeric lower-case words separated by hyphens (e.g. my-awesome-store)',
      );
    }
    if (
      storeGstin.trim() &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(storeGstin.toUpperCase())
    ) {
      return setErrorMsg('Please enter a valid 15-character GSTIN number');
    }

    registerStoreMutation.mutate({
      storeName: storeName.trim(),
      slug: storeSlug.trim(),
      description: storeDescription.trim() || undefined,
      gstin: storeGstin.trim() ? storeGstin.toUpperCase().trim() : undefined,
    });
  };

  const kycStatus = kycData?.status ?? user?.kycStatus ?? 'NOT_SUBMITTED';
  // Sent back by the reviewer: the seller must see why and be able to fix it.
  // "Request fix" leaves the KYC itself at SUBMITTED, so the store status is
  // what says it; without this the seller sat on "under review" for good.
  const needsResubmit =
    kycStatus === 'REJECTED' ||
    vendorStore?.status === 'CHANGES_REQUESTED' ||
    vendorStore?.status === 'REJECTED';
  const reviewReason = vendorStore?.latestReviewNote ?? kycData?.reviewNote ?? null;

  // A resubmission starts from what was sent, not from an empty form. PAN and
  // bank number come back from the seller's own /me/kyc; documents must be
  // uploaded again only if the reviewer asked for new ones.
  useEffect(() => {
    if (!kycData || kycData.status === 'NOT_SUBMITTED') return;
    setLegalName((v) => v || kycData.legalName || '');
    setPan((v) => v || kycData.pan || '');
    setGstin((v) => v || kycData.gstin || '');
    setBankAccountName((v) => v || kycData.bankAccountName || '');
    setBankAccountNumber((v) => v || kycData.bankAccountNumber || '');
    setBankIfsc((v) => v || kycData.bankIfsc || '');
    setPanDocFileId((v) => v || kycData.panDocFileId || '');
    setAddressProofFileId((v) => v || kycData.addressProofFileId || '');
    setGstDocFileId((v) => v || kycData.gstDocFileId || '');
  }, [kycData]);

  // ── Determine which step to show based on completion status ──
  // Step 3 (completion) is only valid when BOTH the vendor account and KYC are
  // APPROVED. Submitted-but-pending vendors stay on step 2 so they see status,
  // not a premature "you're done" screen.
  useEffect(() => {
    if (!isLoadingVendor && !isLoadingKyc) {
      if (!vendorStore) {
        setCurrentStep(1); // No store profile yet
      } else if (kycStatus === 'NOT_SUBMITTED' || kycStatus === 'REJECTED') {
        setCurrentStep(2); // Store exists but KYC not submitted (or rejected)
      } else if (vendorStore.status === 'APPROVED' && kycStatus === 'APPROVED') {
        setCurrentStep(3); // Fully approved — show completion
      } else {
        setCurrentStep(2); // KYC submitted/under review — stay on step 2 with status panel
      }
    }
  }, [vendorStore, kycStatus, isLoadingVendor, isLoadingKyc]);

  // Dynamic Upload Handler for KYC Documents
  const handleDocUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    docType: 'pan' | 'address' | 'gst',
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMsg('');
    setUploadProgress((prev) => ({ ...prev, [docType]: true }));

    try {
      // 1. Fetch secure signature from backend (server forces `authenticated`
      //    for the `kyc` folder so PAN/Aadhaar scans aren't publicly addressable).
      const sigData = await api.files.getUploadSignature({
        folder: 'kyc',
        type: 'authenticated',
      });

      // 2. Build Cloudinary form data — `type` must match what the server signed,
      //    otherwise Cloudinary rejects the upload.
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sigData.apiKey);
      formData.append('timestamp', sigData.timestamp.toString());
      formData.append('signature', sigData.signature);
      formData.append('folder', sigData.folder);
      if (sigData.type === 'authenticated') {
        formData.append('type', 'authenticated');
      }

      // 3. Post to Cloudinary. `authenticated` assets land at the same upload
      //    endpoint; the `type` form field tells Cloudinary to store them as
      //    private.
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sigData.cloudName}/raw/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const errJson = await res.json();
        throw new Error(errJson.error?.message || 'Cloudinary upload failed');
      }

      const uploadResult = await res.json();

      // 4. Persist an internal FileObject so KYC stores a resolvable fileId
      //    (not a public Cloudinary URL).
      const fileRecord = await api.files.registerSelf({
        name: file.name,
        url: uploadResult.secure_url as string,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        visibility: 'PRIVATE',
      });

      // 5. Update the state with the internal FileObject id
      if (docType === 'pan') setPanDocFileId(fileRecord.id);
      else if (docType === 'address') setAddressProofFileId(fileRecord.id);
      else if (docType === 'gst') setGstDocFileId(fileRecord.id);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to upload document. Please try again.');
    } finally {
      setUploadProgress((prev) => ({ ...prev, [docType]: false }));
      e.target.value = '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    // Strict validation
    if (!legalName.trim()) return setErrorMsg('Legal Name is required');
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan.toUpperCase())) {
      return setErrorMsg('Please enter a valid 10-character PAN Card number (e.g. ABCDE1234F)');
    }
    if (gstin.trim()) {
      // Check digit, PAN inside and state, as the server will — said now,
      // while the form is open, rather than after a round trip.
      const check = checkGstin(gstin, { pan, stateCode: pickupStateCode || null });
      if (!check.valid) return setErrorMsg(check.problems.join(' '));
    }
    if (!bankAccountName.trim()) return setErrorMsg('Bank Account Holder Name is required');
    if (!/^\d{6,20}$/.test(bankAccountNumber)) {
      return setErrorMsg('Please enter a valid bank account number (6 to 20 digits)');
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankIfsc.toUpperCase())) {
      return setErrorMsg('Please enter a valid 11-character Bank IFSC code (e.g. HDFC0000240)');
    }
    if (!panDocFileId) return setErrorMsg('Please upload a scanned copy of your PAN Card');
    if (!addressProofFileId) return setErrorMsg('Please upload a valid Address Proof document');
    // The store cannot be approved without somewhere to collect parcels from.
    if ((pickupQuery.data?.problems.length ?? 1) > 0) {
      return setErrorMsg('Please save your full pickup address (above) before submitting KYC');
    }

    submitKycMutation.mutate({
      legalName: legalName.trim(),
      pan: pan.toUpperCase().trim(),
      gstin: gstin.trim() ? gstin.toUpperCase().trim() : undefined,
      bankAccountName: bankAccountName.trim(),
      bankAccountNumber: bankAccountNumber.trim(),
      bankIfsc: bankIfsc.toUpperCase().trim(),
      panDocFileId,
      addressProofFileId,
      gstDocFileId: gstDocFileId || undefined,
    });
  };

  if (isLoadingVendor || isLoadingKyc) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const steps = [
    { label: 'Create account', done: true },
    { label: 'Store profile', done: !!vendorStore, active: currentStep === 1 },
    {
      label: 'Submit KYC (PAN / GST / Bank)',
      done: kycStatus === 'APPROVED' || kycStatus === 'SUBMITTED' || kycStatus === 'UNDER_REVIEW',
      active: currentStep === 2,
    },
    {
      label: 'Admin approval',
      done: kycStatus === 'APPROVED',
      active: kycStatus === 'SUBMITTED' || kycStatus === 'UNDER_REVIEW',
    },
    { label: 'Start selling', done: kycStatus === 'APPROVED' },
  ];

  return (
    <>
      <style>{`
        @keyframes scan-line {
          0% { top: 0%; opacity: 0.8; }
          50% { top: 100%; opacity: 0.8; }
          100% { top: 0%; opacity: 0.8; }
        }
        .scan-laser {
          position: absolute;
          left: 0;
          width: 100%;
          height: 2.5px;
          background: linear-gradient(90deg, transparent, #f59e0b, transparent);
          box-shadow: 0 0 8px #f59e0b;
          animation: scan-line 2.2s infinite linear;
        }
        @keyframes shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .shine-sweep {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent);
          animation: shine 3s infinite ease-in-out;
        }
        @keyframes halo-expand {
          0% { transform: scale(0.9); opacity: 0.65; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .halo-ring {
          position: absolute;
          border: 1.5px solid currentColor;
          border-radius: 9999px;
          animation: halo-expand 2s infinite cubic-bezier(0.16, 1, 0.3, 1);
        }
        .kyc-form input {
          border-radius: 0.5rem !important;
          border-color: #e2e8f0 !important;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
          font-size: 0.8125rem !important;
          padding: 0.5rem 0.75rem !important;
          background-color: #ffffff !important;
        }
        .kyc-form input:focus {
          border-color: #a8161b !important;
          box-shadow: 0 0 0 3px rgba(168, 22, 27, 0.1) !important;
          outline: none !important;
        }
        .kyc-step-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .kyc-step-card:hover {
          transform: translateY(-1.5px) !important;
          border-color: rgba(168, 22, 27, 0.2) !important;
          box-shadow: 0 4px 12px rgba(168, 22, 27, 0.04) !important;
        }
        .kyc-upload-zone {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .kyc-upload-zone:hover {
          transform: translateY(-1.5px) !important;
          border-color: #a8161b !important;
          background-color: rgba(168, 22, 27, 0.01) !important;
        }
      `}</style>
      <div className="kyc-form space-y-4">
        <PageHeader
          title="Onboarding & kyc compliance"
          description="Submit your legal tax compliance and bank details to unlock payouts and start selling."
        />

        {/* Onboarding Steps Progress Track */}
        <Card className="overflow-hidden rounded-xl border border-slate-100 shadow-sm">
          <CardBody className="p-4">
            <h3 className="mb-3 text-xs font-extrabold uppercase tracking-widest text-slate-500">
              Onboarding milestones
            </h3>
            <div className="grid gap-3 md:grid-cols-5">
              {steps.map((step, idx) => (
                <div
                  key={idx}
                  className="kyc-step-card relative flex flex-col items-center gap-2 overflow-hidden rounded-xl border border-slate-200/60 bg-slate-50/50 p-3 text-center md:items-start md:text-left"
                >
                  <div
                    className={`relative flex h-7 w-7 items-center justify-center rounded-full text-xs font-black ${
                      step.done
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/20'
                        : step.active
                          ? 'border border-[#a8161b]/10 bg-gradient-to-r from-[#a8161b] to-[#850c10] text-amber-300 shadow-md shadow-red-900/20'
                          : 'border border-slate-200 bg-white font-semibold text-slate-400'
                    }`}
                  >
                    {step.active && (
                      <>
                        <span
                          className="halo-ring h-7 w-7 text-[#a8161b]/60"
                          style={{ animationDuration: '1.8s' }}
                        />
                        <span
                          className="halo-ring h-7 w-7 text-[#a8161b]/35"
                          style={{ animationDuration: '1.8s', animationDelay: '0.6s' }}
                        />
                      </>
                    )}
                    {step.done ? (
                      <svg
                        className="z-10 h-3.5 w-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="z-10">{idx + 1}</span>
                    )}
                  </div>
                  <div>
                    <span
                      className={`mb-1 block text-[9px] font-extrabold uppercase leading-none tracking-wider ${step.done ? 'text-emerald-600' : step.active ? 'text-[#a8161b]' : 'text-slate-400'}`}
                    >
                      Step {idx + 1}
                    </span>
                    <span
                      className={`text-xs font-bold ${step.done ? 'text-slate-800' : 'text-slate-500'}`}
                    >
                      {step.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Step Navigation Helper */}
        {currentStep < 3 && (
          <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/50 p-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500">Step {currentStep} of 2</span>
              <span className="text-xs text-slate-400">
                {currentStep === 1 ? 'Store Profile' : 'KYC Documents'}
              </span>
            </div>
            <div className="flex gap-2">
              {currentStep === 2 && vendorStore && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentStep(1)}
                  disabled={registerStoreMutation.isPending || submitKycMutation.isPending}
                >
                  ← Back to Store Profile
                </Button>
              )}
              {currentStep === 1 && vendorStore && (
                <Button variant="outline" size="sm" onClick={() => setCurrentStep(2)}>
                  Skip to KYC →
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            STEP 1: STORE PROFILE SETUP
            ══════════════════════════════════════════════════════════════════ */}
        {currentStep === 1 && (
          <form onSubmit={handleStoreSubmit} className="space-y-4">
            {errorMsg && (
              <div className="flex items-center gap-2.5 rounded-xl border border-red-200/80 bg-red-50 p-4 text-xs font-bold text-red-700">
                <svg
                  className="h-4 w-4 shrink-0 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                {errorMsg}
              </div>
            )}
            {successMsg && (
              <div className="rounded-xl border border-emerald-200/80 bg-emerald-50 p-4 text-xs font-bold text-emerald-700">
                {successMsg}
              </div>
            )}

            <Card className="overflow-hidden rounded-xl border border-slate-100 shadow-sm">
              <CardBody className="space-y-4 p-5">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                  <svg
                    className="h-6 w-6 text-[#a8161b]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <h3 className="text-base font-black text-slate-800">
                    Step 1: Create your store profile
                  </h3>
                </div>

                <p className="text-xs leading-relaxed text-slate-500">
                  Set up your marketplace storefront identity. This information will be visible to
                  customers browsing your products.
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="Store name *"
                    placeholder="e.g. Aarna Organics"
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    required
                    hint="Your brand name as customers will see it"
                  />

                  <Input
                    label="Store URL slug *"
                    placeholder="e.g. aarna-organics"
                    value={storeSlug}
                    onChange={(e) => setStoreSlug(e.target.value.toLowerCase())}
                    required
                    hint="Unique identifier for your store URL"
                  />
                </div>

                <div>
                  <label className="text-ink mb-1.5 block text-[13px] font-semibold">
                    Store description
                  </label>
                  <textarea
                    className="border-border-strong/40 bg-surface text-ink hover:border-border-strong/70 focus:border-gold-400 w-full rounded-xl border px-3.5 py-2.5 text-sm transition-all duration-200 focus:outline-none"
                    rows={3}
                    placeholder="Tell customers about your store, products, and what makes you unique..."
                    value={storeDescription}
                    onChange={(e) => setStoreDescription(e.target.value)}
                  />
                  <span className="mt-1 block text-[11px] text-slate-400">
                    This will appear on your store page
                  </span>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <Input
                    label="GSTIN (optional)"
                    placeholder="e.g. 27ABCDE1234F1Z5"
                    value={storeGstin}
                    onChange={(e) => setStoreGstin(e.target.value.toUpperCase())}
                    maxLength={15}
                    hint="15-character GST number"
                  />
                </div>
              </CardBody>
            </Card>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="transform rounded-xl border-none bg-gradient-to-r from-[#a8161b] to-[#850c10] px-6 py-2.5 font-bold text-white shadow-lg shadow-red-950/10 transition-all duration-300 hover:from-[#ba1a20] hover:to-[#9c1015] hover:shadow-xl active:scale-95"
                loading={registerStoreMutation.isPending}
              >
                Save & Continue to KYC
              </Button>
            </div>
          </form>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            STEP 2: KYC DOCUMENTS SUBMISSION
            ══════════════════════════════════════════════════════════════════ */}
        {currentStep === 2 && (
          <>
            {vendorStore && <PickupAddressCard />}

            {/* KYC Submit/Status Panel */}
            {kycStatus === 'APPROVED' ? (
              <Card className="relative overflow-hidden rounded-xl border border-l-4 border-slate-100 border-l-emerald-500 shadow-lg">
                <div className="shine-sweep pointer-events-none" />
                <CardBody className="relative z-10 flex flex-col items-center gap-5 p-5 md:flex-row">
                  <div className="relative flex shrink-0 items-center justify-center">
                    <span
                      className="halo-ring h-14 w-14 text-emerald-400/60"
                      style={{ animationDuration: '2.5s' }}
                    />
                    <span
                      className="halo-ring h-14 w-14 text-emerald-400/35"
                      style={{ animationDuration: '2.5s', animationDelay: '0.8s' }}
                    />
                    <div className="z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/50 text-emerald-600 shadow-inner">
                      <svg
                        className="h-8 w-8"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-base font-black text-slate-800">
                      Compliance verification approved!
                    </h2>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                      Your business verification status is active. Aadhaar, Gstin, Pan, and Bank
                      details are locked under secure Pii encryption. You are ready to configure
                      warehouses and list products.
                    </p>
                    <div className="mt-3 flex gap-2">
                      <Badge tone="success" className="text-[10px] font-bold">
                        Active seller
                      </Badge>
                      <Badge tone="success" className="text-[10px] font-bold">
                        Verified settlement bank
                      </Badge>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ) : (kycStatus === 'SUBMITTED' || kycStatus === 'UNDER_REVIEW') && !needsResubmit ? (
              <Card className="relative overflow-hidden rounded-xl border border-l-4 border-slate-100 border-l-amber-500 shadow-lg">
                <div className="shine-sweep pointer-events-none" />
                <CardBody className="relative z-10 flex flex-col items-center gap-5 p-5 md:flex-row">
                  <div className="relative flex h-16 w-14 shrink-0 flex-col justify-between overflow-hidden rounded-lg border border-slate-200/80 bg-white p-2 shadow-sm">
                    <div className="scan-laser" />
                    {/* Simulated document rows */}
                    <div className="h-0.5 w-6 rounded bg-slate-200" />
                    <div className="h-0.5 w-8 rounded bg-slate-300" />
                    <div className="h-0.5 w-5 rounded bg-slate-200" />
                    <div className="h-0.5 w-7 rounded bg-slate-200" />
                    <div className="mt-1 flex items-center justify-end">
                      <span className="relative flex h-1.5 w-1.5 shrink-0">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75"></span>
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-500"></span>
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-base font-black text-slate-800">
                      Kyc documents under review
                    </h2>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                      Our compliance team is auditing your tax registration certificates and bank
                      account credentials. Verification normally takes 24 to 48 business hours.
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                      <Badge tone="warning" className="text-[10px] font-bold">
                        Status: {kycStatus.replace(/_/g, ' ')}
                      </Badge>
                      <span className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                        <svg
                          className="h-3.5 w-3.5 animate-spin text-amber-500"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Auditing details...
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {errorMsg && (
                  <div className="flex items-center gap-2.5 rounded-xl border border-red-200/80 bg-red-50 p-4 text-xs font-bold text-red-700">
                    <svg
                      className="h-4 w-4 shrink-0 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    {errorMsg}
                  </div>
                )}
                {successMsg && (
                  <div className="rounded-xl border border-emerald-200/80 bg-emerald-50 p-4 text-xs font-bold text-emerald-700">
                    {successMsg}
                  </div>
                )}

                {needsResubmit && (
                  <div className="rounded-xl border-l-4 border-red-500 bg-red-50/70 p-4">
                    <h4 className="text-xs font-black text-red-800">
                      {vendorStore?.status === 'CHANGES_REQUESTED'
                        ? 'Our team needs a few changes:'
                        : 'Your application was not approved:'}
                    </h4>
                    <p className="mt-1 text-[11px] text-red-700">
                      {reviewReason ?? 'Please review your details and documents.'}
                    </p>
                    <p className="mt-2 text-[10px] font-bold text-slate-400">
                      Please correct the information below and re-submit for audit approval.
                    </p>
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  {/* 1. Legal & Tax Identity */}
                  <Card className="overflow-hidden rounded-xl border border-slate-100 shadow-sm">
                    <CardBody className="space-y-4 p-4">
                      <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                        <svg
                          className="h-5 w-5 text-[#a8161b]"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <h3 className="text-sm font-black text-slate-800">
                          1. Tax & entity identification
                        </h3>
                      </div>

                      <Input
                        label="Legal entity name / Sole proprietor name"
                        placeholder="e.g. John Doe Enterprises"
                        value={legalName}
                        onChange={(e) => setLegalName(e.target.value)}
                        required
                      />

                      <Input
                        label="Pan card number"
                        placeholder="e.g. ABCDE1234F"
                        value={pan}
                        onChange={(e) => setPan(e.target.value.toUpperCase())}
                        maxLength={10}
                        required
                      />

                      <Input
                        label="Gstin number (optional)"
                        placeholder="e.g. 27ABCDE1234F1Z5"
                        value={gstin}
                        onChange={(e) => setGstin(e.target.value.toUpperCase())}
                        maxLength={15}
                      />
                    </CardBody>
                  </Card>

                  {/* 2. Bank Account Details */}
                  <Card className="overflow-hidden rounded-xl border border-slate-100 shadow-sm">
                    <CardBody className="space-y-4 p-4">
                      <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                        <svg
                          className="h-5 w-5 text-[#a8161b]"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                          />
                        </svg>
                        <h3 className="text-sm font-black text-slate-800">
                          2. Bank payout settlement account
                        </h3>
                      </div>

                      <Input
                        label="Account holder name"
                        placeholder="Should match legal name exactly"
                        value={bankAccountName}
                        onChange={(e) => setBankAccountName(e.target.value)}
                        required
                      />

                      <Input
                        label="Bank account number"
                        placeholder="6 to 20 digits account number"
                        value={bankAccountNumber}
                        onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, ''))}
                        required
                      />

                      <Input
                        label="Ifsc code"
                        placeholder="11-character code, e.g. HDFC0000104"
                        value={bankIfsc}
                        onChange={(e) => setBankIfsc(e.target.value.toUpperCase())}
                        maxLength={11}
                        required
                      />
                    </CardBody>
                  </Card>
                </div>

                {/* 3. Scanned Documents Upload */}
                <Card className="overflow-hidden rounded-xl border border-slate-100 shadow-sm">
                  <CardBody className="space-y-3 p-4">
                    <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                      <svg
                        className="h-5 w-5 text-[#a8161b]"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <h3 className="text-sm font-black text-slate-800">
                        3. Compliance document scans
                      </h3>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      {/* PAN Card Scan */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-600">Pan card copy *</label>
                        <div className="kyc-upload-zone group relative flex min-h-[110px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/40 p-4 text-center transition-all hover:border-[#a8161b]">
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) => handleDocUpload(e, 'pan')}
                            disabled={uploadProgress.pan}
                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                          />
                          {uploadProgress.pan ? (
                            <div className="flex flex-col items-center gap-1.5">
                              <Spinner size="sm" />
                              <span className="animate-pulse text-[10px] font-bold text-[#a8161b]">
                                Uploading doc...
                              </span>
                            </div>
                          ) : panDocFileId ? (
                            <div className="pointer-events-none flex flex-col items-center gap-1">
                              <span className="flex items-center gap-1 text-[11px] font-extrabold text-emerald-500">
                                <svg
                                  className="h-3.5 w-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                Loaded scan
                              </span>
                              <span className="max-w-[140px] truncate break-all px-1 text-[9px] text-slate-400">
                                {panDocFileId.split('/').pop()}
                              </span>
                            </div>
                          ) : (
                            <div className="pointer-events-none flex flex-col items-center gap-1">
                              <svg
                                className="h-5 w-5 text-slate-400 transition-transform duration-300 group-hover:scale-110"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                              <span className="text-[11px] font-bold text-slate-600">
                                Upload pan card
                              </span>
                              <span className="text-[9px] text-slate-400">PDF, JPG, PNG</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Address Proof Scan */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-600">
                          Address proof * (Aadhaar / Utility bill)
                        </label>
                        <div className="kyc-upload-zone group relative flex min-h-[110px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/40 p-4 text-center transition-all hover:border-[#a8161b]">
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) => handleDocUpload(e, 'address')}
                            disabled={uploadProgress.address}
                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                          />
                          {uploadProgress.address ? (
                            <div className="flex flex-col items-center gap-1.5">
                              <Spinner size="sm" />
                              <span className="animate-pulse text-[10px] font-bold text-[#a8161b]">
                                Uploading doc...
                              </span>
                            </div>
                          ) : addressProofFileId ? (
                            <div className="pointer-events-none flex flex-col items-center gap-1">
                              <span className="flex items-center gap-1 text-[11px] font-extrabold text-emerald-500">
                                <svg
                                  className="h-3.5 w-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                Loaded scan
                              </span>
                              <span className="max-w-[140px] truncate break-all px-1 text-[9px] text-slate-400">
                                {addressProofFileId.split('/').pop()}
                              </span>
                            </div>
                          ) : (
                            <div className="pointer-events-none flex flex-col items-center gap-1">
                              <svg
                                className="h-5 w-5 text-slate-400 transition-transform duration-300 group-hover:scale-110"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                              <span className="text-[11px] font-bold text-slate-600">
                                Upload address proof
                              </span>
                              <span className="text-[9px] text-slate-400">PDF, JPG, PNG</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* GST Registration Certificate Scan */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-600">
                          Gst certificate (optional)
                        </label>
                        <div className="kyc-upload-zone group relative flex min-h-[110px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/40 p-4 text-center transition-all hover:border-[#a8161b]">
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) => handleDocUpload(e, 'gst')}
                            disabled={uploadProgress.gst}
                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                          />
                          {uploadProgress.gst ? (
                            <div className="flex flex-col items-center gap-1.5">
                              <Spinner size="sm" />
                              <span className="animate-pulse text-[10px] font-bold text-[#a8161b]">
                                Uploading doc...
                              </span>
                            </div>
                          ) : gstDocFileId ? (
                            <div className="pointer-events-none flex flex-col items-center gap-1">
                              <span className="flex items-center gap-1 text-[11px] font-extrabold text-emerald-500">
                                <svg
                                  className="h-3.5 w-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                Loaded scan
                              </span>
                              <span className="max-w-[140px] truncate break-all px-1 text-[9px] text-slate-400">
                                {gstDocFileId.split('/').pop()}
                              </span>
                            </div>
                          ) : (
                            <div className="pointer-events-none flex flex-col items-center gap-1">
                              <svg
                                className="h-5 w-5 text-slate-400 transition-transform duration-300 group-hover:scale-110"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                              <span className="text-[11px] font-bold text-slate-600">
                                Upload gst docs
                              </span>
                              <span className="text-[9px] text-slate-400">PDF, JPG, PNG</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {/* Submit Action */}
                <div className="flex justify-end pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    className="transform rounded-xl border-none bg-gradient-to-r from-[#a8161b] to-[#850c10] px-6 py-2.5 font-bold text-white shadow-lg shadow-red-950/10 transition-all duration-300 hover:from-[#ba1a20] hover:to-[#9c1015] hover:shadow-xl active:scale-95"
                    loading={submitKycMutation.isPending}
                    disabled={Object.values(uploadProgress).some(Boolean)}
                  >
                    Submit verification documents
                  </Button>
                </div>
              </form>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            STEP 3: ONBOARDING COMPLETE / AWAITING APPROVAL
            ══════════════════════════════════════════════════════════════════ */}
        {currentStep === 3 && (
          <Card className="relative overflow-hidden rounded-xl border border-slate-100 shadow-lg">
            <div className="shine-sweep pointer-events-none" />
            <CardBody className="relative z-10 p-6 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-600">
                <svg
                  className="h-10 w-10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <h2 className="text-xl font-black text-slate-800">
                {kycStatus === 'APPROVED'
                  ? 'Onboarding Complete!'
                  : 'Documents Submitted Successfully!'}
              </h2>

              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-600">
                {kycStatus === 'APPROVED'
                  ? 'Your store is now live! You can start adding products and managing your inventory.'
                  : "Our compliance team is reviewing your documents. This usually takes 24-48 hours. We'll notify you once approved."}
              </p>

              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {kycStatus === 'APPROVED' ? (
                  <>
                    <Button
                      variant="primary"
                      onClick={() => navigate('/products/new')}
                      className="bg-gradient-to-r from-[#a8161b] to-[#850c10]"
                    >
                      Add Your First Product
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/')}>
                      Go to Dashboard
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => navigate('/')}>
                      Go to Dashboard
                    </Button>
                    <Button variant="outline" onClick={() => setCurrentStep(2)}>
                      Review Submission
                    </Button>
                  </>
                )}
              </div>

              {kycStatus !== 'APPROVED' && (
                <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start gap-3">
                    <svg
                      className="h-5 w-5 shrink-0 text-amber-600"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex-1 text-left">
                      <h4 className="text-xs font-bold text-amber-800">What happens next?</h4>
                      <ul className="mt-2 space-y-1 text-xs text-amber-700">
                        <li>• Our team will verify your PAN, GST, and bank details</li>
                        <li>• You&apos;ll receive an email notification once approved</li>
                        <li>• After approval, you can start listing products immediately</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════
// SUBSCRIPTION  (Phase 2.7 — live SaaS plans + entitlement)
// ══════════════════════════════════════════════════════════════════

export function SubscriptionPage() {
  const qc = useQueryClient();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'ANNUAL'>('MONTHLY');

  const plans = useQuery({ queryKey: ['saas-plans'], queryFn: () => api.saas.listPlans() });
  const entitlement = useQuery({
    queryKey: ['saas-entitlement'],
    queryFn: () => api.saas.myEntitlement(),
  });
  const subscription = useQuery({
    queryKey: ['saas-subscription'],
    queryFn: () => api.saas.mySubscription(),
  });
  const invoices = useQuery({
    queryKey: ['saas-invoices'],
    queryFn: () => api.saas.myInvoices(),
  });

  const changePlan = useMutation({
    mutationFn: (data: { planCode: SaasPlanCode; billingCycle: SaasBillingCycle }) =>
      api.saas.changeMyPlan(data),
    onSuccess: () => {
      setShowPaymentModal(false);
      setSelectedPlan(null);
      void qc.invalidateQueries({ queryKey: ['saas-entitlement'] });
      void qc.invalidateQueries({ queryKey: ['saas-subscription'] });
    },
  });

  const handlePlanSelect = (plan: any) => {
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const currentCode = entitlement.data?.planCode;
  // listPlans() already returns the array; reading .data off it showed no plans.
  const plansList: any[] = plans.data ?? [];
  const invoicesList = invoices.data ?? [];

  // Calculate savings for annual billing
  const calculateSavings = (monthlyPrice: number, annualPrice: number) => {
    const monthlyCost = monthlyPrice * 12;
    const savings = monthlyCost - annualPrice;
    const savingsPercent = (savings / monthlyCost) * 100;
    return { savings, savingsPercent };
  };

  return (
    <div className="space-y-6">
      <style>{`
        .sub-plan-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          border: 1px solid rgba(226, 232, 240, 0.8) !important;
        }
        .sub-plan-card:hover {
          transform: translateY(-4px) !important;
          border-color: rgba(168, 22, 27, 0.2) !important;
          box-shadow: 0 12px 30px -10px rgba(168, 22, 27, 0.1) !important;
        }
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-center;
          z-index: 50;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <PageHeader
          title="Subscription"
          description="Your current plan, entitlements and available upgrades."
        />

        {/* Billing Cycle Toggle */}
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-1">
          <button
            onClick={() => setBillingCycle('MONTHLY')}
            className={`rounded-md px-4 py-2 text-sm font-bold transition-all ${
              billingCycle === 'MONTHLY'
                ? 'bg-[#a8161b] text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('ANNUAL')}
            className={`rounded-md px-4 py-2 text-sm font-bold transition-all ${
              billingCycle === 'ANNUAL'
                ? 'bg-[#a8161b] text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            Annual
            <span className="ml-1 text-xs text-emerald-500">Save 20%</span>
          </button>
        </div>
      </div>

      {/* Current Subscription Status */}
      {entitlement.data && (
        <Card className="overflow-hidden rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50 shadow-sm">
          <CardBody className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Current Plan
                </p>
                <p className="mt-1 text-3xl font-black text-slate-900">
                  {entitlement.data.planCode}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge
                  tone={entitlement.data.active ? 'success' : 'danger'}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider"
                >
                  {entitlement.data.status}
                </Badge>
                {subscription.data?.autoRenew && (
                  <Badge tone="info" className="px-3 py-1 text-xs font-bold">
                    Auto-renew ON
                  </Badge>
                )}
              </div>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-slate-100 bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Product Limit
                </p>
                <p className="mt-2 text-2xl font-black text-slate-900">
                  {entitlement.data.productCap === -1 ? '∞' : entitlement.data.productCap}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {entitlement.data.productCap === -1 ? 'Unlimited products' : 'products allowed'}
                </p>
              </div>

              <div className="rounded-lg border border-slate-100 bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Media Per Product
                </p>
                <p className="mt-2 text-2xl font-black text-slate-900">
                  {entitlement.data.mediaPerProductCap === -1
                    ? '∞'
                    : entitlement.data.mediaPerProductCap}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {entitlement.data.mediaPerProductCap === -1
                    ? 'Unlimited images'
                    : 'images per product'}
                </p>
              </div>

              <div className="rounded-lg border border-slate-100 bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Sponsored Fee
                </p>
                <p className="mt-2 text-2xl font-black text-emerald-600">
                  {(entitlement.data.sponsoredFeeBps / 100).toFixed(2)}%
                </p>
                <p className="mt-1 text-xs text-slate-500">per sponsored click</p>
              </div>

              <div className="rounded-lg border border-slate-100 bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Renewal Date
                </p>
                <p className="mt-2 text-lg font-black text-slate-900">
                  {new Date(entitlement.data.currentPeriodEnd).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                  })}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {new Date(entitlement.data.currentPeriodEnd).getFullYear()}
                </p>
              </div>
            </div>

            {/* Feature Badges */}
            {entitlement.data.features && Object.keys(entitlement.data.features).length > 0 && (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">
                  Active Features
                </p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(entitlement.data.features)
                    .filter(([, enabled]) => enabled)
                    .map(([feature]) => (
                      <span
                        key={feature}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700"
                      >
                        <span className="text-emerald-500">✓</span>
                        {feature.replace(/_/g, ' ')}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Available Plans */}
      <div>
        <h3 className="mb-4 text-lg font-black text-slate-900">Available Plans</h3>
        {plans.isLoading ? (
          <div className="grid min-h-[150px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {plansList
              .filter((p: any) => p.active)
              .map((plan: any) => {
                const isCurrent = plan.code === currentCode;
                const price =
                  billingCycle === 'MONTHLY' ? plan.monthlyPricePaise : plan.annualPricePaise;
                const savings =
                  billingCycle === 'ANNUAL'
                    ? calculateSavings(plan.monthlyPricePaise, plan.annualPricePaise)
                    : null;

                return (
                  <Card
                    key={plan.id}
                    className={`sub-plan-card flex flex-col justify-between overflow-hidden rounded-xl bg-white shadow-sm ${
                      isCurrent ? 'border-2 border-[#a8161b] ring-4 ring-[#a8161b]/5' : ''
                    } ${plan.code === 'PRO' ? 'border-2 border-amber-400 ring-4 ring-amber-400/10' : ''}`}
                  >
                    <CardBody className="flex h-full flex-col justify-between gap-5 p-6">
                      <div className="space-y-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-lg font-black text-slate-900">{plan.name}</h3>
                            {plan.code === 'PRO' && (
                              <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">
                                Most Popular
                              </span>
                            )}
                          </div>
                          {isCurrent && (
                            <Badge
                              tone="success"
                              className="text-xs font-bold uppercase tracking-wider"
                            >
                              Current
                            </Badge>
                          )}
                        </div>

                        <div>
                          <p className="text-3xl font-black text-slate-900">{formatPaise(price)}</p>
                          <p className="text-sm font-bold text-slate-500">
                            per {billingCycle === 'MONTHLY' ? 'month' : 'year'}
                          </p>
                          {savings && (
                            <p className="mt-1 text-xs font-bold text-emerald-600">
                              Save {formatPaise(savings.savings)} (
                              {savings.savingsPercent.toFixed(0)}%)
                            </p>
                          )}
                        </div>

                        <div className="space-y-3 border-t border-slate-100 pt-4">
                          <div className="flex items-center gap-2">
                            <span className="text-emerald-500">✓</span>
                            <span className="text-sm font-bold text-slate-700">
                              {plan.productCap === -1 ? 'Unlimited' : plan.productCap} Products
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-emerald-500">✓</span>
                            <span className="text-sm font-bold text-slate-700">
                              {plan.mediaPerProductCap === -1
                                ? 'Unlimited'
                                : plan.mediaPerProductCap}{' '}
                              Images/Product
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-emerald-500">✓</span>
                            <span className="text-sm font-bold text-slate-700">
                              {(plan.sponsoredFeeBps / 100).toFixed(2)}% Sponsored Fee
                            </span>
                          </div>
                          {Object.entries(plan.features)
                            .filter(([, v]) => v)
                            .map(([k]) => (
                              <div key={k} className="flex items-center gap-2">
                                <span className="text-emerald-500">✓</span>
                                <span className="text-sm text-slate-600">
                                  {k.replace(/_/g, ' ')}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>

                      <div className="pt-2">
                        {isCurrent ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full cursor-not-allowed rounded-lg py-2.5 text-sm font-bold"
                            disabled
                          >
                            Current Plan
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className={`w-full rounded-lg py-2.5 text-sm font-bold text-white shadow-sm ${
                              plan.code === 'PRO'
                                ? 'border-amber-500 bg-amber-500 hover:bg-amber-600'
                                : 'border-[#a8161b] bg-[#a8161b] hover:bg-[#850c10]'
                            }`}
                            onClick={() => handlePlanSelect(plan)}
                          >
                            Upgrade to {plan.name}
                          </Button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
          </div>
        )}
      </div>

      {/* Billing History */}
      {invoicesList.length > 0 && (
        <Card className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <CardBody className="p-6">
            <h3 className="mb-4 text-lg font-black text-slate-900">Billing History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <th className="pb-3">Invoice</th>
                    <th className="pb-3">Period</th>
                    <th className="pb-3">Amount</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {invoicesList.map((invoice: any) => (
                    <tr key={invoice.id} className="border-b border-slate-100 last:border-0">
                      <td className="py-3 font-mono text-xs font-bold text-slate-700">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="py-3 text-xs text-slate-600">
                        {new Date(invoice.periodStart).toLocaleDateString('en-IN', {
                          month: 'short',
                          day: 'numeric',
                        })}{' '}
                        -{' '}
                        {new Date(invoice.periodEnd).toLocaleDateString('en-IN', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td className="py-3 font-bold text-slate-900">
                        {formatPaise(invoice.totalPaise)}
                      </td>
                      <td className="py-3">
                        <Badge
                          tone={
                            invoice.status === 'PAID'
                              ? 'success'
                              : invoice.status === 'FAILED'
                                ? 'danger'
                                : 'warning'
                          }
                          className="text-xs font-bold"
                        >
                          {invoice.status}
                        </Badge>
                      </td>
                      <td className="py-3 text-xs text-slate-600">
                        {invoice.paidAt
                          ? new Date(invoice.paidAt).toLocaleDateString('en-IN')
                          : new Date(invoice.createdAt).toLocaleDateString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedPlan && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <Card
            className="m-4 w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <CardBody className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-black text-slate-900">Confirm Upgrade</h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-bold text-slate-600">Upgrading to:</p>
                  <p className="mt-1 text-2xl font-black text-slate-900">{selectedPlan.name}</p>
                  <p className="mt-2 text-lg font-bold text-emerald-600">
                    {formatPaise(
                      billingCycle === 'MONTHLY'
                        ? selectedPlan.monthlyPricePaise
                        : selectedPlan.annualPricePaise,
                    )}
                    <span className="text-sm text-slate-500">
                      /{billingCycle === 'MONTHLY' ? 'month' : 'year'}
                    </span>
                  </p>
                </div>

                <div className="space-y-2 text-sm text-slate-600">
                  <p>✓ Immediate access to all features</p>
                  <p>✓ Prorated billing for current period</p>
                  <p>
                    ✓ Auto-renews on{' '}
                    {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-IN')}
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-lg py-2 text-sm font-bold"
                    onClick={() => setShowPaymentModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 rounded-lg bg-[#a8161b] py-2 text-sm font-bold text-white hover:bg-[#850c10]"
                    loading={changePlan.isPending}
                    onClick={() => changePlan.mutate({ planCode: selectedPlan.code, billingCycle })}
                  >
                    Confirm & Pay
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// SPONSORED LISTINGS  (Phase 2.7 — vendor ad campaigns)
// ══════════════════════════════════════════════════════════════════

const SPONSORED_STATUS_TONE: Record<string, BadgeTone> = {
  PAUSED: 'neutral',
  ACTIVE: 'success',
  COMPLETED: 'neutral',
  CANCELLED: 'danger',
};

export function SponsoredPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'active' | 'performance' | 'create'>('active');

  const listings = useQuery({
    queryKey: ['vendor-sponsored'],
    queryFn: () => api.saas.mySponsored(),
  });
  const products = useQuery({
    queryKey: ['vendor-products-all'],
    queryFn: () => api.products.myProducts({ pageSize: 100 }),
  });
  const entitlement = useQuery({
    queryKey: ['saas-entitlement'],
    queryFn: () => api.saas.myEntitlement(),
  });

  const [form, setForm] = useState({
    productId: '',
    bidRupees: '',
    budgetRupees: '',
    durationDays: '30',
  });

  const create = useMutation({
    mutationFn: () =>
      api.saas.createSponsored({
        productId: form.productId,
        bidPerClickPaise: Math.round(Number(form.bidRupees) * 100),
        totalBudgetPaise: Math.round(Number(form.budgetRupees) * 100),
      }),
    onSuccess: () => {
      setForm({ productId: '', bidRupees: '', budgetRupees: '', durationDays: '30' });
      setActiveTab('active');
      void qc.invalidateQueries({ queryKey: ['vendor-sponsored'] });
    },
  });
  const pause = useMutation({
    mutationFn: (id: string) => api.saas.pauseSponsored(id, 'Paused by vendor'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vendor-sponsored'] }),
  });
  const resume = useMutation({
    mutationFn: (id: string) => api.saas.resumeSponsored(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vendor-sponsored'] }),
  });

  // mySponsored() already returns the array; reading .data off it showed none.
  const listingsList: any[] = listings.data ?? [];
  const productsList = (products.data as any)?.data ?? [];

  // Calculate performance metrics
  const totalSpent = listingsList.reduce((sum: number, l: any) => sum + l.spentPaise, 0);
  const totalImpressions = listingsList.reduce((sum: number, l: any) => sum + l.impressions, 0);
  const totalClicks = listingsList.reduce((sum: number, l: any) => sum + l.clicks, 0);
  const avgCTR = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
  const avgCPC = totalClicks > 0 ? totalSpent / totalClicks : 0;
  const activeListings = listingsList.filter((l: any) => l.status === 'ACTIVE').length;

  // Get sponsored fee from entitlement
  const sponsoredFee = entitlement.data?.sponsoredFeeBps || 0;
  const sponsoredFeePercent = (sponsoredFee / 100).toFixed(2);

  // Calculate estimated metrics for create form
  const estimatedClicks =
    form.budgetRupees && form.bidRupees
      ? Math.floor(Number(form.budgetRupees) / Number(form.bidRupees))
      : 0;
  const estimatedImpressions = estimatedClicks > 0 ? Math.floor(estimatedClicks / 0.02) : 0; // Assuming 2% CTR
  const dailyBudget =
    form.budgetRupees && form.durationDays
      ? (Number(form.budgetRupees) / Number(form.durationDays)).toFixed(2)
      : '0';

  return (
    <div className="space-y-6">
      <style>{`
        .sponsored-card:hover {
          box-shadow: 0 10px 25px -12px rgba(168, 22, 27, 0.08) !important;
          border-color: rgba(168, 22, 27, 0.15) !important;
        }
        .sponsored-input:focus {
          border-color: #a8161b !important;
          box-shadow: 0 0 0 3px rgba(168, 22, 27, 0.1) !important;
          outline: none !important;
        }
        .sponsored-table-row {
          transition: background-color 0.2s ease !important;
        }
        .sponsored-table-row:hover {
          background-color: rgba(248, 250, 252, 0.8) !important;
        }
        .sponsored-tab {
          transition: all 0.2s ease !important;
        }
        .sponsored-tab.active {
          border-bottom: 2px solid #a8161b !important;
          color: #a8161b !important;
        }
      `}</style>

      <PageHeader
        title="Sponsored Listings"
        description="Promote your products with pay-per-click ad campaigns."
      />

      {/* Sponsored Fee Info Banner */}
      {sponsoredFee > 0 && (
        <Card className="overflow-hidden rounded-xl border border-blue-200 bg-blue-50 shadow-sm">
          <CardBody className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                <span className="text-lg">ℹ️</span>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-blue-900">Sponsored Listing Fee</h4>
                <p className="mt-1 text-xs text-blue-700">
                  Your current plan includes a{' '}
                  <span className="font-bold">{sponsoredFeePercent}%</span> fee on sponsored ad
                  spend. Upgrade your plan to reduce this fee and maximize your ROI.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Performance Overview Cards */}
      {listingsList.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Card className="sponsored-card overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <CardBody className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Total Spent
                  </p>
                  <p className="mt-2 text-2xl font-black text-slate-900">
                    {formatPaise(totalSpent)}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                  <span className="text-2xl">💰</span>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card className="sponsored-card overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <CardBody className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Impressions
                  </p>
                  <p className="mt-2 text-2xl font-black text-slate-900">
                    {totalImpressions.toLocaleString()}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
                  <span className="text-2xl">👁️</span>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card className="sponsored-card overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <CardBody className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Clicks
                  </p>
                  <p className="mt-2 text-2xl font-black text-[#a8161b]">
                    {totalClicks.toLocaleString()}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
                  <span className="text-2xl">🖱️</span>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card className="sponsored-card overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <CardBody className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Avg CTR
                  </p>
                  <p className="mt-2 text-2xl font-black text-slate-900">{avgCTR.toFixed(2)}%</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-50">
                  <span className="text-2xl">📊</span>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card className="sponsored-card overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <CardBody className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Avg CPC
                  </p>
                  <p className="mt-2 text-2xl font-black text-slate-900">{formatPaise(avgCPC)}</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
                  <span className="text-2xl">💵</span>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Tab Navigation */}
      <Card className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <CardBody className="p-0">
          <div className="flex border-b border-slate-200">
            <button
              className={`sponsored-tab flex-1 px-6 py-4 text-sm font-bold transition-all ${
                activeTab === 'active'
                  ? 'active text-[#a8161b]'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => setActiveTab('active')}
            >
              Active Campaigns ({activeListings})
            </button>
            <button
              className={`sponsored-tab flex-1 px-6 py-4 text-sm font-bold transition-all ${
                activeTab === 'performance'
                  ? 'active text-[#a8161b]'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => setActiveTab('performance')}
            >
              Performance
            </button>
            <button
              className={`sponsored-tab flex-1 px-6 py-4 text-sm font-bold transition-all ${
                activeTab === 'create'
                  ? 'active text-[#a8161b]'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => setActiveTab('create')}
            >
              Create Campaign
            </button>
          </div>
        </CardBody>
      </Card>

      {/* Active Campaigns Tab */}
      {activeTab === 'active' && (
        <>
          {listings.isLoading ? (
            <div className="grid min-h-[150px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : listingsList.length === 0 ? (
            <Card className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <CardBody className="p-8">
                <EmptyState
                  title="No campaigns yet"
                  description="Create your first sponsored campaign to start promoting your products."
                />
              </CardBody>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {listingsList.map((l: any) => {
                const product = productsList.find((p: any) => p.id === l.productId);
                const ctr = l.impressions > 0 ? (l.clicks / l.impressions) * 100 : 0;
                const cpc = l.clicks > 0 ? l.spentPaise / l.clicks : 0;
                const budgetUsed =
                  l.totalBudgetPaise > 0 ? (l.spentPaise / l.totalBudgetPaise) * 100 : 0;

                return (
                  <Card
                    key={l.id}
                    className="sponsored-card overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                  >
                    <CardBody className="p-5">
                      <div className="mb-4 flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-sm font-bold text-slate-900">
                            {product?.title || 'Unknown Product'}
                          </h4>
                          <p className="mt-1 text-xs text-slate-500">
                            Campaign ID: {l.id.slice(0, 8)}
                          </p>
                        </div>
                        <Badge
                          tone={SPONSORED_STATUS_TONE[l.status] ?? 'neutral'}
                          className="text-xs font-bold"
                        >
                          {l.status}
                        </Badge>
                      </div>

                      <div className="space-y-3 border-t border-slate-100 pt-4">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold text-slate-600">Budget Used</span>
                          <span className="font-black text-slate-900">
                            {budgetUsed.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full bg-[#a8161b]"
                            style={{ width: `${Math.min(budgetUsed, 100)}%` }}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-2">
                          <div>
                            <p className="text-xs font-bold text-slate-500">Spent</p>
                            <p className="mt-1 text-sm font-black text-slate-900">
                              {formatPaise(l.spentPaise)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-500">Budget</p>
                            <p className="mt-1 text-sm font-bold text-slate-600">
                              {formatPaise(l.totalBudgetPaise)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-500">Impressions</p>
                            <p className="mt-1 text-sm font-bold text-slate-700">
                              {l.impressions.toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-500">Clicks</p>
                            <p className="mt-1 text-sm font-black text-[#a8161b]">
                              {l.clicks.toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-500">CTR</p>
                            <p className="mt-1 text-sm font-bold text-slate-700">
                              {ctr.toFixed(2)}%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-500">CPC</p>
                            <p className="mt-1 text-sm font-bold text-slate-700">
                              {formatPaise(cpc)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 flex gap-2 border-t border-slate-100 pt-4">
                        {l.status === 'ACTIVE' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 rounded-lg px-3 py-2 text-xs font-bold"
                            loading={pause.isPending}
                            onClick={() => pause.mutate(l.id)}
                          >
                            Pause
                          </Button>
                        )}
                        {l.status === 'PAUSED' && (
                          <Button
                            size="sm"
                            className="flex-1 rounded-lg border-[#a8161b] bg-[#a8161b] px-3 py-2 text-xs font-bold text-white hover:bg-[#850c10]"
                            loading={resume.isPending}
                            onClick={() => resume.mutate(l.id)}
                          >
                            Resume
                          </Button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <Card className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <CardBody className="p-6">
            <h3 className="mb-6 text-lg font-black text-slate-900">
              Campaign Performance Comparison
            </h3>

            {listingsList.length === 0 ? (
              <EmptyState
                title="No performance data"
                description="Create campaigns to see performance metrics."
              />
            ) : (
              <div className="space-y-6">
                {/* Performance Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                        <th className="pb-3">Campaign</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3">Budget</th>
                        <th className="pb-3">Spent</th>
                        <th className="pb-3">Impressions</th>
                        <th className="pb-3">Clicks</th>
                        <th className="pb-3">CTR</th>
                        <th className="pb-3">CPC</th>
                        <th className="pb-3">ROI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listingsList.map((l: any) => {
                        const product = productsList.find((p: any) => p.id === l.productId);
                        const ctr = l.impressions > 0 ? (l.clicks / l.impressions) * 100 : 0;
                        const cpc = l.clicks > 0 ? l.spentPaise / l.clicks : 0;
                        // Simplified ROI calculation (would need actual conversion data)
                        const estimatedRevenue = l.clicks * 50000; // Assume ₹500 avg order value
                        const roi =
                          l.spentPaise > 0
                            ? ((estimatedRevenue - l.spentPaise) / l.spentPaise) * 100
                            : 0;

                        return (
                          <tr key={l.id} className="border-b border-slate-100 last:border-0">
                            <td className="py-3 text-xs font-bold text-slate-700">
                              {product?.title?.slice(0, 30) || 'Unknown'}...
                            </td>
                            <td className="py-3">
                              <Badge
                                tone={SPONSORED_STATUS_TONE[l.status] ?? 'neutral'}
                                className="text-xs font-bold"
                              >
                                {l.status}
                              </Badge>
                            </td>
                            <td className="py-3 text-xs font-bold text-slate-600">
                              {formatPaise(l.totalBudgetPaise)}
                            </td>
                            <td className="py-3 text-xs font-black text-slate-900">
                              {formatPaise(l.spentPaise)}
                            </td>
                            <td className="py-3 font-mono text-xs font-semibold text-slate-600">
                              {l.impressions.toLocaleString()}
                            </td>
                            <td className="py-3 font-mono text-xs font-black text-[#a8161b]">
                              {l.clicks.toLocaleString()}
                            </td>
                            <td className="py-3 text-xs font-bold text-slate-700">
                              {ctr.toFixed(2)}%
                            </td>
                            <td className="py-3 text-xs font-bold text-slate-700">
                              {formatPaise(cpc)}
                            </td>
                            <td className="py-3">
                              <span
                                className={`text-xs font-bold ${roi > 0 ? 'text-emerald-600' : 'text-red-600'}`}
                              >
                                {roi > 0 ? '+' : ''}
                                {roi.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Performance Insights */}
                <div className="grid gap-4 sm:grid-cols-3">
                  <Card className="overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50 shadow-sm">
                    <CardBody className="p-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                        Best CTR
                      </h4>
                      <p className="mt-2 text-2xl font-black text-emerald-900">
                        {Math.max(
                          ...listingsList.map((l: any) =>
                            l.impressions > 0 ? (l.clicks / l.impressions) * 100 : 0,
                          ),
                        ).toFixed(2)}
                        %
                      </p>
                    </CardBody>
                  </Card>

                  <Card className="overflow-hidden rounded-xl border border-blue-200 bg-blue-50 shadow-sm">
                    <CardBody className="p-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-blue-700">
                        Lowest CPC
                      </h4>
                      <p className="mt-2 text-2xl font-black text-blue-900">
                        {formatPaise(
                          Math.min(
                            ...listingsList
                              .map((l: any) => (l.clicks > 0 ? l.spentPaise / l.clicks : Infinity))
                              .filter((v: number) => v !== Infinity),
                          ),
                        )}
                      </p>
                    </CardBody>
                  </Card>

                  <Card className="overflow-hidden rounded-xl border border-purple-200 bg-purple-50 shadow-sm">
                    <CardBody className="p-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-purple-700">
                        Total Reach
                      </h4>
                      <p className="mt-2 text-2xl font-black text-purple-900">
                        {totalImpressions.toLocaleString()}
                      </p>
                    </CardBody>
                  </Card>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Create Campaign Tab */}
      {activeTab === 'create' && (
        <Card className="sponsored-card overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <CardBody className="p-6">
            <h3 className="mb-6 text-lg font-black text-slate-900">Create New Campaign</h3>

            <form
              className="space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                if (form.productId && Number(form.bidRupees) > 0 && Number(form.budgetRupees) > 0)
                  create.mutate();
              }}
            >
              <div className="grid gap-6 sm:grid-cols-2">
                {/* Product Selection */}
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-bold text-slate-700">
                    Select Product <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="sponsored-input w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition-all duration-200"
                    value={form.productId}
                    onChange={(e) => setForm((f) => ({ ...f, productId: e.target.value }))}
                    required
                  >
                    <option value="">Choose a product to promote...</option>
                    {productsList.map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500">
                    Select which product you want to promote with this campaign
                  </p>
                </div>

                {/* Bid Per Click */}
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">
                    Bid Per Click (₹) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    className="sponsored-input rounded-lg border border-slate-200 font-bold"
                    placeholder="e.g., 5.00"
                    value={form.bidRupees}
                    onChange={(e) => setForm((f) => ({ ...f, bidRupees: e.target.value }))}
                    required
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Amount you&apos;ll pay for each click on your ad
                  </p>
                </div>

                {/* Total Budget */}
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">
                    Total Budget (₹) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    className="sponsored-input rounded-lg border border-slate-200 font-bold"
                    placeholder="e.g., 1000.00"
                    value={form.budgetRupees}
                    onChange={(e) => setForm((f) => ({ ...f, budgetRupees: e.target.value }))}
                    required
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Maximum amount to spend on this campaign
                  </p>
                </div>

                {/* Campaign Duration */}
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">
                    Campaign Duration (Days)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    className="sponsored-input rounded-lg border border-slate-200 font-bold"
                    placeholder="30"
                    value={form.durationDays}
                    onChange={(e) => setForm((f) => ({ ...f, durationDays: e.target.value }))}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Estimated campaign duration (for planning only)
                  </p>
                </div>

                {/* Daily Budget Display */}
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">
                    Daily Budget (Estimated)
                  </label>
                  <div className="flex h-[42px] items-center rounded-lg border border-slate-200 bg-slate-50 px-4">
                    <span className="text-sm font-black text-slate-700">₹{dailyBudget}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Estimated daily spend based on total budget
                  </p>
                </div>
              </div>

              {/* Campaign Estimate Calculator */}
              {form.bidRupees && form.budgetRupees && (
                <Card className="overflow-hidden rounded-xl border border-blue-200 bg-blue-50 shadow-sm">
                  <CardBody className="p-5">
                    <h4 className="mb-4 flex items-center gap-2 text-sm font-bold text-blue-900">
                      <span>📊</span>
                      Campaign Estimates
                    </h4>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div>
                        <p className="text-xs font-bold text-blue-700">Estimated Clicks</p>
                        <p className="mt-1 text-2xl font-black text-blue-900">
                          {estimatedClicks.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-blue-700">Estimated Impressions</p>
                        <p className="mt-1 text-2xl font-black text-blue-900">
                          {estimatedImpressions.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-blue-700">Estimated CTR</p>
                        <p className="mt-1 text-2xl font-black text-blue-900">2.0%</p>
                      </div>
                    </div>
                    <p className="mt-4 text-xs text-blue-700">
                      * These are estimates based on average performance. Actual results may vary.
                    </p>
                  </CardBody>
                </Card>
              )}

              {/* Submit Button */}
              <div className="flex justify-end gap-3 border-t border-slate-200 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-lg px-6 py-2.5 text-sm font-bold"
                  onClick={() =>
                    setForm({ productId: '', bidRupees: '', budgetRupees: '', durationDays: '30' })
                  }
                >
                  Reset Form
                </Button>
                <Button
                  type="submit"
                  className="rounded-lg border-[#a8161b] bg-[#a8161b] px-6 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-[#850c10]"
                  loading={create.isPending}
                >
                  Launch Campaign
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ANALYTICS  (Phase 2.8 + 3.1 — vendor metrics + 360 health)
// ══════════════════════════════════════════════════════════════════

const HEALTH_TONE: Record<string, BadgeTone> = {
  A: 'success',
  B: 'success',
  C: 'warning',
  D: 'danger',
  F: 'danger',
};
const pct = (bps: number) => `${(bps / 100).toFixed(2)}%`;

/**
 * Read a list endpoint page by page, up to a bound.
 *
 * The API caps `pageSize` at 100. A request for more is not trimmed to the
 * cap — it fails validation outright and comes back with nothing, which a
 * chart renders as "no data" rather than as an error. Walking the pages is
 * what actually gets the rows.
 *
 * Bounded at `maxRows` because these feed client-side charts: a seller with
 * ten thousand orders should not pull all of them into the browser to draw a
 * weekday split. The KPI numbers come from the server-aggregated breakdown
 * query, which has no such limit.
 *
 * Stops early when a page comes back short, so the common case of a seller
 * with well under 100 rows costs exactly one request.
 */
async function fetchUpTo<T>(
  page: (n: number) => Promise<{ data?: T[] } | undefined>,
  maxRows = 500,
): Promise<T[]> {
  const rows: T[] = [];
  for (let n = 1; rows.length < maxRows; n += 1) {
    const res = await page(n);
    const batch = res?.data ?? [];
    rows.push(...batch);
    if (batch.length < 100) break;
  }
  return rows.slice(0, maxRows);
}

export function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'categories' | 'customers'>(
    'overview',
  );
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  const metrics = useQuery({
    queryKey: ['vendor-analytics'],
    queryFn: () => api.analytics.myVendor(),
  });
  const health = useQuery({ queryKey: ['vendor-360'], queryFn: () => api.intelligence.myVendor() });

  // Server-aggregated breakdown — top products / categories / customers / daily
  // trend, computed over ALL revenue-bearing orders in the window (DELIVERED /
  // CLOSED only — cancelled and refunded orders are excluded from revenue per
  // the audit). This is the AUTHORITATIVE source for the audit-flagged metrics.
  const breakdown = useQuery({
    queryKey: ['vendor-analytics-breakdown', dateRange],
    queryFn: () => api.analytics.myVendorBreakdown(dateRange),
  });

  // Secondary client-side calculations (daily trend, weekday split, status
  // distribution, period-over-period compare) still fetch the recent order
  // window — they're best-effort visualisations, not source-of-truth numbers.
  // The breakdown query above is what backs the KPIs.
  //
  // Fetched a page at a time rather than in one large request: `pageSize` is
  // capped at 100 server-side, and asking for more does not return more — the
  // request fails validation and yields nothing, so these charts rendered
  // empty for every seller while looking like a seller with no orders.
  const orders = useQuery({
    queryKey: ['vendor-orders-analytics', dateRange],
    queryFn: () => fetchUpTo((p) => api.orders.vendorOrders({ page: p, pageSize: 100 } as any)),
  });

  // Fetch products for category analysis fall-back (used by the products tab
  // for catalog-side totals like product count per category).
  const products = useQuery({
    queryKey: ['vendor-products-analytics'],
    queryFn: () => fetchUpTo((p) => api.products.myProducts({ page: p, pageSize: 100 })),
  });

  // `fetchUpTo` already unwraps the envelope and concatenates the pages, so
  // these are the rows themselves rather than a `{ data }` response.
  const ordersData = orders.data ?? [];
  const productsData = (products.data as any[]) ?? [];

  const filterByDateRange = (dateStr: string) => {
    if (dateRange === 'all') return true;
    const date = new Date(dateStr);
    const now = new Date();
    const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
    const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return date >= cutoff;
  };

  // Window of orders for secondary client-side viz. Excludes cancelled per
  // the audit so revenue charts don't double-count them.
  const filteredOrders = ordersData.filter(
    (o: any) =>
      o.createdAt &&
      filterByDateRange(o.createdAt) &&
      o.status !== 'CANCELLED' &&
      o.status !== 'REFUNDED',
  );

  // Audit-correct totals (top products / categories / customers, revenue,
  // customer counts) come from the server breakdown — not the capped client
  // list.
  const topProducts = breakdown.data?.topProducts ?? [];
  const topCategories = (breakdown.data?.topCategories ?? []).map((c) => ({
    name: c.name,
    revenue: c.revenue,
    orders: c.orders,
    products: productsData.filter((p: any) => p.category?.name === c.name).length,
  }));
  const topCustomers = breakdown.data?.topCustomers ?? [];
  const repeatCustomers = breakdown.data?.repeatCustomers ?? 0;
  const totalCustomers = breakdown.data?.uniqueCustomers ?? 0;
  const repeatRate = totalCustomers > 0 ? (repeatCustomers / totalCustomers) * 100 : 0;

  // Time-based trends (last 7 days)
  const dailySales: Record<string, { date: string; revenue: number; orders: number }> = {};
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return d.toISOString().split('T')[0]!;
  });

  last7Days.forEach((date) => {
    if (date) {
      dailySales[date] = { date, revenue: 0, orders: 0 };
    }
  });

  filteredOrders.forEach((order: any) => {
    const orderDate = new Date(order.createdAt).toISOString().split('T')[0];
    if (orderDate && dailySales[orderDate]) {
      dailySales[orderDate].revenue += order.totalPaise || 0;
      dailySales[orderDate].orders += 1;
    }
  });

  const salesTrend = Object.values(dailySales);

  // Average Order Value Trend
  const avgOrderValues = salesTrend.map((day) => ({
    date: day.date,
    aov: day.orders > 0 ? day.revenue / day.orders : 0,
  }));

  // Order Status Distribution
  const statusDistribution: Record<string, number> = {};
  filteredOrders.forEach((order: any) => {
    const status = order.status || 'UNKNOWN';
    statusDistribution[status] = (statusDistribution[status] || 0) + 1;
  });

  // Revenue by day of week
  const dayOfWeekRevenue: Record<string, { day: string; revenue: number; orders: number }> = {
    Sun: { day: 'Sunday', revenue: 0, orders: 0 },
    Mon: { day: 'Monday', revenue: 0, orders: 0 },
    Tue: { day: 'Tuesday', revenue: 0, orders: 0 },
    Wed: { day: 'Wednesday', revenue: 0, orders: 0 },
    Thu: { day: 'Thursday', revenue: 0, orders: 0 },
    Fri: { day: 'Friday', revenue: 0, orders: 0 },
    Sat: { day: 'Saturday', revenue: 0, orders: 0 },
  };

  filteredOrders.forEach((order: any) => {
    const dayKey = new Date(order.createdAt).toLocaleDateString('en-US', { weekday: 'short' });
    if (dayOfWeekRevenue[dayKey]) {
      dayOfWeekRevenue[dayKey].revenue += order.totalPaise || 0;
      dayOfWeekRevenue[dayKey].orders += 1;
    }
  });

  const weekdayPerformance = Object.values(dayOfWeekRevenue);

  // Calculate comparison metrics (current vs previous period).
  // Pull the authoritative current-period totals from the server breakdown
  // (uncapped, status-filtered). Previous period still derives from the
  // recent-order window — best-effort for the comparison badge only.
  const currentPeriodRevenue =
    breakdown.data?.totalRevenuePaise ??
    filteredOrders.reduce((sum: number, o: any) => sum + (o.totalPaise || 0), 0);
  const currentPeriodOrders = breakdown.data?.orderCount ?? filteredOrders.length;

  // Previous period calculation
  const getPreviousPeriodDays = () => {
    if (dateRange === '7d') return 7;
    if (dateRange === '30d') return 30;
    if (dateRange === '90d') return 90;
    return 365; // for 'all'
  };

  const previousPeriodDays = getPreviousPeriodDays();
  const previousPeriodOrders = ordersData.filter((o: any) => {
    if (!o.createdAt || o.status === 'CANCELLED') return false;
    const date = new Date(o.createdAt);
    const now = new Date();
    const periodStart = new Date(now.getTime() - previousPeriodDays * 24 * 60 * 60 * 1000);
    const periodEnd = new Date(now.getTime() - previousPeriodDays * 2 * 24 * 60 * 60 * 1000);
    return date >= periodEnd && date < periodStart;
  });

  const previousPeriodRevenue = previousPeriodOrders.reduce(
    (sum: number, o: any) => sum + (o.totalPaise || 0),
    0,
  );
  const revenueGrowth =
    previousPeriodRevenue > 0
      ? ((currentPeriodRevenue - previousPeriodRevenue) / previousPeriodRevenue) * 100
      : 0;
  const ordersGrowth =
    previousPeriodOrders.length > 0
      ? ((currentPeriodOrders - previousPeriodOrders.length) / previousPeriodOrders.length) * 100
      : 0;

  return (
    <div className="space-y-6">
      <style>{`
        .analytics-card {
          transition: all 0.2s ease !important;
          border: 1px solid rgba(226, 232, 240, 0.8) !important;
        }
        .analytics-card:hover {
          transform: translateY(-2px) !important;
          border-color: rgba(168, 22, 27, 0.1) !important;
          box-shadow: 0 4px 12px -4px rgba(168, 22, 27, 0.05) !important;
        }
        .chart-bar {
          transition: all 0.3s ease !important;
        }
        .chart-bar:hover {
          opacity: 0.8 !important;
        }
      `}</style>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <PageHeader
          title="Analytics"
          description="Your sales performance, fulfillment and store health."
        />

        {/* Date Range Filter */}
        <div className="flex gap-2">
          {[
            { key: '7d', label: 'Last 7 Days' },
            { key: '30d', label: 'Last 30 Days' },
            { key: '90d', label: 'Last 90 Days' },
            { key: 'all', label: 'All Time' },
          ].map((range) => (
            <button
              key={range.key}
              onClick={() => setDateRange(range.key as any)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-all ${
                dateRange === range.key
                  ? 'border-[#a8161b] bg-[#a8161b] text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-[#a8161b] hover:text-[#a8161b]'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { key: 'overview', label: 'Overview', icon: '📊' },
          { key: 'products', label: 'Products', icon: '📦' },
          { key: 'categories', label: 'Categories', icon: '🏷️' },
          { key: 'customers', label: 'Customers', icon: '👥' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-bold transition-all ${
              activeTab === tab.key
                ? 'border-[#a8161b] text-[#a8161b]'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <>
          {/* Comparison Metrics - NEW! */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="analytics-card overflow-hidden">
              <CardBody className="p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Revenue ({dateRange})
                </p>
                <p className="mt-2 text-2xl font-black text-slate-900">
                  {formatPaise(currentPeriodRevenue)}
                </p>
                <div className="mt-2 flex items-center gap-1">
                  {revenueGrowth >= 0 ? (
                    <span className="text-xs font-bold text-emerald-600">
                      ↑ {revenueGrowth.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-xs font-bold text-red-600">
                      ↓ {Math.abs(revenueGrowth).toFixed(1)}%
                    </span>
                  )}
                  <span className="text-xs text-slate-400">vs previous period</span>
                </div>
              </CardBody>
            </Card>

            <Card className="analytics-card overflow-hidden">
              <CardBody className="p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Orders ({dateRange})
                </p>
                <p className="mt-2 text-2xl font-black text-slate-900">{currentPeriodOrders}</p>
                <div className="mt-2 flex items-center gap-1">
                  {ordersGrowth >= 0 ? (
                    <span className="text-xs font-bold text-emerald-600">
                      ↑ {ordersGrowth.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-xs font-bold text-red-600">
                      ↓ {Math.abs(ordersGrowth).toFixed(1)}%
                    </span>
                  )}
                  <span className="text-xs text-slate-400">vs previous period</span>
                </div>
              </CardBody>
            </Card>

            <Card className="analytics-card overflow-hidden">
              <CardBody className="p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Avg Order Value
                </p>
                <p className="mt-2 text-2xl font-black text-slate-900">
                  {formatPaise(
                    currentPeriodOrders > 0 ? currentPeriodRevenue / currentPeriodOrders : 0,
                  )}
                </p>
                <p className="mt-2 text-xs text-slate-500">{currentPeriodOrders} orders</p>
              </CardBody>
            </Card>

            <Card className="analytics-card overflow-hidden">
              <CardBody className="p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Total Customers
                </p>
                <p className="mt-2 text-2xl font-black text-slate-900">{totalCustomers}</p>
                <p className="mt-2 text-xs font-bold text-emerald-600">
                  {repeatRate.toFixed(1)}% repeat rate
                </p>
              </CardBody>
            </Card>
          </div>

          <div>
            <h3 className="text-ink-muted mb-3 text-sm font-semibold uppercase">Performance</h3>
            {metrics.isLoading ? (
              <Spinner size="lg" />
            ) : metrics.data ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Card className="analytics-card">
                  <CardBody>
                    <p className="text-ink-muted text-sm">GMV</p>
                    <p className="mt-1 text-2xl font-semibold">
                      {formatPaise(metrics.data.gmvPaise)}
                    </p>
                  </CardBody>
                </Card>
                <Card className="analytics-card">
                  <CardBody>
                    <p className="text-ink-muted text-sm">Orders</p>
                    <p className="mt-1 text-2xl font-semibold">{metrics.data.orderCount}</p>
                  </CardBody>
                </Card>
                <Card className="analytics-card">
                  <CardBody>
                    <p className="text-ink-muted text-sm">Delivered</p>
                    <p className="mt-1 text-2xl font-semibold">{metrics.data.deliveredCount}</p>
                  </CardBody>
                </Card>
                <Card className="analytics-card">
                  <CardBody>
                    <p className="text-ink-muted text-sm">Avg order value</p>
                    <p className="mt-1 text-2xl font-semibold">
                      {formatPaise(metrics.data.avgOrderValuePaise)}
                    </p>
                  </CardBody>
                </Card>
                <Card className="analytics-card">
                  <CardBody>
                    <p className="text-ink-muted text-sm">Return rate</p>
                    <p className="mt-1 text-2xl font-semibold">{pct(metrics.data.returnRateBps)}</p>
                  </CardBody>
                </Card>
                <Card className="analytics-card">
                  <CardBody>
                    <p className="text-ink-muted text-sm">Fulfillment rate</p>
                    <p className="mt-1 text-2xl font-semibold">
                      {pct(metrics.data.fulfillmentRateBps)}
                    </p>
                  </CardBody>
                </Card>
              </div>
            ) : (
              <p className="text-ink-muted text-sm">No metrics yet.</p>
            )}
          </div>

          {/* Charts Grid - NEW! */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Sales Trend Chart */}
            <Card className="analytics-card">
              <CardBody className="p-6">
                <h3 className="mb-4 text-lg font-black text-slate-900">
                  Sales Trend (Last 7 Days)
                </h3>
                {orders.isLoading ? (
                  <div className="grid min-h-[200px] place-items-center">
                    <Spinner size="lg" />
                  </div>
                ) : salesTrend.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex h-48 items-end justify-between gap-2">
                      {salesTrend.map((day, idx) => {
                        const maxRevenue = Math.max(...salesTrend.map((d) => d.revenue), 1);
                        const height = (day.revenue / maxRevenue) * 100;
                        return (
                          <div key={idx} className="flex flex-1 flex-col items-center gap-2">
                            <div className="relative w-full">
                              <div
                                className="chart-bar w-full rounded-t-lg bg-gradient-to-t from-[#a8161b] to-[#ef4444]"
                                style={{ height: `${Math.max(height, 5)}%` }}
                                title={`${formatPaise(day.revenue)} - ${day.orders} orders`}
                              />
                            </div>
                            <div className="text-center">
                              <p className="text-xs font-bold text-slate-600">
                                {new Date(day.date).toLocaleDateString('en-IN', {
                                  weekday: 'short',
                                })}
                              </p>
                              <p className="text-[10px] text-slate-400">
                                {new Date(day.date).getDate()}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                          Total Revenue
                        </p>
                        <p className="mt-1 text-xl font-black text-slate-900">
                          {formatPaise(salesTrend.reduce((sum, d) => sum + d.revenue, 0))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                          Total Orders
                        </p>
                        <p className="mt-1 text-xl font-black text-slate-900">
                          {salesTrend.reduce((sum, d) => sum + d.orders, 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    title="No sales data"
                    description="Sales data will appear here once you have orders."
                  />
                )}
              </CardBody>
            </Card>

            {/* Day of Week Performance - NEW! */}
            <Card className="analytics-card">
              <CardBody className="p-6">
                <h3 className="mb-4 text-lg font-black text-slate-900">
                  Performance by Day of Week
                </h3>
                {orders.isLoading ? (
                  <div className="grid min-h-[200px] place-items-center">
                    <Spinner size="lg" />
                  </div>
                ) : weekdayPerformance.length > 0 ? (
                  <div className="space-y-3">
                    {weekdayPerformance.map((day, idx) => {
                      const maxRevenue = Math.max(...weekdayPerformance.map((d) => d.revenue), 1);
                      const percentage = (day.revenue / maxRevenue) * 100;
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-bold text-slate-700">{day.day}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-500">{day.orders} orders</span>
                              <span className="font-bold text-emerald-600">
                                {formatPaise(day.revenue)}
                              </span>
                            </div>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-2 rounded-full bg-gradient-to-r from-[#a8161b] to-[#ef4444] transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState
                    title="No data"
                    description="Day of week performance will appear here."
                  />
                )}
              </CardBody>
            </Card>
          </div>

          {/* Order Status Distribution - NEW! */}
          <Card className="analytics-card">
            <CardBody className="p-6">
              <h3 className="mb-4 text-lg font-black text-slate-900">Order Status Distribution</h3>
              {orders.isLoading ? (
                <div className="grid min-h-[150px] place-items-center">
                  <Spinner size="lg" />
                </div>
              ) : Object.keys(statusDistribution).length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {Object.entries(statusDistribution).map(([status, count]) => {
                    const statusColors: Record<string, string> = {
                      CONFIRMED: 'from-blue-500 to-blue-600',
                      PACKED: 'from-amber-500 to-amber-600',
                      SHIPPED: 'from-purple-500 to-purple-600',
                      DELIVERED: 'from-emerald-500 to-emerald-600',
                      CANCELLED: 'from-red-500 to-red-600',
                      RETURN_REQUESTED: 'from-orange-500 to-orange-600',
                    };
                    const colorClass = statusColors[status] || 'from-slate-500 to-slate-600';

                    return (
                      <div
                        key={status}
                        className={`rounded-lg bg-gradient-to-br ${colorClass} p-4 text-white shadow-sm`}
                      >
                        <p className="text-xs font-bold uppercase tracking-wider opacity-90">
                          {status.replace(/_/g, ' ')}
                        </p>
                        <p className="mt-2 text-3xl font-black">{count}</p>
                        <p className="mt-1 text-xs opacity-75">
                          {((count / filteredOrders.length) * 100).toFixed(1)}% of orders
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  title="No orders"
                  description="Order status distribution will appear here."
                />
              )}
            </CardBody>
          </Card>

          <div>
            <h3 className="text-ink-muted mb-3 text-sm font-semibold uppercase">Store health</h3>
            {health.isLoading ? (
              <Spinner size="lg" />
            ) : health.data ? (
              <Card className="analytics-card">
                <CardBody>
                  <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                    <div>
                      <p className="text-ink-muted text-xs">Health grade</p>
                      <Badge tone={HEALTH_TONE[health.data.healthGrade] ?? 'neutral'}>
                        {health.data.healthGrade}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-ink-muted text-xs">GMV lifetime</p>
                      <p className="font-semibold">{formatPaise(health.data.gmvLifetimePaise)}</p>
                    </div>
                    <div>
                      <p className="text-ink-muted text-xs">GMV 30d</p>
                      <p className="font-semibold">{formatPaise(health.data.gmv30dPaise)}</p>
                    </div>
                    <div>
                      <p className="text-ink-muted text-xs">SLA</p>
                      <p className="font-semibold">{pct(health.data.slaBps)}</p>
                    </div>
                    <div>
                      <p className="text-ink-muted text-xs">Avg fulfilment</p>
                      <p className="font-semibold">{health.data.avgFulfillmentDays} days</p>
                    </div>
                    <div>
                      <p className="text-ink-muted text-xs">Delivered</p>
                      <p className="font-semibold">{health.data.deliveredOrders}</p>
                    </div>
                    <div>
                      <p className="text-ink-muted text-xs">Returned</p>
                      <p className="font-semibold">{health.data.returnedOrders}</p>
                    </div>
                    <div>
                      <p className="text-ink-muted text-xs">Last order</p>
                      <p className="text-xs">
                        {health.data.lastOrderAt
                          ? new Date(health.data.lastOrderAt).toLocaleDateString()
                          : '—'}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ) : (
              <p className="text-ink-muted text-sm">No health data yet.</p>
            )}
          </div>
        </>
      )}

      {/* PRODUCTS TAB - NEW! */}
      {activeTab === 'products' && (
        <Card className="analytics-card">
          <CardBody className="p-6">
            <h2 className="mb-4 text-lg font-black text-slate-900">Top Selling Products</h2>
            <p className="mb-4 text-sm text-slate-600">
              Product performance based on revenue for selected period
            </p>

            {orders.isLoading ? (
              <div className="grid min-h-[200px] place-items-center">
                <Spinner size="lg" />
              </div>
            ) : topProducts.length === 0 ? (
              <EmptyState
                title="No product sales yet"
                description="Product sales data will appear here once you have orders."
              />
            ) : (
              <div className="space-y-3">
                {topProducts.map((product, idx) => {
                  const maxRevenue = topProducts[0]?.revenue || 1;
                  const percentage = (product.revenue / maxRevenue) * 100;
                  return (
                    <div
                      key={idx}
                      className="rounded-lg border border-slate-100 bg-slate-50/50 p-4 transition-all hover:border-[#a8161b]/20 hover:bg-white"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#a8161b] text-xs font-black text-white">
                              {idx + 1}
                            </span>
                            <h3 className="font-bold text-slate-900">{product.title}</h3>
                          </div>
                          <div className="mt-3 grid grid-cols-3 gap-3">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Revenue
                              </p>
                              <p className="mt-1 text-sm font-black text-emerald-600">
                                {formatPaise(product.revenue)}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Units Sold
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-700">{product.qty}</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Orders
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-700">
                                {product.orders}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                          <div
                            className="h-2 rounded-full bg-gradient-to-r from-[#a8161b] to-[#ef4444] transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* CATEGORIES TAB - NEW! */}
      {activeTab === 'categories' && (
        <Card className="analytics-card">
          <CardBody className="p-6">
            <h2 className="mb-4 text-lg font-black text-slate-900">Category Performance</h2>
            <p className="mb-4 text-sm text-slate-600">Sales performance by product category</p>

            {orders.isLoading || products.isLoading ? (
              <div className="grid min-h-[200px] place-items-center">
                <Spinner size="lg" />
              </div>
            ) : topCategories.length === 0 ? (
              <EmptyState
                title="No category data yet"
                description="Category performance will appear here once you have products and orders."
              />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {topCategories.map((category, idx) => {
                  const colors = [
                    'from-rose-500 to-pink-500',
                    'from-orange-500 to-amber-500',
                    'from-emerald-500 to-teal-500',
                    'from-blue-500 to-indigo-500',
                    'from-purple-500 to-violet-500',
                    'from-red-500 to-rose-500',
                    'from-yellow-500 to-orange-500',
                    'from-green-500 to-emerald-500',
                  ];
                  const colorClass = colors[idx % colors.length];

                  return (
                    <Card
                      key={idx}
                      className="transform overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
                    >
                      <CardBody className="p-4">
                        <div
                          className={`mb-3 inline-block rounded-lg bg-gradient-to-r ${colorClass} px-3 py-1`}
                        >
                          <h3 className="text-sm font-black text-white">{category.name}</h3>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                              Revenue
                            </span>
                            <span className="text-lg font-black text-emerald-600">
                              {formatPaise(category.revenue)}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Products
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-700">
                                {category.products}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Orders
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-700">
                                {category.orders}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* CUSTOMERS TAB - NEW! */}
      {activeTab === 'customers' && (
        <div className="space-y-6">
          {/* Customer Metrics */}
          <div className="grid gap-4 sm:grid-cols-3">
            <Card className="analytics-card">
              <CardBody>
                <p className="text-sm text-slate-500">Total Customers</p>
                <p className="mt-2 text-3xl font-black text-slate-900">{totalCustomers}</p>
              </CardBody>
            </Card>
            <Card className="analytics-card">
              <CardBody>
                <p className="text-sm text-slate-500">Repeat Customers</p>
                <p className="mt-2 text-3xl font-black text-emerald-600">{repeatCustomers}</p>
              </CardBody>
            </Card>
            <Card className="analytics-card">
              <CardBody>
                <p className="text-sm text-slate-500">Repeat Rate</p>
                <p className="mt-2 text-3xl font-black text-[#a8161b]">{repeatRate.toFixed(1)}%</p>
              </CardBody>
            </Card>
          </div>

          {/* Top Customers */}
          <Card className="analytics-card">
            <CardBody className="p-6">
              <h2 className="mb-4 text-lg font-black text-slate-900">Top Customers</h2>
              <p className="mb-4 text-sm text-slate-600">
                Your most valuable customers by total revenue
              </p>

              {orders.isLoading ? (
                <div className="grid min-h-[200px] place-items-center">
                  <Spinner size="lg" />
                </div>
              ) : topCustomers.length === 0 ? (
                <EmptyState
                  title="No customer data yet"
                  description="Customer analytics will appear here once you have orders."
                />
              ) : (
                <div className="space-y-3">
                  {topCustomers.map((customer, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-slate-100 bg-slate-50/50 p-4 transition-all hover:border-[#a8161b]/20 hover:bg-white"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#a8161b] to-[#ef4444] text-sm font-black text-white">
                              {customer.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h3 className="font-bold text-slate-900">{customer.name}</h3>
                              {customer.email && (
                                <p className="text-xs text-slate-500">{customer.email}</p>
                              )}
                            </div>
                          </div>

                          <div className="mt-3 grid grid-cols-3 gap-3">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Total Spent
                              </p>
                              <p className="mt-1 text-sm font-black text-emerald-600">
                                {formatPaise(customer.revenue)}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Orders
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-700">
                                {customer.orders}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Last Order
                              </p>
                              <p className="mt-1 text-xs font-bold text-slate-600">
                                {new Date(customer.lastOrder).toLocaleDateString('en-IN', {
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// SUPPORT  (vendor tickets — same self-service endpoints as customer)
// ══════════════════════════════════════════════════════════════════

const TICKET_STATUS_TONE: Record<string, BadgeTone> = {
  OPEN: 'warning',
  PENDING_CUSTOMER: 'neutral',
  IN_PROGRESS: 'warning',
  ESCALATED: 'danger',
  RESOLVED: 'success',
  CLOSED: 'neutral',
};
const VENDOR_CATEGORIES: SupportTicketCategory[] = [
  'VENDOR',
  'ORDER',
  'PAYMENT',
  'PRODUCT',
  'ACCOUNT',
  'OTHER',
];

export function SupportPage() {
  const qc = useQueryClient();
  // Arriving from the suspension banner: the ticket is about the account.
  const [searchParams] = useSearchParams();
  const suspensionAppeal = searchParams.get('topic') === 'suspension';
  // 'mine' = tickets the vendor user raised (e.g. platform issues).
  // 'complaints' = tickets customers raised AGAINST this vendor — escalated
  // here so the vendor can respond and resolve before admin intervention.
  const [inboxMode, setInboxMode] = useState<'mine' | 'complaints'>('mine');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [form, setForm] = useState({
    category: (suspensionAppeal ? 'ACCOUNT' : 'VENDOR') as SupportTicketCategory,
    subject: suspensionAppeal ? 'Store suspension appeal' : '',
    body: '',
  });
  const [reply, setReply] = useState('');

  const tickets = useQuery({
    queryKey: ['vendor-tickets', inboxMode],
    queryFn: () =>
      inboxMode === 'mine'
        ? api.support.list({ limit: 50 })
        : api.support.vendor.list({ limit: 50 }),
  });
  const detail = useQuery({
    queryKey: ['vendor-ticket', inboxMode, activeId],
    queryFn: () =>
      inboxMode === 'mine' ? api.support.get(activeId!) : api.support.vendor.get(activeId!),
    enabled: !!activeId,
  });

  const create = useMutation({
    mutationFn: () =>
      api.support.create({ category: form.category, subject: form.subject, body: form.body }),
    onSuccess: (t) => {
      setForm({ category: 'VENDOR', subject: '', body: '' });
      setInboxMode('mine');
      setActiveId(t.id);
      void qc.invalidateQueries({ queryKey: ['vendor-tickets'] });
    },
  });
  const sendReply = useMutation({
    mutationFn: () =>
      inboxMode === 'mine'
        ? api.support.reply(activeId!, { body: reply })
        : api.support.vendor.reply(activeId!, { body: reply }),
    onSuccess: () => {
      setReply('');
      void qc.invalidateQueries({ queryKey: ['vendor-ticket', inboxMode, activeId] });
      void qc.invalidateQueries({ queryKey: ['vendor-tickets', inboxMode] });
    },
  });
  const resolveComplaint = useMutation({
    mutationFn: (resolution: string) =>
      api.support.vendor.resolve(activeId!, { resolutionNote: resolution }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor-ticket', inboxMode, activeId] });
      void qc.invalidateQueries({ queryKey: ['vendor-tickets', inboxMode] });
    },
  });

  // Switching inboxes deselects the open ticket (different ticket pools).
  useEffect(() => {
    setActiveId(null);
  }, [inboxMode]);

  const ticketsList = Array.isArray(tickets.data)
    ? (tickets.data as any[])
    : ((tickets.data as any)?.data ?? []);

  return (
    <div className="space-y-6">
      <style>{`
        .support-card:hover {
          box-shadow: 0 10px 25px -12px rgba(168, 22, 27, 0.08) !important;
          border-color: rgba(168, 22, 27, 0.15) !important;
        }
        .support-input:focus {
          border-color: #a8161b !important;
          box-shadow: 0 0 0 3px rgba(168, 22, 27, 0.1) !important;
          outline: none !important;
        }
        .ticket-btn {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .ticket-btn:hover {
          background-color: rgba(248, 250, 252, 0.8) !important;
          border-color: rgba(168, 22, 27, 0.2) !important;
        }
      `}</style>

      <PageHeader
        title="Support"
        description="Raise tickets for vendor onboarding, payouts, product approvals or other issues."
      />

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <div className="space-y-5">
          <Card className="support-card rounded-xl border border-slate-200/80 bg-white shadow-sm transition-all duration-300">
            <CardBody className="p-5">
              <h3 className="mb-4 text-xs font-extrabold uppercase tracking-wider text-slate-400">
                New ticket
              </h3>
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (form.subject.trim().length >= 4 && form.body.trim().length >= 4)
                    create.mutate();
                }}
              >
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Category</label>
                  <select
                    className="support-input w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition-all duration-200"
                    value={form.category}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, category: e.target.value as SupportTicketCategory }))
                    }
                  >
                    {VENDOR_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <Input
                    label="Subject"
                    className="support-input rounded-lg border border-slate-200 font-bold"
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    required
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Details</label>
                  <textarea
                    className="support-input w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition-all duration-200"
                    rows={4}
                    value={form.body}
                    onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  fullWidth
                  className="rounded-lg border-[#a8161b] bg-[#a8161b] py-2 text-xs font-extrabold text-white shadow-sm hover:border-[#850c10] hover:bg-[#850c10]"
                  loading={create.isPending}
                >
                  Submit ticket
                </Button>
              </form>
            </CardBody>
          </Card>

          <div className="space-y-2.5">
            <div className="flex gap-2">
              {(['mine', 'complaints'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setInboxMode(mode)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-[11px] font-extrabold uppercase tracking-wider transition-all ${
                    inboxMode === mode
                      ? 'border-[#a8161b] bg-[#a8161b] text-white shadow-sm'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-[#a8161b]/40 hover:text-[#a8161b]'
                  }`}
                >
                  {mode === 'mine' ? 'My tickets' : 'Customer complaints'}
                </button>
              ))}
            </div>
            {tickets.isLoading ? (
              <Spinner size="sm" />
            ) : ticketsList.length === 0 ? (
              <div className="rounded-xl border border-slate-200/80 bg-white p-4 text-center shadow-sm">
                <p className="text-xs font-bold text-slate-400">
                  {inboxMode === 'mine'
                    ? 'No tickets yet.'
                    : 'No customer complaints. Tickets escalated against your store appear here.'}
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {ticketsList.map((t: any) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={`ticket-btn w-full rounded-xl border bg-white p-3 text-left text-sm shadow-sm hover:bg-slate-50 ${
                        activeId === t.id
                          ? 'border-[#a8161b] ring-2 ring-[#a8161b]/5'
                          : 'border-slate-200/80'
                      }`}
                      onClick={() => setActiveId(t.id)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[10px] font-extrabold text-slate-400">
                          #{t.ticketNumber}
                        </span>
                        <Badge
                          tone={TICKET_STATUS_TONE[t.status] ?? 'neutral'}
                          className="text-[9px] font-extrabold uppercase tracking-wider"
                        >
                          {t.status.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <p className="mt-1.5 truncate text-xs font-bold text-slate-700 sm:text-sm">
                        {t.subject}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          {!activeId ? (
            <div className="flex min-h-[300px] flex-col items-center justify-center rounded-xl border border-slate-200/80 bg-white p-8 shadow-sm">
              <EmptyState
                title="Select a ticket"
                description="Pick one from the list or create a new ticket."
              />
            </div>
          ) : detail.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : detail.data ? (
            <Card className="rounded-xl border border-slate-200/80 bg-white shadow-sm">
              <CardBody className="space-y-4 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                  <div>
                    <span className="font-mono text-[10px] font-extrabold text-slate-400">
                      #{detail.data.ticketNumber}
                    </span>
                    <h2 className="mt-0.5 text-lg font-black text-slate-800">
                      {detail.data.subject}
                    </h2>
                  </div>
                  <Badge
                    tone={TICKET_STATUS_TONE[detail.data.status] ?? 'neutral'}
                    className="px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider"
                  >
                    {detail.data.status.replace(/_/g, ' ')}
                  </Badge>
                </div>

                <div className="max-h-[350px] space-y-3 overflow-y-auto pr-1">
                  {detail.data.messages
                    .filter((m: any) => !m.internal)
                    .map((m: any) => {
                      const isYou = m.authorId === detail.data?.subjectUserId;
                      return (
                        <div
                          key={m.id}
                          className={`rounded-xl p-3 text-sm shadow-sm ${isYou ? 'ml-8 border border-slate-200/60 bg-slate-50' : 'mr-8 border border-red-100 bg-red-50/30'}`}
                        >
                          <div className="mb-1 flex justify-between text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                            <span className={isYou ? 'text-slate-500' : 'text-[#a8161b]'}>
                              {isYou ? 'You' : 'Support'}
                            </span>
                            <span>{new Date(m.createdAt).toLocaleString()}</span>
                          </div>
                          <p className="mt-1.5 whitespace-pre-wrap text-xs font-medium text-slate-600 sm:text-sm">
                            {m.body}
                          </p>
                        </div>
                      );
                    })}
                </div>

                {detail.data.status !== 'CLOSED' && (
                  <div className="space-y-3 border-t border-slate-100 pt-3">
                    <textarea
                      className="support-input w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition-all duration-200"
                      rows={3}
                      placeholder={
                        inboxMode === 'complaints' ? 'Reply to the customer…' : 'Type your reply…'
                      }
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                    />
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {inboxMode === 'complaints' && (
                        <Button
                          variant="outline"
                          className="rounded-lg border-emerald-300 px-5 py-2 text-xs font-extrabold text-emerald-700 shadow-sm hover:border-emerald-400 hover:bg-emerald-50"
                          disabled={resolveComplaint.isPending}
                          onClick={() => {
                            const resolution = reply.trim();
                            if (resolution.length < 4) {
                              alert('Please describe the resolution (at least 4 characters).');
                              return;
                            }
                            resolveComplaint.mutate(resolution);
                          }}
                        >
                          Resolve complaint
                        </Button>
                      )}
                      <Button
                        className="rounded-lg border-[#a8161b] bg-[#a8161b] px-5 py-2 text-xs font-extrabold text-white shadow-sm hover:border-[#850c10] hover:bg-[#850c10] disabled:opacity-60"
                        disabled={reply.trim().length === 0 || sendReply.isPending}
                        onClick={() => reply.trim() && sendReply.mutate()}
                        loading={sendReply.isPending}
                      >
                        Send reply
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          ) : (
            <p className="text-ink-muted text-sm">Ticket not found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// NOT FOUND
// ══════════════════════════════════════════════════════════════════

export const NotFoundPage = () => (
  <EmptyState title="Page not found" description="That seller page doesn't exist." />
);
